// Need to import the appropriate rendering profiles because of a breaking change in v18.0.0 (https://github.com/Kitware/vtk-js/releases/tag/v18.0.0)
import 'vtk.js/Sources/Rendering/Profiles/Volume';

import vtkGenericRenderWindow from 'vtk.js/Sources/Rendering/Misc/GenericRenderWindow';

// Force the loading of HttpDataAccessHelper to support gzip decompression
import 'vtk.js/Sources/IO/Core/DataAccessHelper/HttpDataAccessHelper';

import vtkImageMapper from 'vtk.js/Sources/Rendering/Core/ImageMapper';
import vtkImageReslice from "vtk.js/Sources/Imaging/Core/ImageReslice";
import vtkPlane from 'vtk.js/Sources/Common/DataModel/Plane';

//// DEBUG
//import vtkConeSource from 'vtk.js/Sources/Filters/Sources/ConeSource';
//import vtkMapper from 'vtk.js/Sources/Rendering/Core/Mapper';
//import vtkActor from 'vtk.js/Sources/Rendering/Core/Actor';

import vtkActor from 'vtk.js/Sources/Rendering/Core/Actor';
import vtkMapper from 'vtk.js/Sources/Rendering/Core/Mapper';

import React, { useEffect, useRef } from 'react';
import Konva from 'konva';

import { logDebug, debugCallback, isNullOrUndefined, getDUName, CprViewMode, CprControlPointMode, clamp } from './Common';
import { CenterlineDistanceBranchSelection, CenterlineBranchSelection } from '../CenterlineMeasurement/CenterlinePlaneShared';
import { convertUnifiedBranchIndexToMasterIndex } from '../StentPlanning/StentPlanning.js';
import customImageInteractor from "./CustomImageInteractor";
import vtkMatrixBuilder from "vtk.js/Sources/Common/Core/MatrixBuilder";
import vtkCoordinate from 'vtk.js/Sources/Rendering/Core/Coordinate';

import { useScreenshot } from '../Screenshot/Screenshot';
import { useInteraction } from './Interaction';
import { InterpolationType } from "vtk.js/Sources/Rendering/Core/ImageProperty/Constants";

import { Union } from "fable-compiler/bin/fable-library/Types";

import { ProjectionMode } from 'vtk.js/Sources/Rendering/Core/ImageCPRMapper/Constants';
import { radiansFromDegrees } from 'vtk.js/Sources/Common/Core/Math';
import { updateState } from 'vtk.js/Sources/Widgets/Widgets3D/ResliceCursorWidget/helpers';
import { vec3, mat3, mat4 } from 'gl-matrix';
import { ViewTypes } from 'vtk.js/Sources/Widgets/Core/WidgetManager/Constants';
import vtkCPRManipulator from 'vtk.js/Sources/Widgets/Manipulators/CPRManipulator';
import vtkDataArray from 'vtk.js/Sources/Common/Core/DataArray';
import vtkImageCPRMapper from 'vtk.js/Sources/Rendering/Core/ImageCPRMapper';
import vtkImageSlice from 'vtk.js/Sources/Rendering/Core/ImageSlice';
import vtkPlaneManipulator from 'vtk.js/Sources/Widgets/Manipulators/PlaneManipulator';
import vtkPolyData from 'vtk.js/Sources/Common/DataModel/PolyData';
// Importing vtkPolyLine even if not used directly so we can reference it via JSdoc.
import vtkPolyLine from 'vtk.js/Sources/Common/DataModel/PolyLine';
import vtkPointPicker from 'vtk.js/Sources/Rendering/Core/PointPicker';
import vtkResliceCursorWidget from 'vtk.js/Sources/Widgets/Widgets3D/ResliceCursorWidget';
import { viewTypeToPlaneName } from 'vtk.js/Sources/Widgets/Widgets3D/ResliceCursorWidget/Constants';
import vtkWidgetManager from 'vtk.js/Sources/Widgets/Core/WidgetManager';
import widgetBehavior from 'vtk.js/Sources/Widgets/Widgets3D/ResliceCursorWidget/cprBehavior';

// Spline-related imports
import vtkSplineWidget from 'vtk.js/Sources/Widgets/Widgets3D/SplineWidget';
import { splineKind } from 'vtk.js/Sources/Common/DataModel/Spline3D/Constants';
import { BoundaryCondition } from 'vtk.js/Sources/Common/DataModel/Spline1D/Constants';

// For exporting edited centerline
import vtkPoints from 'vtk.js/Sources/Common/Core/Points';
import vtkXMLPolyDataWriter from 'vtk.js/Sources/IO/XML/XMLPolyDataWriter';
import vtkXMLWriter from 'vtk.js/Sources/IO/XML/XMLWriter';
import vtkXMLPolyDataReader from 'vtk.js/Sources/IO/XML/XMLPolyDataReader';

const isDebugging = false;

function log(msg, ...params) {
    logDebug("CprVisualization", isDebugging, msg, ...params);
}

function debug(callback) {
    debugCallback(isDebugging, callback);
}

function fitImageVerticallyToViewport({ renderWindow, renderer, cprObjects }, extraZoom = 1.0) {
    const view = renderWindow.getViews()[0];

    // Sync canvas to CSS size / DPR
    const [cw, ch] = view.getContainerSize();
    if (!cw || !ch) {
        return;
    }

    view.setSize(cw, ch);

    const [, vpH] = view.getViewportSize(renderer);
    const camera = renderer.getActiveCamera();
    camera.setParallelProjection(true);

    const img = cprObjects.actor.getMapper().getInputData();
    if (!img) {
        return;
    }

    // image center is off for volumes with different image sizes so we use actor center instead
    const center = cprObjects.actor.getCenter();
    const [minX, maxX, minY, maxY, minZ, maxZ] = img.getBounds();

    // Center camera on actor bounds, preserving view direction and distance
    const dop = camera.getDirectionOfProjection(); // camera -> focal point
    const dist = camera.getDistance() || 100; // distance to focal point
    camera.setFocalPoint(...center);
    camera.setPosition(center[0] - dop[0] * dist, center[1] - dop[1] * dist, center[2] - dop[2] * dist);
    camera.setParallelScale(1);

    // Project to display pixels using vtkCoordinate (handles all transforms)
    const coord = vtkCoordinate.newInstance();
    coord.setCoordinateSystemToWorld();

    const corners = [
        [minX, minY, minZ], [maxX, minY, minZ],
        [maxX, maxY, minZ], [minX, maxY, minZ],
        [minX, minY, maxZ], [maxX, minY, maxZ],
        [maxX, maxY, maxZ], [minX, maxY, maxZ],
    ];

    const ys = corners.map((c) => {
        coord.setValue(c);
        // Need to make a copy since vtkCoordinate re-uses the same array internally.
        return coord.getComputedDoubleDisplayValue(renderer)[1];
    });

    const heightPx = Math.max(...ys) - Math.min(...ys);

    // Compute contain-fit scale factor and add a 1 pixel margin to avoid precision issues
    const factor = (heightPx / vpH) || 1;
    const marginFactor = heightPx === 0 ? 1 : (heightPx + 1) / heightPx;
    camera.setParallelScale(factor * marginFactor * extraZoom);

    renderer.resetCameraClippingRange();

    // Update horizontal index lines if they exist (for straightened CPR view).
    if (cprObjects.horizontalIndexLines?.length && cprObjects.resliceFilter) {
        updateStraightenedIndexLines(
            cprObjects.resliceFilter,
            cprObjects.horizontalIndexLines,
            renderer,
            renderWindow
        );
    }

    if (cprObjects.lengthMeasurementArrow && cprObjects.resliceFilter) {
        updateStraightenedMeasurementArrow(
            cprObjects.resliceFilter,
            cprObjects.lengthMeasurementArrow,
            renderer,
            renderWindow,
        );
    }

    renderWindow.render();
}

/**
 * Compute the straightened centerline endpoints using image IJK indices.
 * World bounds are not reliable since the source image may be composed of non-contiguous slices.
 * @param {vtkImageData} reslicedImage - Output image from the reslice filter.
 * @returns {{start: number[], end: number[]}|null}
 */
function computeStraightenedCenterlineEndpoints(reslicedImage) {
    if (!reslicedImage) {
        return null;
    }

    const extent = reslicedImage.getExtent();
    if (!extent || extent.length !== 6) {
        console.warn('Straightened centerline: missing extent information.');
        return null;
    }

    const [iMin, iMax, jMin, jMax, kMin, kMax] = extent;

    if (kMin !== kMax) {
        console.error('Straightened centerline: expected single K slice, got', kMin, kMax);
        return null;
    }

    const jCenter = jMin + (jMax - jMin) * 0.5;
    const kSlice = kMin;

    const start = reslicedImage.indexToWorld([iMin, jCenter, kSlice]);
    const end = reslicedImage.indexToWorld([iMax, jCenter, kSlice]);

    if (!start || !end) {
        console.warn('Straightened centerline: unable to convert IJK to world coordinates.');
        return null;
    }

    return { start, end };
}

/**
 * Create a vertical centerline actor for the straightened CPR view.
 * The straightened view displays the orthogonal centerline DICOM series from the side,
 * and since the orthogonal series is centered on the centerline, we draw a straight line
 * top-to-bottom in the middle of the resliced image.
 * @param {*} resliceFilter - The reslice filter whose output we'll use to determine the centerline position.
 * @returns {{actor: vtkActor, mapper: vtkMapper, polyData: vtkPolyData}} The centerline actor, mapper, and polydata.
 */
function createStraightenedCenterline(resliceFilter) {
    if (!resliceFilter) {
        return null;
    }

    // Get the output of the reslice filter.
    const reslicedImage = resliceFilter.getOutputData();
    if (!reslicedImage) {
        return null;
    }

    const endpoints = computeStraightenedCenterlineEndpoints(reslicedImage);
    if (!endpoints) {
        return null;
    }

    const { start, end } = endpoints;

    // Create two points for the line using the world coordinates derived from IJK indices.
    const points = vtkPoints.newInstance();
    points.setNumberOfPoints(2);
    points.setPoint(0, start[0], start[1], start[2]);
    points.setPoint(1, end[0], end[1], end[2]);

    // Create a line cell connecting the two points.
    const lines = new Uint16Array([2, 0, 1]);

    // Create polydata and set points and lines.
    const polyData = vtkPolyData.newInstance();
    polyData.setPoints(points);
    polyData.getLines().setData(lines);

    // Create mapper and actor.
    const mapper = vtkMapper.newInstance();
    mapper.setInputData(polyData);

    const actor = vtkActor.newInstance();
    actor.setMapper(mapper);

    // Set line color to green and make it visible.
    actor.getProperty().setColor(0.0, 1.0, 0.0); // Green.
    actor.getProperty().setLineWidth(1);
    actor.getProperty().setLighting(false);

    return { actor, mapper, polyData };
}

/**
 * Update the centerline position based on the resliced image output.
 * @param {*} resliceFilter - The reslice filter whose output determines the centerline position.
 * @param {vtkPolyData} centerlinePolyData - The polydata to update.
 */
function updateStraightenedCenterline(resliceFilter, centerlinePolyData) {
    if (!resliceFilter || !centerlinePolyData) {
        return;
    }

    // Get the output of the reslice filter.
    const reslicedImage = resliceFilter.getOutputData();
    if (!reslicedImage) {
        return;
    }

    const endpoints = computeStraightenedCenterlineEndpoints(reslicedImage);
    if (!endpoints) {
        return;
    }

    const { start, end } = endpoints;

    const points = centerlinePolyData.getPoints();
    if (!points || points.getNumberOfPoints() < 2) {
        centerlinePolyData.setPoints(vtkPoints.newInstance());
    }

    const polyPoints = centerlinePolyData.getPoints();
    if (polyPoints.getNumberOfPoints() < 2) {
        polyPoints.setNumberOfPoints(2);
    }

    polyPoints.setPoint(0, start[0], start[1], start[2]);
    polyPoints.setPoint(1, end[0], end[1], end[2]);
    polyPoints.modified();

    centerlinePolyData.modified();
}

// Measurement guide line helpers for the straightened CPR view.
/**
 * @typedef {Object} StraightenedMeasurementLineConfig
 * @property {string} key - Stable identifier so actors can be replaced atomically.
 * @property {number} offset - Measurement index from the proximal end of the centerline.
 * @property {[number, number, number]} color - RGB color used for the guide line.
 */
const straightenedMeasurementColors = {
    start: [0.0, 0.0, 1.0],
    end: [1.0, 0.0, 0.0],
};

const diameterMeasurementCaseNames = ['Diameter', 'OffsetDiameter', 'MidpointDiameter'];

/**
 * Validate the provided centerline index.
 * @param {number} value - Centerline index to check.
 * @returns {boolean} True when the index is usable.
 */
function isValidCenterlineIndex(value) {
    return Number.isFinite(value) && value >= 0;
}

/**
 * Convert the optional active measurement into line configs for the straightened CPR view.
 * @param {Union|null|undefined} maybeMeasurement - Active stent measurement (Fable union).
 * @returns {StraightenedMeasurementLineConfig[]} Measurement-aligned line definitions.
 */
function getStraightenedMeasurementLineConfigs(maybeMeasurement) {
    if (isNullOrUndefined(maybeMeasurement)) {
        return [];
    }

    const measurementName = maybeMeasurement.name;

    if (diameterMeasurementCaseNames.includes(measurementName)) {
        const selection = maybeMeasurement?.fields?.[1]?.[0];
        const index = Number(selection?.index);

        if (!isValidCenterlineIndex(index)) {
            return [];
        }

        return [
            {
                key: `${measurementName}-${index}`,
                offset: index,
                color: straightenedMeasurementColors.start,
            },
        ];
    }

    if (measurementName === 'Length') {
        const range = maybeMeasurement?.fields?.[1]?.[0];
        const firstIndex = Number(range?.firstIndex);
        const secondIndex = Number(range?.secondIndex);
        const configs = [];

        if (isValidCenterlineIndex(firstIndex)) {
            configs.push({
                key: `${measurementName}-start-${firstIndex}`,
                offset: firstIndex,
                color: straightenedMeasurementColors.start,
            });
        }

        if (isValidCenterlineIndex(secondIndex)) {
            configs.push({
                key: `${measurementName}-end-${secondIndex}`,
                offset: secondIndex,
                color: straightenedMeasurementColors.end,
            });
        }

        return configs;
    }

    return [];
}

/**
 * Remove the supplied measurement lines from the renderer and free their resources.
 * @param {vtkRenderer|null|undefined} renderer - Renderer hosting the straightened lines.
 * @param {Array<{actor: vtkActor, mapper: vtkMapper, polyData: vtkPolyData}>} [lineDescriptors=[]] - Actors to dispose.
 * @returns {void}
 */
function disposeStraightenedIndexLines(renderer, lineDescriptors = []) {
    if (!Array.isArray(lineDescriptors) || !lineDescriptors.length) {
        return;
    }

    lineDescriptors.forEach((descriptor) => {
        if (!descriptor) {
            return;
        }

        if (renderer && descriptor.actor) {
            renderer.removeActor(descriptor.actor);
        }

        descriptor.actor?.delete?.();
        descriptor.mapper?.delete?.();
    });
}

/**
 * Update the measurement lines for the straightened view to reflect the active measurement.
 * @param {{
 *   cprObjects: {
 *     resliceFilter: vtkImageReslice,
 *     horizontalIndexLines?: Array<{actor: vtkActor, mapper: vtkMapper, polyData: vtkPolyData}>,
 *   },
 *   renderer: vtkRenderer,
 *   renderWindow: vtkRenderWindow,
 * }|null} ctx - Visualization context created during initialization.
 * @param {Union|null|undefined} maybeMeasurement - Active stent measurement (Fable union).
 * @returns {void}
 */
function refreshStraightenedMeasurementLines(ctx, maybeMeasurement) {
    if (!ctx || !ctx.cprObjects?.resliceFilter || !ctx.renderer || !ctx.renderWindow) {
        return;
    }

    const existingLines = Array.isArray(ctx.cprObjects.horizontalIndexLines)
        ? ctx.cprObjects.horizontalIndexLines
        : [];
    const hadLines = existingLines.length > 0;

    disposeStraightenedIndexLines(ctx.renderer, existingLines);
    ctx.cprObjects.horizontalIndexLines = [];

    const lineConfigs = getStraightenedMeasurementLineConfigs(maybeMeasurement);

    if (!lineConfigs.length) {
        if (hadLines) {
            ctx.renderWindow?.render();
        }
        return;
    }

    const descriptors = lineConfigs
        .map((config) =>
            createStraightenedIndexLine(
                ctx.cprObjects.resliceFilter,
                config,
                ctx.renderer,
                ctx.renderWindow
            )
        )
        .filter(Boolean);

    if (!descriptors.length) {
        if (hadLines) {
            ctx.renderWindow?.render();
        }
        return;
    }

    descriptors.forEach((descriptor) => ctx.renderer.addActor(descriptor.actor));
    ctx.cprObjects.horizontalIndexLines = descriptors;

    ctx.renderWindow?.render();
}

/**
 * Compute the I-axis index for a straightened CPR guide line from the proximal end.
 * The I axis corresponds to the distance along the straightened centerline, so we clamp the
 * requested offset against the reslice extent to ensure the line always lies within the image.
 *
 * @param {number[]} extent - vtkImageData extent in the form [iMin, iMax, jMin, jMax, kMin, kMax].
 * @param {number} offset - Measurement index from the proximal end of the centerline.
 * @returns {number|null} - Reslice-space I index or null if the extent is invalid.
 */
function computeStraightenedLineImageIndex(extent, offset) {
    if (!extent || extent.length !== 6) {
        return null;
    }

    const iMin = extent[0];
    const iMax = extent[1];

    if (!Number.isFinite(iMin) || !Number.isFinite(iMax)) {
        return null;
    }

    const span = Math.max(0, iMax - iMin);
    const safeOffset = clamp(Number(offset) || 0, 0, span);

    return iMax - safeOffset;
}

/**
 * Update a single straightened CPR guide line by sampling the current reslice output.
 * Converts the fixed I index into world coordinates for the (I, Jmin) and (I, Jmax) points and
 * writes them into the line's shared vtkPolyData.
 *
 * @param {vtkImageReslice} resliceFilter - Source filter that generates the straightened CPR image.
 * @param {{ polyData: vtkPolyData, offset: number }} lineDescriptor - Mutable descriptor for the line.
 * @param {vtkRenderer} renderer - VTK renderer for viewport calculations.
 * @param {vtkRenderWindow} renderWindow - VTK render window for viewport calculations.
 * @returns {void}
 */
function updateStraightenedIndexLine(resliceFilter, lineDescriptor, renderer, renderWindow) {
    if (!resliceFilter || !lineDescriptor?.polyData) {
        return;
    }

    const reslicedImage = resliceFilter.getOutputData();
    if (!reslicedImage) {
        return;
    }

    const extent = reslicedImage.getExtent();
    if (!extent || extent.length !== 6) {
        console.warn('Horizontal index line: missing extent information.');
        return;
    }

    const [_iMin, _iMax, jMin, jMax, kMin, kMax] = extent;

    if (kMin !== kMax) {
        console.error('Horizontal index line: expected single K slice, got', kMin, kMax);
        return;
    }

    const iIndex = computeStraightenedLineImageIndex(
        extent,
        lineDescriptor.offset,
    );

    if (!Number.isFinite(iIndex)) {
        return;
    }

    // Get the full width endpoints in world space.
    const leftWorld = reslicedImage.indexToWorld([iIndex, jMin, kMin]);
    const rightWorld = reslicedImage.indexToWorld([iIndex, jMax, kMin]);

    let start = leftWorld;
    let end = rightWorld;

    // If renderer and renderWindow are available, calculate 90% based on actual display dimensions.
    if (renderer && renderWindow && leftWorld && rightWorld) {
        const view = renderWindow.getViews()[0];
        if (view) {
            // Get the canvas element.
            const canvas = view.getCanvas();
            if (canvas) {
                const canvasWidth = canvas.width;
                const canvasHeight = canvas.height;

                if (canvasWidth > 0 && canvasHeight > 0) {
                    // In parallel projection, calculate the world-space width visible in the viewport.
                    const camera = renderer.getActiveCamera();
                    const parallelScale = camera.getParallelScale();
                    const aspect = canvasWidth / canvasHeight;

                    // The visible world height is 2 * parallelScale.
                    // The visible world width is 2 * parallelScale * aspect.
                    const worldWidth = 2 * parallelScale * aspect;

                    // Calculate 90% of world width.
                    const targetWorldWidth = worldWidth * 0.9;
                    const halfTargetWidth = targetWorldWidth / 2;

                    // The line should be horizontal at the I index position, so we need to find
                    // the Y coordinate at that position and extend +/- halfTargetWidth in Y direction.
                    const centerWorld = reslicedImage.indexToWorld([iIndex, (jMin + jMax) / 2, kMin]);

                    // Create start and end points by offsetting in Y direction (horizontal in the view).
                    start = [centerWorld[0], centerWorld[1] - halfTargetWidth, centerWorld[2]];
                    end = [centerWorld[0], centerWorld[1] + halfTargetWidth, centerWorld[2]];
                }
            }
        }
    }

    if (!start || !end) {
        console.warn('Horizontal index line: unable to convert IJK to world coordinates.');
        return;
    }

    const points = lineDescriptor.polyData.getPoints();
    if (!points) {
        return;
    }

    if (points.getNumberOfPoints() < 2) {
        points.setNumberOfPoints(2);
    }

    points.setPoint(0, start[0], start[1], start[2]);
    points.setPoint(1, end[0], end[1], end[2]);
    points.modified();

    lineDescriptor.polyData.modified();
}

/**
 * Update a collection of straightened CPR guide lines.
 *
 * @param {vtkImageReslice} resliceFilter - Straightened CPR reslice filter.
 * @param {Array<{ polyData: vtkPolyData, offset: number }>} lineDescriptors - Line descriptors to refresh.
 * @param {vtkRenderer} renderer - VTK renderer for viewport calculations.
 * @param {vtkRenderWindow} renderWindow - VTK render window for viewport calculations.
 * @returns {void}
 */
function updateStraightenedIndexLines(resliceFilter, lineDescriptors = [], renderer, renderWindow) {
    lineDescriptors.forEach((descriptor) => {
        updateStraightenedIndexLine(resliceFilter, descriptor, renderer, renderWindow);
    });
}

/**
 * Create a straightened CPR guide line actor/mapper pair and initialize it against the current reslice output.
 *
 * @param {vtkImageReslice} resliceFilter - Straightened CPR reslice filter.
 * @param {{ key: string, offset: number, color: [number, number, number] }} config - Line configuration.
 * @param {vtkRenderer} renderer - VTK renderer for viewport calculations.
 * @param {vtkRenderWindow} renderWindow - VTK render window for viewport calculations.
 * @returns {{ actor: vtkActor, mapper: vtkMapper, polyData: vtkPolyData, offset: number, key: string }|null}
 */
function createStraightenedIndexLine(resliceFilter, config, renderer, renderWindow) {
    if (!resliceFilter || !config) {
        return null;
    }

    const polyData = vtkPolyData.newInstance();
    const points = vtkPoints.newInstance();
    points.setNumberOfPoints(2);
    polyData.setPoints(points);

    const lines = new Uint16Array([2, 0, 1]);
    polyData.getLines().setData(lines);

    const mapper = vtkMapper.newInstance();
    mapper.setInputData(polyData);

    const actor = vtkActor.newInstance();
    actor.setMapper(mapper);
    const [r, g, b] = config.color || [1, 1, 1];
    actor.getProperty().setColor(r, g, b);
    actor.getProperty().setLineWidth(1);
    actor.getProperty().setLighting(false);

    const descriptor = {
        actor,
        mapper,
        polyData,
        offset: config.offset,
        key: config.key,
    };

    updateStraightenedIndexLine(resliceFilter, descriptor, renderer, renderWindow);

    return descriptor;
}

/**
 * @typedef {Object} StraightenedLengthArrowDescriptor
 * @property {vtkActor} actor - Actor responsible for rendering the arrow.
 * @property {vtkMapper} mapper - Mapper connected to the arrow polydata.
 * @property {vtkPolyData} polyData - Polydata containing line segments for the arrow and heads.
 * @property {number|null} startOffset - Measurement index from the proximal end.
 * @property {number|null} endOffset - Measurement index from the proximal end.
 */

const STRAIGHTENED_ARROW_POSITION_RATIO = 0.75;
const STRAIGHTENED_ARROW_HEAD_LENGTH_RATIO = 0.06;
const STRAIGHTENED_ARROW_HEAD_WIDTH_RATIO = 0.02;
const STRAIGHTENED_ARROW_MIN_HEAD_LENGTH = 0.25;
const STRAIGHTENED_ARROW_MIN_HEAD_WIDTH = 0.25;
const STRAIGHTENED_ARROW_HEAD_LENGTH_PX = 18;
const STRAIGHTENED_ARROW_HEAD_WIDTH_PX = 10;

/**
 * Convert a desired display-space length into world-space magnitude along the supplied direction.
 * @param {vtkRenderer|null|undefined} renderer - Renderer used for world/display transforms.
 * @param {number[]|Float32Array} originWorld - Anchor point expressed in world coordinates.
 * @param {import('gl-matrix').vec3} direction - Direction vector defining the measurement axis.
 * @param {number} pixels - Target size in pixels.
 * @returns {number} Corresponding world-space magnitude or 0 when unavailable.
 */
function getWorldLengthForPixelDelta(renderer, originWorld, direction, pixels) {
    if (!renderer || !originWorld || !direction || !Number.isFinite(pixels) || pixels <= 0) {
        return 0;
    }

    const normalizedDir = vec3.clone(direction);
    if (vec3.len(normalizedDir) < 1e-5) {
        return 0;
    }
    vec3.normalize(normalizedDir, normalizedDir);

    const coord = vtkCoordinate.newInstance();
    coord.setCoordinateSystemToWorld();

    coord.setValue(originWorld[0], originWorld[1], originWorld[2]);
    const startDisplay = coord.getComputedDoubleDisplayValue(renderer);
    const start = [startDisplay[0], startDisplay[1]];

    const samplePoint = vec3.scaleAndAdd(vec3.create(), originWorld, normalizedDir, 1);
    coord.setValue(samplePoint[0], samplePoint[1], samplePoint[2]);
    const endDisplay = coord.getComputedDoubleDisplayValue(renderer);
    const end = [endDisplay[0], endDisplay[1]];

    const pixelsPerWorld = Math.hypot(end[0] - start[0], end[1] - start[1]);

    if (!Number.isFinite(pixelsPerWorld) || pixelsPerWorld < 1e-5) {
        return 0;
    }

    return pixels / pixelsPerWorld;
}

/**
 * Extract offsets for the straightened length measurement arrow.
 * @param {Union|null|undefined} maybeMeasurement - Active measurement union from Elmish state.
 * @returns {{start: number, end: number}|null}
 */
function getStraightenedLengthMeasurementOffsets(maybeMeasurement) {
    if (isNullOrUndefined(maybeMeasurement) || maybeMeasurement.name !== 'Length') {
        return null;
    }

    const range = maybeMeasurement?.fields?.[1]?.[0];
    const firstIndex = Number(range?.firstIndex);
    const secondIndex = Number(range?.secondIndex);

    if (!isValidCenterlineIndex(firstIndex) || !isValidCenterlineIndex(secondIndex)) {
        return null;
    }

    return {
        start: firstIndex,
        end: secondIndex,
    };
}

/**
 * Create the reusable arrow descriptor for straightened length measurements.
 * @returns {StraightenedLengthArrowDescriptor}
 */
function createStraightenedMeasurementArrow() {
    const polyData = vtkPolyData.newInstance();
    const points = vtkPoints.newInstance();
    points.setNumberOfPoints(6);
    polyData.setPoints(points);

    // Five independent line segments: shaft plus four arrowhead edges.
    const lines = new Uint16Array([
        2, 0, 1,
        2, 1, 2,
        2, 1, 3,
        2, 0, 4,
        2, 0, 5,
    ]);
    polyData.getLines().setData(lines);

    const mapper = vtkMapper.newInstance();
    mapper.setInputData(polyData);

    const actor = vtkActor.newInstance();
    actor.setMapper(mapper);
    actor.getProperty().setColor(1.0, 0.9, 0.0);
    actor.getProperty().setLineWidth(2);
    actor.getProperty().setLighting(false);

    return {
        actor,
        mapper,
        polyData,
        startOffset: null,
        endOffset: null,
    };
}

/**
 * Dispose of the arrow actor and associated resources.
 * @param {vtkRenderer|null|undefined} renderer - Renderer the arrow was added to.
 * @param {StraightenedLengthArrowDescriptor|null|undefined} arrowDescriptor - Descriptor to dispose.
 * @returns {void}
 */
function disposeStraightenedMeasurementArrow(renderer, arrowDescriptor) {
    if (!arrowDescriptor) {
        return;
    }

    if (renderer && arrowDescriptor.actor) {
        renderer.removeActor(arrowDescriptor.actor);
    }

    arrowDescriptor.actor?.delete?.();
    arrowDescriptor.mapper?.delete?.();
    arrowDescriptor.polyData?.delete?.();
}

/**
 * Update the arrow geometry so it stays aligned with the measurement bounds.
 * @param {vtkImageReslice} resliceFilter - Reslice filter powering the straightened view.
 * @param {StraightenedLengthArrowDescriptor} arrowDescriptor - Descriptor containing offsets and VTK data.
 * @param {vtkRenderer|null} renderer - Renderer used to evaluate pixel-to-world scaling.
 * @param {vtkRenderWindow|null} renderWindow - Render window providing canvas dimensions.
 * @returns {boolean} True when the arrow geometry was updated.
 */
function updateStraightenedMeasurementArrow(resliceFilter, arrowDescriptor, renderer, renderWindow) {
    if (!resliceFilter || !arrowDescriptor || !Number.isFinite(arrowDescriptor.startOffset) || !Number.isFinite(arrowDescriptor.endOffset)) {
        return false;
    }

    const reslicedImage = resliceFilter.getOutputData();
    if (!reslicedImage) {
        return false;
    }

    const extent = reslicedImage.getExtent();
    if (!extent || extent.length !== 6) {
        console.warn('Length arrow: missing extent information.');
        return false;
    }

    const [iMin, iMax, jMin, jMax, kMin, kMax] = extent;

    if (kMin !== kMax) {
        console.error('Length arrow: expected a single K slice.');
        return false;
    }

    // Compute I indices for start and end points from the proximal end.
    const startIndex = computeStraightenedLineImageIndex(extent, arrowDescriptor.startOffset);
    const endIndex = computeStraightenedLineImageIndex(extent, arrowDescriptor.endOffset);

    if (!Number.isFinite(startIndex) || !Number.isFinite(endIndex)) {
        return false;
    }

    const lowIndex = Math.min(startIndex, endIndex);
    const highIndex = Math.max(startIndex, endIndex);

    // Position arrow laterally using viewport width calculation.
    const centerJ = jMin + (jMax - jMin) * 0.5;
    let startWorld = reslicedImage.indexToWorld([lowIndex, centerJ, kMin]);
    let endWorld = reslicedImage.indexToWorld([highIndex, centerJ, kMin]);

    if (!startWorld || !endWorld) {
        console.warn('Length arrow: unable to map IJK to world coordinates.');
        return false;
    }

    // Compute lateral direction from camera view.
    let lateralDir = null;
    if (renderer) {
        const camera = renderer.getActiveCamera();
        const viewPlaneNormal = camera?.getViewPlaneNormal?.();
        const viewUp = camera?.getViewUp?.();
        if (viewPlaneNormal && viewUp) {
            lateralDir = vec3.cross(vec3.create(), viewUp, viewPlaneNormal);
            if (vec3.len(lateralDir) > 1e-5) {
                vec3.normalize(lateralDir, lateralDir);
                // Ensure lateral direction points right in display space.
                const coord = vtkCoordinate.newInstance();
                coord.setCoordinateSystemToWorld();
                coord.setValue(startWorld[0], startWorld[1], startWorld[2]);
                const referenceDisplay = coord.getComputedDoubleDisplayValue(renderer);
                const sampleWorld = vec3.add(vec3.create(), startWorld, lateralDir);
                coord.setValue(sampleWorld[0], sampleWorld[1], sampleWorld[2]);
                const sampleDisplay = coord.getComputedDoubleDisplayValue(renderer);
                if (sampleDisplay?.[0] < referenceDisplay?.[0]) {
                    vec3.scale(lateralDir, lateralDir, -1);
                }
            } else {
                lateralDir = null;
            }
        }
    }

    // Position arrow at 75% of viewport width.
    if (lateralDir && renderer && renderWindow) {
        const view = renderWindow.getViews()?.[0];
        const canvas = view?.getCanvas?.();
        if (canvas?.width > 0 && canvas?.height > 0) {
            const camera = renderer.getActiveCamera();
            const parallelScale = camera.getParallelScale();
            const aspect = canvas.width / canvas.height;
            const worldWidth = 2 * parallelScale * aspect;
            const offsetFromCenter = (STRAIGHTENED_ARROW_POSITION_RATIO - 0.5) * worldWidth;
            const lateralShift = vec3.scale(vec3.create(), lateralDir, offsetFromCenter);
            startWorld = vec3.add(vec3.create(), startWorld, lateralShift);
            endWorld = vec3.add(vec3.create(), endWorld, lateralShift);
        }
    }

    // Compute arrow direction and length.
    const arrowDir = vec3.sub(vec3.create(), endWorld, startWorld);
    const arrowLength = vec3.len(arrowDir);

    if (!Number.isFinite(arrowLength) || arrowLength < 1e-5) {
        return false;
    }

    vec3.normalize(arrowDir, arrowDir);

    // Compute perpendicular direction for arrowheads.
    const perpDir = lateralDir && vec3.len(lateralDir) > 1e-5
        ? vec3.normalize(vec3.create(), lateralDir)
        : vec3.normalize(vec3.create(), vec3.cross(vec3.create(), arrowDir, [0, 0, 1]));

    // Compute arrowhead dimensions in world space from pixel sizes.
    let headLength = renderer
        ? getWorldLengthForPixelDelta(renderer, startWorld, arrowDir, STRAIGHTENED_ARROW_HEAD_LENGTH_PX)
        : STRAIGHTENED_ARROW_MIN_HEAD_LENGTH;
    headLength = clamp(headLength || STRAIGHTENED_ARROW_MIN_HEAD_LENGTH, STRAIGHTENED_ARROW_MIN_HEAD_LENGTH, arrowLength * 0.4);

    let headWidth = renderer
        ? getWorldLengthForPixelDelta(renderer, startWorld, perpDir, STRAIGHTENED_ARROW_HEAD_WIDTH_PX)
        : STRAIGHTENED_ARROW_MIN_HEAD_WIDTH;
    headWidth = clamp(headWidth || STRAIGHTENED_ARROW_MIN_HEAD_WIDTH, STRAIGHTENED_ARROW_MIN_HEAD_WIDTH, headLength * 2);

    // Build arrow geometry: shaft from startWorld to endWorld, with arrowheads at each end.
    const startInset = vec3.scaleAndAdd(vec3.create(), startWorld, arrowDir, headLength);
    const startLeft = vec3.scaleAndAdd(vec3.create(), startInset, perpDir, headWidth);
    const startRight = vec3.scaleAndAdd(vec3.create(), startInset, perpDir, -headWidth);

    const endInset = vec3.scaleAndAdd(vec3.create(), endWorld, arrowDir, -headLength);
    const endLeft = vec3.scaleAndAdd(vec3.create(), endInset, perpDir, headWidth);
    const endRight = vec3.scaleAndAdd(vec3.create(), endInset, perpDir, -headWidth);

    // Update polydata points.
    const points = arrowDescriptor.polyData.getPoints();
    if (!points) {
        return false;
    }

    if (points.getNumberOfPoints() < 6) {
        points.setNumberOfPoints(6);
    }

    points.setPoint(0, startWorld[0], startWorld[1], startWorld[2]);
    points.setPoint(1, endWorld[0], endWorld[1], endWorld[2]);
    points.setPoint(2, endLeft[0], endLeft[1], endLeft[2]);
    points.setPoint(3, endRight[0], endRight[1], endRight[2]);
    points.setPoint(4, startLeft[0], startLeft[1], startLeft[2]);
    points.setPoint(5, startRight[0], startRight[1], startRight[2]);
    points.modified();

    arrowDescriptor.polyData.modified();

    return true;
}

/**
 * Synchronize the arrow descriptor with the supplied measurement state.
 * @param {{ cprObjects: { resliceFilter: vtkImageReslice, lengthMeasurementArrow?: StraightenedLengthArrowDescriptor|null }, renderer: vtkRenderer, renderWindow: vtkRenderWindow }|null} ctx - Visualization context.
 * @param {Union|null|undefined} maybeMeasurement - Active measurement union from Elmish state.
 * @returns {void}
 */
function refreshStraightenedLengthMeasurementArrow(ctx, maybeMeasurement) {
    if (!ctx || !ctx.cprObjects?.resliceFilter || !ctx.renderer) {
        return;
    }

    const offsets = getStraightenedLengthMeasurementOffsets(maybeMeasurement);

    if (!offsets) {
        if (ctx.cprObjects.lengthMeasurementArrow) {
            disposeStraightenedMeasurementArrow(ctx.renderer, ctx.cprObjects.lengthMeasurementArrow);
            ctx.cprObjects.lengthMeasurementArrow = null;
            ctx.renderWindow?.render();
        }
        return;
    }

    let arrowDescriptor = ctx.cprObjects.lengthMeasurementArrow;
    let addedArrow = false;

    if (!arrowDescriptor) {
        arrowDescriptor = createStraightenedMeasurementArrow();
        ctx.renderer.addActor(arrowDescriptor.actor);
        ctx.cprObjects.lengthMeasurementArrow = arrowDescriptor;
        addedArrow = true;
    }

    arrowDescriptor.startOffset = offsets.start;
    arrowDescriptor.endOffset = offsets.end;

    const updated = updateStraightenedMeasurementArrow(
        ctx.cprObjects.resliceFilter,
        arrowDescriptor,
        ctx.renderer,
        ctx.renderWindow,
    );

    if ((addedArrow || updated) && ctx.renderWindow) {
        ctx.renderWindow.render();
    }
}

/**
 * Initialize the Konva stage for the straightened CPR measurement label.
 * @param {HTMLElement} container - Container element for the Konva stage.
 * @param {string} viewportId - Unique identifier for the viewport.
 * @returns {{stage: Konva.Stage, layer: Konva.Layer, label: Konva.Label, konvaContainer: HTMLElement}|null}
 */
function initializeStraightenedMeasurementLabel(container, viewportId) {
    if (!container) {
        return null;
    }

    // Create a div container for Konva that overlays the VTK canvas.
    const konvaContainer = document.createElement('div');
    konvaContainer.id = `cprMeasurementLabelContainer-${viewportId}`;
    konvaContainer.style.position = 'absolute';
    konvaContainer.style.top = '0';
    konvaContainer.style.left = '0';
    konvaContainer.style.width = '100%';
    konvaContainer.style.height = '100%';
    konvaContainer.style.pointerEvents = 'none'; // Allow clicks to pass through to VTK.
    konvaContainer.style.zIndex = '1';
    container.appendChild(konvaContainer);

    const stage = new Konva.Stage({
        container: konvaContainer,
        width: container.offsetWidth,
        height: container.offsetHeight,
    });

    const layer = new Konva.Layer();
    stage.add(layer);

    const label = new Konva.Label({
        visible: false,
    });

    const tag = new Konva.Tag({
        fill: 'yellow',
        cornerRadius: 3,
        pointerDirection: 'none',
    });

    const text = new Konva.Text({
        text: '',
        fontSize: 24,
        fontFamily: 'Open Sans',
        fonstStyle: 'Bold',
        fill: 'black',
        padding: 5,
    });

    label.add(tag);
    label.add(text);
    layer.add(label);

    return { stage, layer, label, konvaContainer };
}

/**
 * Update the Konva measurement label for the straightened CPR view.
 * @param {{ cprObjects: { resliceFilter: vtkImageReslice, lengthMeasurementArrow?: StraightenedLengthArrowDescriptor|null }, renderer: vtkRenderer, renderWindow: vtkRenderWindow, konvaElements?: { stage: Konva.Stage, layer: Konva.Layer, label: Konva.Label } }|null} ctx - Visualization context.
 * @param {Union|null|undefined} maybeMeasurement - Active measurement union from Elmish state.
 * @returns {void}
 */
function updateStraightenedMeasurementLabel(ctx, maybeMeasurement) {
    if (!ctx || !ctx.konvaElements || !ctx.cprObjects?.resliceFilter || !ctx.renderer || !ctx.renderWindow) {
        return;
    }

    const { stage, layer, label } = ctx.konvaElements;

    if (!stage || !layer || !label) {
        return;
    }

    function hideLabel() {
        label.visible(false);
        layer.batchDraw();
    }

    // Hide the label if there's no measurement.
    if (isNullOrUndefined(maybeMeasurement) || maybeMeasurement.name !== 'Length') {
        hideLabel();
        return;
    }

    const arrowDescriptor = ctx.cprObjects.lengthMeasurementArrow;
    if (!arrowDescriptor) {
        hideLabel();
        return;
    }

    // Get the measurement value from fields[1][1].
    const measurementValue = maybeMeasurement?.fields?.[1]?.[1];
    if (!Number.isFinite(measurementValue)) {
        hideLabel();
        return;
    }

    // Format the value.
    const formattedValue = measurementValue.toFixed(1);
    label.getText().text(formattedValue);

    // Get the arrow's start and end points in world coordinates.
    const points = arrowDescriptor.polyData.getPoints();
    if (!points || points.getNumberOfPoints() < 2) {
        hideLabel();
        return;
    }

    const startPoint = points.getPoint(0);
    const endPoint = points.getPoint(1);
    if (!startPoint || startPoint.length < 3 || !endPoint || endPoint.length < 3) {
        hideLabel();
        return;
    }

    // Calculate the midpoint of the arrow.
    const midPoint = [
        (startPoint[0] + endPoint[0]) / 2,
        (startPoint[1] + endPoint[1]) / 2,
        (startPoint[2] + endPoint[2]) / 2
    ];

    // Convert the arrow midpoint to display coordinates.
    const coord = vtkCoordinate.newInstance();
    coord.setCoordinateSystemToWorld();
    coord.setValue(midPoint[0], midPoint[1], midPoint[2]);
    const displayCoords = coord.getComputedDoubleDisplayValue(ctx.renderer);

    if (!displayCoords || displayCoords.length < 2) {
        hideLabel();
        return;
    }

    // Convert display coordinates to Konva canvas coordinates.
    // VTK display coordinates have origin at bottom-left, Konva has origin at top-left.
    const canvas = ctx.renderWindow.getViews()?.[0]?.getCanvas?.();
    if (!canvas || !canvas.height) {
        hideLabel();
        return;
    }

    // displayCoords is expressed in device pixels (canvas.width/height).
    // We need to flip Y in device pixel space, then convert to CSS pixels.
    const dpr = window.devicePixelRatio || 1;
    const konvaX = displayCoords[0] / dpr;
    const konvaY = (canvas.height - displayCoords[1]) / dpr;

    // Validate coordinates before positioning (can be NaN if container is hidden).
    if (!Number.isFinite(konvaX) || !Number.isFinite(konvaY) || !Number.isFinite(label.height())) {
        hideLabel();
        return;
    }

    // Position the label: left edge 20 pixels to the right, vertically centered.
    label.x(konvaX + 20);
    label.y(konvaY - (label.height() / 2));
    label.visible(true);

    layer.batchDraw();
}

// ============================================================================
// Stretched CPR Measurement Indicators
// ============================================================================

/**
 * @typedef {Object} StretchedMeasurementLineConfig
 * @property {string} key - Stable identifier for the line.
 * @property {number} unifiedIndex - Unified branch index from the measurement.
 * @property {[number, number, number]} color - RGB color for the line.
 * @property {'start'|'end'} role - Role of this line (start or end).
 */

/**
 * @typedef {Object} StretchedMeasurementLineDescriptor
 * @property {vtkActor} actor - VTK actor for the line.
 * @property {vtkMapper} mapper - VTK mapper for the line.
 * @property {vtkPolyData} polyData - VTK polydata for the line geometry.
 * @property {number} unifiedIndex - Unified branch index.
 * @property {string} key - Stable identifier.
 * @property {'start'|'end'} role - Role of this line.
 */

/**
 * @typedef {Object} StretchedMeasurementArrowDescriptor
 * @property {vtkActor} actor - VTK actor for the arrow.
 * @property {vtkMapper} mapper - VTK mapper for the arrow.
 * @property {vtkPolyData} polyData - VTK polydata for the arrow geometry.
 * @property {number|null} startUnifiedIndex - Unified index for the start point.
 * @property {number|null} endUnifiedIndex - Unified index for the end point.
 */

/**
 * @typedef {Object} StretchedMeasurementKonvaElements
 * @property {Konva.Stage} stage - Konva stage.
 * @property {Konva.Layer} layer - Konva layer.
 * @property {Konva.Label} label - Measurement value label.
 * @property {Konva.Line} topDashedLine - Top dashed horizontal line.
 * @property {Konva.Line} bottomDashedLine - Bottom dashed horizontal line.
 * @property {HTMLElement} konvaContainer - Container element for the Konva stage.
 */

const stretchedMeasurementColors = {
    start: [0.0, 0.0, 1.0], // Blue.
    end: [1.0, 0.0, 0.0],   // Red.
};

const STRETCHED_LINE_WIDTH_RATIO = 0.9; // 90% of container width for indicator lines.
const STRETCHED_ARROW_POSITION_RATIO = 0.75; // Same as straightened CPR.
const STRETCHED_ARROW_HEAD_LENGTH_PX = 18;
const STRETCHED_ARROW_HEAD_WIDTH_PX = 10;
const STRETCHED_ARROW_MIN_HEAD_LENGTH = 0.25;
const STRETCHED_ARROW_MIN_HEAD_WIDTH = 0.25;

/**
 * Get the branch selection type for converting unified index to master index.
 * @param {Union|null|undefined} selectedBranch - Selected branch (UnifiedDistanceBranch).
 * @returns {Object|null} - CenterlineBranchSelection value or null.
 */
function getBranchSelectionForConversion(selectedBranch) {
    if (isNullOrUndefined(selectedBranch)) {
        return null;
    }

    const branchName = getDUName(selectedBranch);

    switch (branchName) {
        case CenterlineDistanceBranchSelection.LEFT:
            return CenterlineBranchSelection.DISTANCE_LEFT;
        case CenterlineDistanceBranchSelection.RIGHT:
            return CenterlineBranchSelection.DISTANCE_RIGHT;
        case CenterlineDistanceBranchSelection.CUSP:
            return CenterlineBranchSelection.DISTANCE_CUSP;
        default:
            return null;
    }
}

/**
 * Extract measurement line configurations for stretched CPR view.
 * @param {Union|null|undefined} maybeMeasurement - Active stent measurement (Fable union).
 * @returns {StretchedMeasurementLineConfig[]} - Line configurations.
 */
function getStretchedMeasurementLineConfigs(maybeMeasurement) {
    if (isNullOrUndefined(maybeMeasurement)) {
        return [];
    }

    const measurementName = maybeMeasurement.name;

    if (diameterMeasurementCaseNames.includes(measurementName)) {
        const selection = maybeMeasurement?.fields?.[1]?.[0];
        const index = Number(selection?.index);

        if (!isValidCenterlineIndex(index)) {
            return [];
        }

        return [
            {
                key: `stretched-${measurementName}-${index}`,
                unifiedIndex: index,
                color: stretchedMeasurementColors.start,
                role: 'start',
            },
        ];
    }

    if (measurementName === 'Length') {
        const range = maybeMeasurement?.fields?.[1]?.[0];
        const firstIndex = Number(range?.firstIndex);
        const secondIndex = Number(range?.secondIndex);
        const configs = [];

        if (isValidCenterlineIndex(firstIndex)) {
            configs.push({
                key: `stretched-${measurementName}-start-${firstIndex}`,
                unifiedIndex: firstIndex,
                color: stretchedMeasurementColors.start,
                role: 'start',
            });
        }

        if (isValidCenterlineIndex(secondIndex)) {
            configs.push({
                key: `stretched-${measurementName}-end-${secondIndex}`,
                unifiedIndex: secondIndex,
                color: stretchedMeasurementColors.end,
                role: 'end',
            });
        }

        return configs;
    }

    return [];
}

/**
 * Extract length measurement offsets for stretched CPR view.
 * @param {Union|null|undefined} maybeMeasurement - Active measurement union from Elmish state.
 * @returns {{startUnifiedIndex: number, endUnifiedIndex: number, value: number}|null}
 */
function getStretchedLengthMeasurementOffsets(maybeMeasurement) {
    if (isNullOrUndefined(maybeMeasurement) || maybeMeasurement.name !== 'Length') {
        return null;
    }

    const range = maybeMeasurement?.fields?.[1]?.[0];
    const firstIndex = Number(range?.firstIndex);
    const secondIndex = Number(range?.secondIndex);
    const measurementValue = maybeMeasurement?.fields?.[1]?.[1];

    if (!isValidCenterlineIndex(firstIndex) || !isValidCenterlineIndex(secondIndex)) {
        return null;
    }

    return {
        startUnifiedIndex: firstIndex,
        endUnifiedIndex: secondIndex,
        value: Number.isFinite(measurementValue) ? measurementValue : null,
    };
}

/**
 * Create a stretched CPR measurement indicator line actor.
 * @returns {StretchedMeasurementLineDescriptor}
 */
function createStretchedMeasurementLine() {
    const polyData = vtkPolyData.newInstance();
    const points = vtkPoints.newInstance();
    points.setNumberOfPoints(2);
    polyData.setPoints(points);

    const lines = new Uint16Array([2, 0, 1]);
    polyData.getLines().setData(lines);

    const mapper = vtkMapper.newInstance();
    mapper.setInputData(polyData);

    const actor = vtkActor.newInstance();
    actor.setMapper(mapper);
    actor.getProperty().setLineWidth(1);
    actor.getProperty().setLighting(false);

    return {
        actor,
        mapper,
        polyData,
        unifiedIndex: null,
        key: null,
        role: null,
    };
}

/**
 * Compute world position and normal for a centerline index on the stretched CPR.
 * @param {number} centerlineIndex - Index into the oriented centerline.
 * @param {vtkPolyLine} orientedCenterline - Oriented centerline from the mapper.
 * @param {CPRParams} params - CPR parameters.
 * @returns {{worldPosition: number[], normal: number[]}|null}
 */
function getCenterlineWorldPositionAndNormal(centerlineIndex, orientedCenterline, params) {
    const ptsArray = orientedCenterline.getPoints().getData();
    const nPts = orientedCenterline.getPoints().getNumberOfPoints();

    if (
        centerlineIndex < 0 ||
        centerlineIndex >= nPts ||
        !ptsArray
    ) {
        return null;
    }

    // World position of this centerline point.
    const worldPosition = [
        ptsArray[3 * centerlineIndex],
        ptsArray[3 * centerlineIndex + 1],
        ptsArray[3 * centerlineIndex + 2],
    ];

    // Get tangent direction from orientations if available.
    const orientations = params.orientations;
    let tangent = [0, 1, 0]; // Default tangent.

    if (orientations && orientations.length >= (centerlineIndex + 1) * 16) {
        // The orientation is stored as a mat4; columns are [T, B, N, P].
        // T (tangent) is column 0.
        const offset = centerlineIndex * 16;
        tangent = [
            orientations[offset],
            orientations[offset + 1],
            orientations[offset + 2],
        ];
        vec3.normalize(tangent, tangent);
    } else {
        // Fallback: compute tangent from neighboring points.
        if (centerlineIndex > 0 && centerlineIndex < nPts - 1) {
            const prev = [
                ptsArray[3 * (centerlineIndex - 1)],
                ptsArray[3 * (centerlineIndex - 1) + 1],
                ptsArray[3 * (centerlineIndex - 1) + 2],
            ];
            const next = [
                ptsArray[3 * (centerlineIndex + 1)],
                ptsArray[3 * (centerlineIndex + 1) + 1],
                ptsArray[3 * (centerlineIndex + 1) + 2],
            ];
            vec3.subtract(tangent, next, prev);
            vec3.normalize(tangent, tangent);
        }
    }

    // Compute a normal perpendicular to the tangent in the CPR plane.
    // Use the tangent direction from params to define the cross-section orientation.
    const cprTangent = params.tangentDirection || [1, 0, 0];
    let normal = vec3.cross([], tangent, cprTangent);

    if (vec3.len(normal) < 1e-5) {
        // Fallback if tangent is parallel to cprTangent.
        normal = vec3.cross([], tangent, [0, 0, 1]);
        if (vec3.len(normal) < 1e-5) {
            normal = vec3.cross([], tangent, [0, 1, 0]);
        }
    }
    vec3.normalize(normal, normal);

    return { worldPosition, normal };
}

/**
 * Update a stretched measurement indicator line geometry.
 * @param {StretchedMeasurementLineDescriptor} lineDescriptor - Line descriptor to update.
 * @param {number} centerlineIndex - Index into the oriented centerline.
 * @param {vtkPolyLine} orientedCenterline - Oriented centerline from the mapper.
 * @param {CPRParams} params - CPR parameters.
 * @param {mat4} actorMatrix - Actor user matrix for world-to-CPR transform.
 * @param {CPRFrame} currentCPRFrame - Current CPR frame data.
 * @returns {boolean} - True if the geometry was updated.
 */
function updateStretchedMeasurementLine(
    lineDescriptor,
    centerlineIndex,
    orientedCenterline,
    params,
    actorMatrix,
    currentCPRFrame,
) {
    if (!lineDescriptor || !orientedCenterline || !params || !actorMatrix) {
        return false;
    }

    // Compute CPR coordinates for this centerline index.
    const baseCPR = computeCPRCoordsForCenterlineIndex(
        centerlineIndex,
        orientedCenterline,
        params
    );

    if (!baseCPR) {
        return false;
    }

    // The indicator line extends 90% of the CPR width, centered horizontally.
    // In CPR space, X is across the width, Y is along the height (distance along centerline).
    const lineWidth = params.width * STRETCHED_LINE_WIDTH_RATIO;
    const halfLineWidth = lineWidth / 2;
    const centerX = params.width / 2;

    // Create start and end points in CPR space (horizontal line centered in view).
    const cprStart = [centerX - halfLineWidth, baseCPR[1], 0];
    const cprEnd = [centerX + halfLineWidth, baseCPR[1], 0];

    // Transform from CPR space to world space.
    const worldStart = vec3.transformMat4([], cprStart, actorMatrix);
    const worldEnd = vec3.transformMat4([], cprEnd, actorMatrix);

    // Update polydata points.
    const points = lineDescriptor.polyData.getPoints();
    if (!points) {
        return false;
    }

    if (points.getNumberOfPoints() < 2) {
        points.setNumberOfPoints(2);
    }

    points.setPoint(0, worldStart[0], worldStart[1], worldStart[2]);
    points.setPoint(1, worldEnd[0], worldEnd[1], worldEnd[2]);
    points.modified();

    lineDescriptor.polyData.modified();

    return true;
}

/**
 * Dispose of stretched measurement line descriptors.
 * @param {vtkRenderer|null|undefined} renderer - Renderer hosting the lines.
 * @param {StretchedMeasurementLineDescriptor[]} [lineDescriptors=[]] - Line descriptors to dispose.
 */
function disposeStretchedMeasurementLines(renderer, lineDescriptors = []) {
    if (!Array.isArray(lineDescriptors) || !lineDescriptors.length) {
        return;
    }

    lineDescriptors.forEach((descriptor) => {
        if (!descriptor) {
            return;
        }

        if (renderer && descriptor.actor) {
            renderer.removeActor(descriptor.actor);
        }

        descriptor.actor?.delete?.();
        descriptor.mapper?.delete?.();
        descriptor.polyData?.delete?.();
    });
}

/**
 * Refresh stretched CPR measurement indicator lines.
 * @param {Object} ctx - Visualization context.
 * @param {Union|null|undefined} maybeMeasurement - Active measurement.
 * @param {Union|null|undefined} selectedBranch - Selected branch.
 * @param {CPRFrame} currentCPRFrame - Current CPR frame data.
 */
function refreshStretchedMeasurementLines(ctx, maybeMeasurement, selectedBranch, currentCPRFrame) {
    if (!ctx || !ctx.cprObjects?.mapper || !ctx.renderer || !ctx.renderWindow) {
        return;
    }

    const { cprObjects, renderer, renderWindow } = ctx;
    const { mapper, actor, wallCenterline } = cprObjects;

    // Dispose existing lines.
    const existingLines = Array.isArray(cprObjects.stretchedMeasurementLines)
        ? cprObjects.stretchedMeasurementLines
        : [];
    const hadLines = existingLines.length > 0;

    disposeStretchedMeasurementLines(renderer, existingLines);
    cprObjects.stretchedMeasurementLines = [];

    // Get line configurations.
    const lineConfigs = getStretchedMeasurementLineConfigs(maybeMeasurement);

    if (!lineConfigs.length) {
        if (hadLines) {
            renderWindow.render();
        }
        return;
    }

    // Get branch selection for index conversion.
    const branchSelection = getBranchSelectionForConversion(selectedBranch);
    if (!branchSelection || !wallCenterline) {
        if (hadLines) {
            renderWindow.render();
        }
        return;
    }

    const centerlineFieldData = wallCenterline.getFieldData();

    // Get oriented centerline and CPR params.
    const orientedCenterline = mapper.getOrientedCenterline();
    if (!orientedCenterline) {
        return;
    }

    const actorMatrix = actor.getUserMatrix();
    if (!actorMatrix) {
        return;
    }

    const distancesToFirstPoint = orientedCenterline.getDistancesToFirstPoint();
    const orientations = orientedCenterline.getOrientations() || null;

    const params = {
        width: currentCPRFrame.width,
        height: currentCPRFrame.height,
        tangentDirection: mapper.getTangentDirection(),
        centerPoint: mapper.getCenterPoint(),
        distancesToFirstPoint,
        orientations,
    };

    // Create and update lines.
    const descriptors = lineConfigs.map((config) => {
        // Convert unified index to master index, then to centerline index.
        const masterIndex = convertUnifiedBranchIndexToMasterIndex(
            config.unifiedIndex,
            branchSelection,
            centerlineFieldData
        );

        // Find the centerline index that corresponds to this master index.
        // The branchOriginalIndices maps branch polydata index -> master index.
        const branchName = getDUName(selectedBranch);
        const branchIndices = cprObjects.branchOriginalIndices?.[branchName];

        let centerlineIndex = -1;
        if (branchIndices) {
            centerlineIndex = branchIndices.indexOf(masterIndex);
        }

        if (centerlineIndex < 0) {
            return null;
        }

        const lineDescriptor = createStretchedMeasurementLine();
        lineDescriptor.unifiedIndex = config.unifiedIndex;
        lineDescriptor.key = config.key;
        lineDescriptor.role = config.role;

        const [r, g, b] = config.color;
        lineDescriptor.actor.getProperty().setColor(r, g, b);

        const updated = updateStretchedMeasurementLine(
            lineDescriptor,
            centerlineIndex,
            orientedCenterline,
            params,
            actorMatrix,
            currentCPRFrame
        );

        if (!updated) {
            lineDescriptor.actor?.delete?.();
            lineDescriptor.mapper?.delete?.();
            lineDescriptor.polyData?.delete?.();
            return null;
        }

        return lineDescriptor;
    }).filter(Boolean);

    if (!descriptors.length) {
        if (hadLines) {
            renderWindow.render();
        }
        return;
    }

    descriptors.forEach((descriptor) => renderer.addActor(descriptor.actor));
    cprObjects.stretchedMeasurementLines = descriptors;

    renderWindow.render();
}

/**
 * Create the arrow descriptor for stretched length measurements.
 * @returns {StretchedMeasurementArrowDescriptor}
 */
function createStretchedMeasurementArrow() {
    const polyData = vtkPolyData.newInstance();
    const points = vtkPoints.newInstance();
    points.setNumberOfPoints(6);
    polyData.setPoints(points);

    // Five independent line segments: shaft plus four arrowhead edges.
    const lines = new Uint16Array([
        2, 0, 1,
        2, 1, 2,
        2, 1, 3,
        2, 0, 4,
        2, 0, 5,
    ]);
    polyData.getLines().setData(lines);

    const mapper = vtkMapper.newInstance();
    mapper.setInputData(polyData);

    const actor = vtkActor.newInstance();
    actor.setMapper(mapper);
    actor.getProperty().setColor(1.0, 0.9, 0.0); // Yellow.
    actor.getProperty().setLineWidth(2);
    actor.getProperty().setLighting(false);

    return {
        actor,
        mapper,
        polyData,
        startUnifiedIndex: null,
        endUnifiedIndex: null,
    };
}

/**
 * Dispose of stretched measurement arrow.
 * @param {vtkRenderer|null|undefined} renderer - Renderer hosting the arrow.
 * @param {StretchedMeasurementArrowDescriptor|null|undefined} arrowDescriptor - Arrow descriptor to dispose.
 */
function disposeStretchedMeasurementArrow(renderer, arrowDescriptor) {
    if (!arrowDescriptor) {
        return;
    }

    if (renderer && arrowDescriptor.actor) {
        renderer.removeActor(arrowDescriptor.actor);
    }

    arrowDescriptor.actor?.delete?.();
    arrowDescriptor.mapper?.delete?.();
    arrowDescriptor.polyData?.delete?.();
}

/**
 * Update stretched measurement arrow geometry.
 * @param {StretchedMeasurementArrowDescriptor} arrowDescriptor - Arrow descriptor.
 * @param {number} startCenterlineIndex - Start centerline index.
 * @param {number} endCenterlineIndex - End centerline index.
 * @param {vtkPolyLine} orientedCenterline - Oriented centerline.
 * @param {CPRParams} params - CPR parameters.
 * @param {mat4} actorMatrix - Actor user matrix.
 * @param {CPRFrame} currentCPRFrame - Current CPR frame.
 * @param {vtkRenderer} renderer - Renderer for pixel-to-world calculations.
 * @param {vtkRenderWindow} renderWindow - Render window for viewport calculations.
 * @returns {boolean} - True if arrow was updated.
 */
function updateStretchedMeasurementArrow(
    arrowDescriptor,
    startCenterlineIndex,
    endCenterlineIndex,
    orientedCenterline,
    params,
    actorMatrix,
    currentCPRFrame,
    renderer,
    renderWindow,
) {
    if (!arrowDescriptor || !orientedCenterline || !params || !actorMatrix) {
        return false;
    }

    // Get CPR coordinates for both endpoints.
    const startCPR = computeCPRCoordsForCenterlineIndex(
        startCenterlineIndex,
        orientedCenterline,
        params
    );

    const endCPR = computeCPRCoordsForCenterlineIndex(
        endCenterlineIndex,
        orientedCenterline,
        params
    );

    if (!startCPR || !endCPR) {
        return false;
    }

    // Calculate arrow X position based on visible viewport width, not CPR image width.
    // Use the camera's parallel scale and aspect ratio to determine the visible world width.
    let arrowX = params.width * STRETCHED_ARROW_POSITION_RATIO; // Fallback to CPR width.

    if (renderer && renderWindow) {
        const view = renderWindow.getViews()?.[0];
        const canvas = view?.getCanvas?.();
        if (canvas?.width > 0 && canvas?.height > 0) {
            const camera = renderer.getActiveCamera();
            const parallelScale = camera.getParallelScale();
            const aspect = canvas.width / canvas.height;
            const visibleWorldWidth = 2 * parallelScale * aspect;

            // The CPR image is centered in the viewport.
            // Calculate the arrow position as a percentage of the visible viewport width.
            const centerX = params.width / 2;
            const offsetFromCenter = (STRETCHED_ARROW_POSITION_RATIO - 0.5) * visibleWorldWidth;
            arrowX = centerX + offsetFromCenter;
        }
    }

    // Determine which point is higher (lower Y in CPR space = higher on screen).
    const lowY = Math.min(startCPR[1], endCPR[1]);
    const highY = Math.max(startCPR[1], endCPR[1]);

    // Arrow goes from lowY to highY at arrowX.
    const startCPRArrow = [arrowX, lowY, 0];
    const endCPRArrow = [arrowX, highY, 0];

    // Transform to world space.
    const worldStart = vec3.transformMat4([], startCPRArrow, actorMatrix);
    const worldEnd = vec3.transformMat4([], endCPRArrow, actorMatrix);

    // Compute arrow direction and length.
    const arrowDir = vec3.sub([], worldEnd, worldStart);
    const arrowLength = vec3.len(arrowDir);

    if (!Number.isFinite(arrowLength) || arrowLength < 1e-5) {
        return false;
    }

    vec3.normalize(arrowDir, arrowDir);

    // Compute perpendicular direction for arrowheads.
    // In CPR space, the perpendicular is along X.
    const perpCPR = [1, 0, 0];
    const worldZero = vec3.transformMat4([], [0, 0, 0], actorMatrix);
    const worldPerpUnit = vec3.transformMat4([], perpCPR, actorMatrix);
    vec3.sub(worldPerpUnit, worldPerpUnit, worldZero);
    vec3.normalize(worldPerpUnit, worldPerpUnit);

    // Compute arrowhead dimensions.
    let headLength = renderer
        ? getWorldLengthForPixelDelta(renderer, worldStart, arrowDir, STRETCHED_ARROW_HEAD_LENGTH_PX)
        : STRETCHED_ARROW_MIN_HEAD_LENGTH;
    headLength = clamp(headLength || STRETCHED_ARROW_MIN_HEAD_LENGTH, STRETCHED_ARROW_MIN_HEAD_LENGTH, arrowLength * 0.4);

    let headWidth = renderer
        ? getWorldLengthForPixelDelta(renderer, worldStart, worldPerpUnit, STRETCHED_ARROW_HEAD_WIDTH_PX)
        : STRETCHED_ARROW_MIN_HEAD_WIDTH;
    headWidth = clamp(headWidth || STRETCHED_ARROW_MIN_HEAD_WIDTH, STRETCHED_ARROW_MIN_HEAD_WIDTH, headLength * 2);

    // Build arrow geometry.
    const startInset = vec3.scaleAndAdd([], worldStart, arrowDir, headLength);
    const startLeft = vec3.scaleAndAdd([], startInset, worldPerpUnit, headWidth);
    const startRight = vec3.scaleAndAdd([], startInset, worldPerpUnit, -headWidth);

    const endInset = vec3.scaleAndAdd([], worldEnd, arrowDir, -headLength);
    const endLeft = vec3.scaleAndAdd([], endInset, worldPerpUnit, headWidth);
    const endRight = vec3.scaleAndAdd([], endInset, worldPerpUnit, -headWidth);

    // Update polydata points.
    const points = arrowDescriptor.polyData.getPoints();
    if (!points) {
        return false;
    }

    if (points.getNumberOfPoints() < 6) {
        points.setNumberOfPoints(6);
    }

    points.setPoint(0, worldStart[0], worldStart[1], worldStart[2]);
    points.setPoint(1, worldEnd[0], worldEnd[1], worldEnd[2]);
    points.setPoint(2, endLeft[0], endLeft[1], endLeft[2]);
    points.setPoint(3, endRight[0], endRight[1], endRight[2]);
    points.setPoint(4, startLeft[0], startLeft[1], startLeft[2]);
    points.setPoint(5, startRight[0], startRight[1], startRight[2]);
    points.modified();

    arrowDescriptor.polyData.modified();

    return true;
}

/**
 * Refresh stretched CPR length measurement arrow.
 * @param {Object} ctx - Visualization context.
 * @param {Union|null|undefined} maybeMeasurement - Active measurement.
 * @param {Union|null|undefined} selectedBranch - Selected branch.
 * @param {CPRFrame} currentCPRFrame - Current CPR frame data.
 */
function refreshStretchedLengthMeasurementArrow(ctx, maybeMeasurement, selectedBranch, currentCPRFrame) {
    if (!ctx || !ctx.cprObjects?.mapper || !ctx.renderer) {
        return;
    }

    const { cprObjects, renderer, renderWindow } = ctx;
    const { mapper, actor, wallCenterline } = cprObjects;

    const offsets = getStretchedLengthMeasurementOffsets(maybeMeasurement);

    if (!offsets) {
        if (cprObjects.stretchedMeasurementArrow) {
            disposeStretchedMeasurementArrow(renderer, cprObjects.stretchedMeasurementArrow);
            cprObjects.stretchedMeasurementArrow = null;
            renderWindow?.render();
        }
        return;
    }

    // Get branch selection for index conversion.
    const branchSelection = getBranchSelectionForConversion(selectedBranch);
    if (!branchSelection || !wallCenterline) {
        if (cprObjects.stretchedMeasurementArrow) {
            disposeStretchedMeasurementArrow(renderer, cprObjects.stretchedMeasurementArrow);
            cprObjects.stretchedMeasurementArrow = null;
            renderWindow?.render();
        }
        return;
    }

    const centerlineFieldData = wallCenterline.getFieldData();

    // Convert unified indices to centerline indices.
    const startMasterIndex = convertUnifiedBranchIndexToMasterIndex(
        offsets.startUnifiedIndex,
        branchSelection,
        centerlineFieldData
    );
    const endMasterIndex = convertUnifiedBranchIndexToMasterIndex(
        offsets.endUnifiedIndex,
        branchSelection,
        centerlineFieldData
    );

    const branchName = getDUName(selectedBranch);
    const branchIndices = cprObjects.branchOriginalIndices?.[branchName];

    let startCenterlineIndex = -1;
    let endCenterlineIndex = -1;

    if (branchIndices) {
        startCenterlineIndex = branchIndices.indexOf(startMasterIndex);
        endCenterlineIndex = branchIndices.indexOf(endMasterIndex);
    }

    if (startCenterlineIndex < 0 || endCenterlineIndex < 0) {
        if (cprObjects.stretchedMeasurementArrow) {
            disposeStretchedMeasurementArrow(renderer, cprObjects.stretchedMeasurementArrow);
            cprObjects.stretchedMeasurementArrow = null;
            renderWindow?.render();
        }
        return;
    }

    // Get oriented centerline and CPR params.
    const orientedCenterline = mapper.getOrientedCenterline();
    if (!orientedCenterline) {
        return;
    }

    const actorMatrix = actor.getUserMatrix();
    if (!actorMatrix) {
        return;
    }

    const distancesToFirstPoint = orientedCenterline.getDistancesToFirstPoint();
    const orientations = orientedCenterline.getOrientations() || null;

    const params = {
        width: currentCPRFrame.width,
        height: currentCPRFrame.height,
        tangentDirection: mapper.getTangentDirection(),
        centerPoint: mapper.getCenterPoint(),
        distancesToFirstPoint,
        orientations,
    };

    // Create arrow if it doesn't exist.
    let arrowDescriptor = cprObjects.stretchedMeasurementArrow;
    let addedArrow = false;

    if (!arrowDescriptor) {
        arrowDescriptor = createStretchedMeasurementArrow();
        renderer.addActor(arrowDescriptor.actor);
        cprObjects.stretchedMeasurementArrow = arrowDescriptor;
        addedArrow = true;
    }

    arrowDescriptor.startUnifiedIndex = offsets.startUnifiedIndex;
    arrowDescriptor.endUnifiedIndex = offsets.endUnifiedIndex;

    const updated = updateStretchedMeasurementArrow(
        arrowDescriptor,
        startCenterlineIndex,
        endCenterlineIndex,
        orientedCenterline,
        params,
        actorMatrix,
        currentCPRFrame,
        renderer,
        renderWindow,
    );

    if ((addedArrow || updated) && renderWindow) {
        renderWindow.render();
    }
}

/**
 * Initialize Konva elements for stretched CPR measurement display.
 * @param {HTMLElement} container - Container element for the Konva stage.
 * @param {string} viewportId - Unique identifier for the viewport.
 * @returns {StretchedMeasurementKonvaElements|null}
 */
function initializeStretchedMeasurementKonva(container, viewportId) {
    if (!container) {
        return null;
    }

    // Create a div container for Konva that overlays the VTK canvas.
    const konvaContainer = document.createElement('div');
    konvaContainer.id = `stretchedMeasurementKonvaContainer-${viewportId}`;
    konvaContainer.style.position = 'absolute';
    konvaContainer.style.top = '0';
    konvaContainer.style.left = '0';
    konvaContainer.style.width = '100%';
    konvaContainer.style.height = '100%';
    konvaContainer.style.pointerEvents = 'none';
    konvaContainer.style.zIndex = '1';
    container.appendChild(konvaContainer);

    const stage = new Konva.Stage({
        container: konvaContainer,
        width: container.offsetWidth,
        height: container.offsetHeight,
    });

    const layer = new Konva.Layer();
    stage.add(layer);

    // Create the measurement value label.
    const label = new Konva.Label({
        visible: false,
    });

    const tag = new Konva.Tag({
        fill: 'yellow',
        cornerRadius: 3,
        pointerDirection: 'none',
    });

    const text = new Konva.Text({
        text: '',
        fontSize: 24,
        fontFamily: 'Open Sans',
        fonstStyle: 'Bold',
        fill: 'black',
        padding: 5,
    });

    label.add(tag);
    label.add(text);
    layer.add(label);

    return { stage, layer, label, konvaContainer };
}

/**
 * Update Konva elements for stretched CPR measurement display.
 * @param {Object} ctx - Visualization context.
 * @param {Union|null|undefined} maybeMeasurement - Active measurement.
 * @param {Union|null|undefined} selectedBranch - Selected branch.
 * @param {CPRFrame} currentCPRFrame - Current CPR frame data.
 */
function updateStretchedMeasurementKonva(ctx, maybeMeasurement, selectedBranch, currentCPRFrame) {
    if (!ctx || !ctx.stretchedKonvaElements || !ctx.cprObjects?.mapper || !ctx.renderer || !ctx.renderWindow) {
        return;
    }

    const { stage, layer, label } = ctx.stretchedKonvaElements;
    const { cprObjects, renderer, renderWindow } = ctx;
    const { mapper, actor, wallCenterline } = cprObjects;

    if (!stage || !layer || !label) {
        return;
    }

    function hideElements() {
        label.visible(false);
        layer.batchDraw();
    }

    // Only show for length measurements.
    if (isNullOrUndefined(maybeMeasurement) || maybeMeasurement.name !== 'Length') {
        hideElements();
        return;
    }

    const offsets = getStretchedLengthMeasurementOffsets(maybeMeasurement);
    if (!offsets || !Number.isFinite(offsets.value)) {
        hideElements();
        return;
    }

    // Get branch selection for index conversion.
    const branchSelection = getBranchSelectionForConversion(selectedBranch);
    if (!branchSelection || !wallCenterline) {
        hideElements();
        return;
    }

    const centerlineFieldData = wallCenterline.getFieldData();

    // Convert unified indices to centerline indices.
    const startMasterIndex = convertUnifiedBranchIndexToMasterIndex(
        offsets.startUnifiedIndex,
        branchSelection,
        centerlineFieldData
    );
    const endMasterIndex = convertUnifiedBranchIndexToMasterIndex(
        offsets.endUnifiedIndex,
        branchSelection,
        centerlineFieldData
    );

    const branchName = getDUName(selectedBranch);
    const branchIndices = cprObjects.branchOriginalIndices?.[branchName];

    let startCenterlineIndex = -1;
    let endCenterlineIndex = -1;

    if (branchIndices) {
        startCenterlineIndex = branchIndices.indexOf(startMasterIndex);
        endCenterlineIndex = branchIndices.indexOf(endMasterIndex);
    }

    if (startCenterlineIndex < 0 || endCenterlineIndex < 0) {
        hideElements();
        return;
    }

    // Get oriented centerline and CPR params.
    const orientedCenterline = mapper.getOrientedCenterline();
    if (!orientedCenterline) {
        hideElements();
        return;
    }

    const actorMatrix = actor.getUserMatrix();
    if (!actorMatrix) {
        hideElements();
        return;
    }

    const distancesToFirstPoint = orientedCenterline.getDistancesToFirstPoint();
    const orientations = orientedCenterline.getOrientations() || null;

    const params = {
        width: currentCPRFrame.width,
        height: currentCPRFrame.height,
        tangentDirection: mapper.getTangentDirection(),
        centerPoint: mapper.getCenterPoint(),
        distancesToFirstPoint,
        orientations,
    };

    // Get CPR coordinates for both endpoints.
    const startCPR = computeCPRCoordsForCenterlineIndex(
        startCenterlineIndex,
        orientedCenterline,
        params
    );

    const endCPR = computeCPRCoordsForCenterlineIndex(
        endCenterlineIndex,
        orientedCenterline,
        params
    );

    if (!startCPR || !endCPR) {
        hideElements();
        return;
    }

    // Calculate arrow X position based on visible viewport width, not CPR image width.
    // Use the camera's parallel scale and aspect ratio to determine the visible world width.
    let arrowX = params.width * STRETCHED_ARROW_POSITION_RATIO; // Fallback to CPR width.

    const view = renderWindow.getViews()?.[0];
    const canvas = view?.getCanvas?.();
    if (canvas?.width > 0 && canvas?.height > 0) {
        const camera = renderer.getActiveCamera();
        const parallelScale = camera.getParallelScale();
        const aspect = canvas.width / canvas.height;
        const visibleWorldWidth = 2 * parallelScale * aspect;

        // The CPR image is centered in the viewport.
        // Calculate the arrow position as a percentage of the visible viewport width.
        const centerX = params.width / 2;
        const offsetFromCenter = (STRETCHED_ARROW_POSITION_RATIO - 0.5) * visibleWorldWidth;
        arrowX = centerX + offsetFromCenter;
    }

    const arrowCPRTop = [arrowX, Math.min(startCPR[1], endCPR[1]), 0];
    const arrowCPRBottom = [arrowX, Math.max(startCPR[1], endCPR[1]), 0];

    // Transform to world space.
    const worldArrowTop = vec3.transformMat4([], arrowCPRTop, actorMatrix);
    const worldArrowBottom = vec3.transformMat4([], arrowCPRBottom, actorMatrix);

    // Convert world coordinates to display coordinates.
    if (!canvas || !canvas.height || !canvas.width) {
        hideElements();
        return;
    }

    const coord = vtkCoordinate.newInstance();
    coord.setCoordinateSystemToWorld();

    const dpr = window.devicePixelRatio || 1;

    function worldToKonva(worldPoint) {
        coord.setValue(worldPoint[0], worldPoint[1], worldPoint[2]);
        const displayCoords = coord.getComputedDoubleDisplayValue(renderer);
        if (!displayCoords || displayCoords.length < 2) {
            return null;
        }
        return {
            x: displayCoords[0] / dpr,
            y: (canvas.height - displayCoords[1]) / dpr,
        };
    }

    const konvaArrowTop = worldToKonva(worldArrowTop);
    const konvaArrowBottom = worldToKonva(worldArrowBottom);

    if (!konvaArrowTop || !konvaArrowBottom) {
        hideElements();
        return;
    }

    // Update label with measurement value.
    const formattedValue = offsets.value.toFixed(1);
    label.getText().text(formattedValue);

    // Position label at midpoint of arrow, offset to the right.
    const labelX = (konvaArrowTop.x + konvaArrowBottom.x) / 2 + 20;
    const labelY = (konvaArrowTop.y + konvaArrowBottom.y) / 2 - label.height() / 2;

    if (!Number.isFinite(labelX) || !Number.isFinite(labelY)) {
        hideElements();
        return;
    }

    label.x(labelX);
    label.y(labelY);
    label.visible(true);

    layer.batchDraw();
}

/**
 * Resize the stretched measurement Konva stage.
 * @param {StretchedMeasurementKonvaElements|null|undefined} konvaElements - Konva elements.
 * @param {number} width - New width.
 * @param {number} height - New height.
 */
function resizeStretchedMeasurementKonva(konvaElements, width, height) {
    if (!konvaElements || !konvaElements.stage) {
        return;
    }

    konvaElements.stage.width(width);
    konvaElements.stage.height(height);
    konvaElements.layer?.batchDraw();
}

/**
 * Dispose of stretched measurement Konva elements.
 * @param {StretchedMeasurementKonvaElements|null|undefined} konvaElements - Konva elements to dispose.
 */
function disposeStretchedMeasurementKonva(konvaElements) {
    if (!konvaElements) {
        return;
    }

    konvaElements.stage?.destroy();
    konvaElements.konvaContainer?.remove();
}

// ============================================================================
// End of Stretched CPR Measurement Indicators
// ============================================================================

/**
 * Build a vtkPolyData polyline from an array of point indices.
 * @param {number[]} indices - Array of point indices for the polyline.
 * @param {Float32Array} points - Array of point positions from the source VTP (length = 3 * number of points in VTP).
 * @param {Float32Array|null} centerlineTangents - Optional array of tangents from the source VTP (length = 3 * number of points in VTP).
 * @param {number} lastTrunkMasterIndex - Last index of the trunk branch.
 * @returns {vtkPolyData}
 */
function buildPolyline(
    indices,
    points,
    centerlineTangents,
    lastTrunkMasterIndex,
) {
    const n = indices.length;

    /**
     * Build compacted positions and tangents arrays for the requested indices.
     * @returns {{positions: Float32Array, tangents: Float32Array|null}}
     */
    function createPositionsAndTangents() {
        const positions = new Float32Array(n * 3);
        const outTangents = centerlineTangents ? new Float32Array(n * 3) : null;

        const tmpVec = vec3.create();
        for (let i = 0; i < n; i++) {
            const pointIndex = indices[i];
            // Copy position.
            positions.set(points.subarray(3 * pointIndex, 3 * pointIndex + 3), 3 * i);

            // If tangents exist in the source VTP, read them by original point index.
            if (outTangents) {
                tmpVec[0] = centerlineTangents[3 * pointIndex] || 0;
                tmpVec[1] = centerlineTangents[3 * pointIndex + 1] || 0;
                tmpVec[2] = centerlineTangents[3 * pointIndex + 2] || 0;
                vec3.normalize(tmpVec, tmpVec);
                outTangents.set(tmpVec, 3 * i);
            }
        }

        return { positions, tangents: outTangents };
    }

    /**
     * Write a 4x4 matrix built from tangent/bitangent/normal/position [T, B, N, P] into the
     * packed orientations buffer at slot `k`.
     * @param {Float32Array} orientations - Buffer to write into (length >= 16*(k+1))
     * @param {number} k - Matrix index to write
     * @param {import('gl-matrix').vec3} T - Tangent vector
     * @param {import('gl-matrix').vec3} B - Bitangent vector
     * @param {import('gl-matrix').vec3} N - Normal vector
     * @param {Float32Array|Array<number>} p - Position [x,y,z]
     * @returns {void}
     */
    function emitMatrix(orientations, k, T, B, N, p) {
        const m = mat4.create();
        m[0] = T[0]; m[1] = T[1]; m[2] = T[2];
        m[4] = B[0]; m[5] = B[1]; m[6] = B[2];
        m[8] = N[0]; m[9] = N[1]; m[10] = N[2];
        m[12] = p[0]; m[13] = p[1]; m[14] = p[2]; m[15] = 1;
        orientations.set(m, k * 16);
    }

    /**
     * Parallel-transport the frame's bitangent B from orientation with Tprev to orientation with T, minimizing twist.
     * This mutates B in-place.
     * @param {import('gl-matrix').vec3} Tprev - Previous tangent
     * @param {import('gl-matrix').vec3} T - Current tangent
     * @param {import('gl-matrix').vec3} B - Bitangent to rotate (mutated)
     * @returns {void}
     */
    function transportFrame(Tprev, T, B) {
        const axis = vec3.cross([], Tprev, T);
        const s = vec3.len(axis);
        let c = vec3.dot(Tprev, T);
        c = clamp(c, -1, 1);

        if (s > 1e-8) {
            const k = vec3.scale([], axis, 1 / s);
            const theta = Math.atan2(s, c);

            // Rodrigues rotation of B around k by theta.
            const kxB = vec3.cross([], k, B);
            const kv = vec3.dot(k, B);
            const term1 = vec3.scale([], B, Math.cos(theta));
            const term2 = vec3.scale([], kxB, Math.sin(theta));
            const term3 = vec3.scale([], k, kv * (1 - Math.cos(theta)));
            vec3.add(B, term1, term2);
            vec3.add(B, B, term3);
        }

        // Re-orthonormalize B against T.
        const proj = vec3.scale([], T, vec3.dot(B, T));
        vec3.sub(B, B, proj);

        if (vec3.len(B) < 1e-8) {
            // Fallback if degenerate.
            vec3.set(B, 0, 1, 0);
            if (Math.abs(vec3.dot(B, T)) > 0.9) vec3.set(B, 0, 0, 1);
            const proj2 = vec3.scale([], T, vec3.dot(B, T));
            vec3.sub(B, B, proj2);
        }

        vec3.normalize(B, B);
    }

    const { positions, tangents } = createPositionsAndTangents();

    // Build rotation-minimizing frames (parallel transport).
    const orientations = new Float32Array(n * 16);
    const T = vec3.create();
    const Tprev = vec3.create();
    const B = vec3.create(); // Sampling direction across width.
    const N = vec3.create(); // Completes the basis: N = T x B.

    // Initialize T0 either from tangents or geometry.
    {
        const p0 = positions.subarray(0, 3);
        const p1 = positions.subarray(3, 6);
        if (tangents) {
            vec3.set(T, tangents[0], tangents[1], tangents[2]);
        } else {
            vec3.subtract(T, p1, p0);
        }
        if (vec3.len(T) === 0) vec3.set(T, 1, 0, 0);
        vec3.normalize(T, T);
        vec3.copy(Tprev, T);
    }

    // Choose initial B0 perpendicular to T0.
    vec3.set(B, 1, 0, 0);
    if (Math.abs(vec3.dot(B, T)) > 0.9) vec3.set(B, 0, 1, 0);
    const tmp = vec3.scale([], T, vec3.dot(B, T));
    vec3.sub(B, B, tmp);
    if (vec3.len(B) === 0) vec3.set(B, 0, 0, 1);
    vec3.normalize(B, B);
    vec3.cross(N, T, B);
    vec3.normalize(N, N);

    // Emit first matrix.
    emitMatrix(orientations, 0, T, B, N, positions.subarray(0, 3));

    // March along the polyline with minimal twist.
    for (let i = 1; i < n; ++i) {
        const p = positions.subarray(3 * i, 3 * i + 3);
        // Next tangent.
        if (tangents) {
            vec3.set(T, tangents[3 * i], tangents[3 * i + 1], tangents[3 * i + 2]);
        } else {
            const pPrev = positions.subarray(3 * (i - 1), 3 * (i - 1) + 3);
            vec3.subtract(T, p, pPrev);
        }
        if (vec3.len(T) === 0) vec3.copy(T, Tprev);
        vec3.normalize(T, T);

        // Flip T to maintain continuity.
        if (vec3.dot(T, Tprev) < 0) vec3.scale(T, T, -1);

        transportFrame(Tprev, T, B);

        // N = T x B.
        vec3.cross(N, T, B);
        vec3.normalize(N, N);

        emitMatrix(orientations, i, T, B, N, p);
        vec3.copy(Tprev, T);
    }

    const out = vtkPolyData.newInstance();
    out.getPoints().setData(positions, 3);
    const lines = new Uint32Array(1 + n);
    lines[0] = n;
    for (let i = 0; i < n; i++) lines[1 + i] = i;
    out.getLines().setData(lines);

    // Abuse the tensors slot to store the mat4 orientations.
    out.getPointData().setTensors(
        vtkDataArray.newInstance({
            name: 'Orientation',
            numberOfComponents: 16,
            values: orientations,
        })
    );

    // Store lastTrunkMasterIndex in the polydata metadata (FieldData)
    out.getFieldData().addArray(
        vtkDataArray.newInstance({
            name: 'LastTrunkMasterIndex',
            numberOfComponents: 1,
            values: new Float32Array([lastTrunkMasterIndex]),
        })
    );

    return out;
}

/**
 * Build centerline polylines for Left, Right and Cusp branches from the wall centerline VTP.
 * @param {vtkPolyData} wallCenterline
 * @returns {{ centerlinesPolyData: {Left: vtkPolyData, Right: vtkPolyData, Cusp: vtkPolyData}, branchOriginalIndices: {Left: number[], Right: number[], Cusp: number[]}}}
 */
function buildCenterlinesPolyData(wallCenterline) {
    log('buildCenterlinesPolyData - wallCenterline:', wallCenterline);

    function getFieldData(name) {
        const array = wallCenterline.getFieldData().getArrayByName(name);
        return array ? Array.from(array.getData()) : null;
    }

    const distanceBranchTrunk = getFieldData('Distance_Branch_Trunk');
    const distanceBranchLeft = getFieldData('Distance_Branch_Left');
    const distanceBranchRight = getFieldData('Distance_Branch_Right');
    const distanceBranchCusp = getFieldData('Distance_Branch_Trunk_Cusp');

    const leftIndices = [...distanceBranchTrunk, ...distanceBranchLeft];
    const rightIndices = [...distanceBranchTrunk, ...distanceBranchRight];
    const cuspIndices = [...distanceBranchCusp];

    // Remember, for each branch, which original polyData point indices we used
    const branchOriginalIndices = {
        Left: leftIndices,
        Right: rightIndices,
        Cusp: cuspIndices
    };

    // This array contains the plane normals for each cross-section, which in this dataset
    // are the centerline tangents (along-path direction).
    const normalsPointData = wallCenterline.getPointData().getArrayByName('Normals');

    const pts = wallCenterline.getPoints().getData();

    // Use the actual Normals point data if available. Normals point toward the inlet, so we invert them to get tangents along the flow direction.
    const tangentsFromVTP = normalsPointData ? normalsPointData.getData().map((x) => -x) : null;

    const lastTrunkMasterIndex = distanceBranchTrunk[distanceBranchTrunk.length - 1];

    const centerlinesPolyData = {
        [CenterlineDistanceBranchSelection.LEFT]: buildPolyline(leftIndices, pts, tangentsFromVTP, lastTrunkMasterIndex),
        [CenterlineDistanceBranchSelection.RIGHT]: buildPolyline(rightIndices, pts, tangentsFromVTP, lastTrunkMasterIndex),
        [CenterlineDistanceBranchSelection.CUSP]: buildPolyline(cuspIndices, pts, tangentsFromVTP, lastTrunkMasterIndex)
    };

    return {
        centerlinesPolyData,
        branchOriginalIndices,
    };
}

/**
 * Spline world control point storage.
 * @typedef {Object} SplineWorldControlPointData
 * @property {Array.<[number, number, number]>} worldControlPoints
 *   Array of sampled control points in world coordinates. Each entry is a 3-tuple
 *   [x, y, z].
 * @property {number[]} worldControlPointIndices
 *   Array of master indices on the centerline polydata corresponding to each control point.
 * @property {number} [lastTrunkMasterIndex]
 *   Master index of the last point on the trunk portion of the centerline (unrelated to control points).
 */

/**
 * Branch control data storage for CPR visualization.
 * @typedef {Object} BranchControlData
 * @property {SplineWorldControlPointData} splineWorldControlPointData
 *   Sampled control points and their indices from the centerline polydata.
 * @property {number[]} controlRadialOffsetR
 *   Array of radial offsets for each control point.
 * @property {number[]} controlOffsetYCPR
 *   Array of Y offsets for each control point in CPR space.
 */

/**
 * @typedef {Object} CommittedChanges
 * @property {BranchControlData} branchControlData - The committed control point data for this branch.
 */

/**
 * Build the sampled original control points from a vtkPolyData centerline.
 * We sample approx every 20th point, always keeping the first and last.
 * @param {vtkPolyData} polyData - Centerline polydata.
 * @returns {BranchControlData} Branch control data containing sampled control points and initialized offset arrays.
 */
function buildOriginalControlPointsFromPolyData(
    polyData,
) {
    if (!polyData) {
        throw new Error('buildOriginalControlPointsFromPolyData - polyData is null or undefined');
    }

    const pts = polyData.getPoints().getData();
    const n = polyData.getPoints().getNumberOfPoints();
    const lastTrunkMasterIndex = polyData.getFieldData().getArrayByName('LastTrunkMasterIndex').getData()[0];

    if (n <= 0) {
        throw new Error('buildOriginalControlPointsFromPolyData - polyData has no points');
    }

    const indices = [];
    const step = 20; // every 20th point

    for (let i = 0; i < n; i += step) {
        indices.push(i);
    }

    if (indices.length === 0 || indices[indices.length - 1] !== n - 1) {
        indices.push(n - 1);
    }

    const worldControlPoints = indices.map((idx) => [
        pts[3 * idx],
        pts[3 * idx + 1],
        pts[3 * idx + 2],
    ]);

    const worldControlPointIndices = indices.slice();

    return {
        splineWorldControlPointData: {
            worldControlPoints,
            worldControlPointIndices,
            lastTrunkMasterIndex: lastTrunkMasterIndex
        },
        controlRadialOffsetR: new Array(worldControlPointIndices.length).fill(null),
        controlOffsetYCPR: new Array(worldControlPointIndices.length).fill(null),
    };
}

/**
 * If the offset arrays do not exist or are not the correct length, initialize them to zeroes.
 * @param {BranchControlData} branchControlData - Branch control data containing offset arrays.
 * @param {number} targetLength - Target length for the arrays.
 */
function ensureOffsetArraysExist(
    branchControlData,
    targetLength) {
    if (!branchControlData.controlRadialOffsetR || branchControlData.controlRadialOffsetR.length !== targetLength) {
        branchControlData.controlRadialOffsetR = new Array(targetLength).fill(0.0);
    }
    if (!branchControlData.controlOffsetYCPR || branchControlData.controlOffsetYCPR.length !== targetLength) {
        branchControlData.controlOffsetYCPR = new Array(targetLength).fill(0.0);
    }
}

/**
 * @typedef {Object} CPRParams
 * @property {number} width - CPR frame width.
 * @property {number} height - CPR frame height.
 * @property {import('gl-matrix').vec3} tangentDirection - Tangent direction vector.
 * @property {import('gl-matrix').vec3} centerPoint - Center point of the CPR frame.
 * @property {number[]} distancesToFirstPoint - Distances along centerline to first point.
 * @property {import('gl-matrix').quat|null} orientations - Optional packed mat4 orientations from the centerline.
 */

/** @typedef {Object} CPRFrame
 * @property {import('gl-matrix').vec3} worldTangent
 * @property {import('gl-matrix').vec3} worldNormal
 * @property {import('gl-matrix').vec3} worldBitangent
 * @property {import('gl-matrix').vec3} worldWidgetCenter
 * @property {number} distance
 * @property {number} height
 * @property {number} width
 * @property {import('gl-matrix').vec3} worldActorTranslation
 */

/**
 * Build CPR parameters object from the mapper and current CPR frame.
 * @param {vtkImageCPRMapper} mapper - CPR image mapper.
 * @param {vtkPolyLine} orientedCenterline - Oriented centerline from the mapper.
 * @param {CPRFrame} currentCPRFrame - Current CPR frame data.
 * @returns {CPRParams}
 */
function buildCPRParams(mapper, orientedCenterline, currentCPRFrame) {
    const { width, height } = currentCPRFrame;
    const tangentDirection = mapper.getTangentDirection();
    const centerPoint = mapper.getCenterPoint();
    const distancesToFirstPoint = orientedCenterline.getDistancesToFirstPoint();
    const orientations = orientedCenterline.getOrientations() || null;

    return {
        width,
        height,
        tangentDirection,
        centerPoint,
        distancesToFirstPoint,
        orientations,
    };
}

/**
 * @typedef {Object} CPRUpdateContext
 * @property {vtkRenderWindow} renderWindow
 * @property {vtkRenderer} renderer
 * @property {vtkRenderWindowInteractor} interactor
 * @property {vtkImageCPRMapper} mapper
 * @property {vtkImageSlice} actor
 * @property {vtkResliceCursorWidget} resliceCursorWidget
 * @property {Object} resliceCursorWidgetState
 * @property {vtkCPRManipulator} cprManipulator
 * @property {vtkPlaneManipulator} planeManipulator
 * @property {vtkSplineWidget} splineWidget
 * @property {Object} cprObjects - Reference to cprObjects for state tracking.
 */

/**
 * Create a CPR update context object from the main context.
 * @param {React.RefObject<Object>} context
 * @return {CPRUpdateContext}
 */
function createCPRUpdateContext(context) {
    return {
        renderWindow: context.renderWindow,
        renderer: context.renderer,
        interactor: context.interactor,
        mapper: context.cprObjects.mapper,
        actor: context.cprObjects.actor,
        resliceCursorWidget: context.cprObjects.resliceCursorWidget,
        resliceCursorWidgetState: context.cprObjects.resliceCursorWidgetState,
        cprManipulator: context.cprObjects.cprManipulator,
        planeManipulator: context.cprObjects.planeManipulator,
        splineWidget: context.cprObjects.splineWidget,
        cprObjects: context.cprObjects
    };
}

/**
 * Updates the control point offsets based on the current positions of the spline widget handles.
 * @param {BranchControlData} branchControlData - Branch control data containing spline world control points and offset arrays.
 * @param {React.RefObject<CPRFrame>} currentCPRFrameRef - Ref to current CPR frame data.
 * @param {vtkImageCPRMapper} mapper - CPR image mapper.
 * @param {vtkImageSlice} actor - Image slice actor.
 * @param {vtkSplineWidget} splineWidget - Spline widget.
 */
function updateOffsetsFromHandles(
    branchControlData,
    currentCPRFrameRef,
    mapper,
    actor,
    splineWidget,
) {
    const { worldControlPointIndices } = branchControlData.splineWorldControlPointData;

    if (
        !worldControlPointIndices ||
        !worldControlPointIndices.length
    ) {
        return;
    }

    if (!currentCPRFrameRef.current) {
        return;
    }

    const orientedCenterline = mapper.getOrientedCenterline();

    if (!orientedCenterline) {
        return;
    }

    const distancesToFirstPoint = orientedCenterline.getDistancesToFirstPoint();
    const pts = orientedCenterline.getPoints();
    const nPts = pts.getNumberOfPoints();
    if (!distancesToFirstPoint || !pts || !nPts) {
        return;
    }

    const widgetState = splineWidget.getWidgetState();
    if (!widgetState) {
        return;
    }

    const handles = widgetState.getHandleList();
    if (!handles || !handles.length) {
        return;
    }

    // Ensure arrays exist / aligned
    ensureOffsetArraysExist(
        branchControlData,
        worldControlPointIndices.length,
    );

    const actorMatrix = actor.getUserMatrix();
    if (!actorMatrix) {
        return;
    }

    const invActorMatrix = mat4.create();
    if (!mat4.invert(invActorMatrix, actorMatrix)) {
        return;
    }

    const params = buildCPRParams(mapper, orientedCenterline, currentCPRFrameRef.current);
    log('updateOffsetsFromHandles - params:', params);

    const worldPos = [0, 0, 0];
    const cprPos = [0, 0, 0];

    const handleList = handles;
    for (let k = 0; k < handleList.length; ++k) {
        const handle = handleList[k];
        const clIndex = worldControlPointIndices[k];

        if (
            clIndex == null ||
            clIndex < 0 ||
            clIndex >= nPts ||
            distancesToFirstPoint[clIndex] === undefined
        ) {
            continue;
        }

        const baseCPR = computeCPRCoordsForCenterlineIndex(
            clIndex,
            orientedCenterline,
            params
        );
        if (!baseCPR) {
            continue;
        }
        log(`updateOffsetsFromHandles - baseCPR[${k}]:`, baseCPR);

        const origin = handle.getOrigin();
        log(`updateOffsetsFromHandles - handle origin[${k}]:`, origin);
        worldPos[0] = origin[0];
        worldPos[1] = origin[1];
        worldPos[2] = origin[2];

        vec3.transformMat4(cprPos, worldPos, invActorMatrix);
        log(`updateOffsetsFromHandles - cprPos[${k}]:`, cprPos);

        let dx = cprPos[0] - baseCPR[0];
        let dy = cprPos[1] - baseCPR[1];

        // Round to 0 if below threshold.
        if (Math.abs(dx) < 0.00001) {
            dx = 0;
        }
        if (Math.abs(dy) < 0.00001) {
            dy = 0;
        }

        branchControlData.controlRadialOffsetR[k] = Number.isFinite(dx) ? dx : 0.0;
        branchControlData.controlOffsetYCPR[k] = Number.isFinite(dy) ? dy : 0.0;
    }
}



/**
 * Project original control points onto the current CPR plane and update the spline widget handles accordingly.
 * @param {BranchControlData} branchControlData - Branch control data containing spline world control points and offset arrays.
 * @param {Object} currentCPRFrameRef - Current CPR frame data.
 * @param {vtkImageCPRMapper} mapper - CPR image mapper.
 * @param {vtkSplineWidget} splineWidget - Spline widget to update.
 * @param {vtkRenderWindowInteractor} interactor - Render window interactor.
 * @param {vtkRenderWindow} renderWindow - Render window.
 * @param {vtkPlaneManipulator} planeManipulator - Plane manipulator for the spline handles.
 * @param {vtkImageSlice} actor - Image slice actor.
 */
function updateSplineProjection(
    branchControlData,
    currentCPRFrameRef,
    mapper,
    splineWidget,
    interactor,
    renderWindow,
    planeManipulator,
    actor,
) {
    const { worldControlPoints, worldControlPointIndices } = branchControlData.splineWorldControlPointData;

    if (!Array.isArray(worldControlPoints) || worldControlPoints.length === 0) {
        log('updateSplineProjection: No world control points to project.');
        return;
    }

    if (!worldControlPointIndices || worldControlPointIndices.length === 0 || currentCPRFrameRef.current.height === null) {
        log('updateSplineProjection: Missing control point indices or invalid CPR frame height.');
        return;
    }

    const { width, height } = currentCPRFrameRef.current;

    const orientedCenterline = mapper.getOrientedCenterline();

    const distancesToFirstPoint = orientedCenterline.getDistancesToFirstPoint();
    const pts = orientedCenterline.getPoints();
    const nPts = pts.getNumberOfPoints();
    const ptsArray = pts.getData();
    if (!distancesToFirstPoint || !ptsArray || !nPts) {
        return;
    }

    // const orientations = orientedCenterline.getOrientations();
    // const tangentDirection = mapper.getTangentDirection();
    // const centerPoint = mapper.getCenterPoint();

    const widgetState = splineWidget.getWidgetState();
    widgetState.clearHandleList();


    // Keep offset arrays aligned with indices
    if (
        !branchControlData.controlRadialOffsetR ||
        branchControlData.controlRadialOffsetR.length !== worldControlPointIndices.length
    ) {
        const old = branchControlData.controlRadialOffsetR || [];
        const arr = new Array(worldControlPointIndices.length).fill(0.0);
        for (let i = 0; i < Math.min(old.length, arr.length); i++) {
            arr[i] = old[i];
        }
        branchControlData.controlRadialOffsetR = arr;
    }

    if (
        !branchControlData.controlOffsetYCPR ||
        branchControlData.controlOffsetYCPR.length !== worldControlPointIndices.length
    ) {
        const old = branchControlData.controlOffsetYCPR || [];
        const arr = new Array(worldControlPointIndices.length).fill(0.0);
        for (let i = 0; i < Math.min(old.length, arr.length); i++) {
            arr[i] = old[i];
        }
        branchControlData.controlOffsetYCPR = arr;
    }

    const actorMatrix = actor.getUserMatrix();

    const params = buildCPRParams(mapper, orientedCenterline, currentCPRFrameRef.current);

    const cprPoint = [0, 0, 0];
    const worldCoord = [0, 0, 0];

    for (let i = 0; i < worldControlPointIndices.length; ++i) {
        const pointIndex = worldControlPointIndices[i];

        if (
            pointIndex < 0 ||
            pointIndex >= nPts ||
            distancesToFirstPoint[pointIndex] === undefined
        ) {
            continue;
        }

        const baseCPR = computeCPRCoordsForCenterlineIndex(
            pointIndex,
            orientedCenterline,
            params
        );
        if (!baseCPR) {
            continue;
        }
        log('updateSplineProjection - baseCPR:', baseCPR);

        let x = baseCPR[0];
        let y = baseCPR[1];
        let z = baseCPR[2] ?? 0.0;

        const dx = branchControlData.controlRadialOffsetR[i];
        const dy = branchControlData.controlOffsetYCPR[i];

        if (Number.isFinite(dx)) {
            x += dx;
        }
        if (Number.isFinite(dy)) {
            y += dy;
        }

        // Clamp into CPR frame
        x = clamp(x, 0, width);
        y = clamp(y, 0, height);

        cprPoint[0] = x;
        cprPoint[1] = y;
        cprPoint[2] = z;

        vec3.transformMat4(worldCoord, cprPoint, actorMatrix);

        const handle = widgetState.addHandle();
        handle.setOrigin(worldCoord[0], worldCoord[1], worldCoord[2]);
        handle.setManipulator(planeManipulator);
        handle.setVisible(true);
    }

    interactor.render();
    renderWindow.render();
}

const stretchViewType = ViewTypes.XZ_PLANE;

/**
 * Save the current camera state for a specific branch.
 * @param {vtkCamera} camera - The camera to save state from.
 * @param {Map<string, Object>} branchCameraStates - Map to store camera states per branch.
 * @param {string} branchName - The branch name to save state for.
 * @param {number[]} [defaultPosition] - The default (computed) camera position for reference.
 * @param {number[]} [defaultFocalPoint] - The default (computed) focal point for reference.
 */
function saveCameraStateForBranch(camera, branchCameraStates, branchName, defaultPosition, defaultFocalPoint) {
    if (!camera || !branchCameraStates || !branchName) {
        return;
    }

    const position = camera.getPosition();
    const focalPoint = camera.getFocalPoint();
    const viewUp = camera.getViewUp();
    const parallelScale = camera.getParallelScale();

    const existingState = branchCameraStates.get(branchName);

    let panOffset = [0, 0, 0];
    let lastDefaultPosition;
    let lastDefaultFocalPoint;

    if (defaultPosition && defaultFocalPoint) {
        // We're setting a new default (from updateDistanceAndDirection)
        // Calculate pan offset relative to this new default
        panOffset = [
            position[0] - defaultPosition[0],
            position[1] - defaultPosition[1],
            position[2] - defaultPosition[2]
        ];
        lastDefaultPosition = [...defaultPosition];
        lastDefaultFocalPoint = [...defaultFocalPoint];
    } else if (existingState && existingState.lastDefaultPosition && existingState.lastDefaultFocalPoint) {
        // User interaction - calculate offset from last known default
        panOffset = [
            position[0] - existingState.lastDefaultPosition[0],
            position[1] - existingState.lastDefaultPosition[1],
            position[2] - existingState.lastDefaultPosition[2]
        ];
        lastDefaultPosition = existingState.lastDefaultPosition;
        lastDefaultFocalPoint = existingState.lastDefaultFocalPoint;
    } else {
        // No default available, preserve existing offset
        panOffset = existingState?.panOffset || [0, 0, 0];
        lastDefaultPosition = existingState?.lastDefaultPosition;
        lastDefaultFocalPoint = existingState?.lastDefaultFocalPoint;
    }

    const state = {
        position: [...position],
        focalPoint: [...focalPoint],
        viewUp: [...viewUp],
        parallelScale,
        panOffset,
        lastDefaultPosition,
        lastDefaultFocalPoint
    };

    branchCameraStates.set(branchName, state);
}

/** Update CPR distance, direction, actor and camera based on the reslice cursor widget state.
 * @param {Object} context - Context object containing VTK components.
 * @param {vtkRenderWindow} context.renderWindow - The render window.
 * @param {vtkRenderer} context.renderer - The renderer.
 * @param {vtkRenderWindowInteractor} context.interactor - The render window interactor.
 * @param {vtkImageCPRMapper} context.mapper - The CPR image mapper.
 * @param {vtkImageSlice} context.actor - The image slice actor.
 * @param {vtkResliceCursorWidget} context.resliceCursorWidget - The reslice cursor widget.
 * @param {Object} context.resliceCursorWidgetState - The reslice cursor widget state.
 * @param {vtkCPRManipulator} context.cprManipulator - The CPR manipulator.
 * @param {vtkPlaneManipulator} context.planeManipulator - The plane manipulator for the spline handles.
 * @param {vtkSplineWidget} context.splineWidget - The spline widget.
 * @param {Object} context.cprObjects - Reference to cprObjects for state tracking.
 * @param {function(number): void} angleUpdatedCallback - Callback function for angle updates.
 * @param {BranchControlData} branchControlData - Branch control data containing spline world control points and offset arrays.
 * @param {React.RefObject.<Object>} currentCPRFrameRef - Ref to current CPR frame data.
 * @param {string} selectedBranchName - The currently selected branch name for camera state restoration.
 */
function updateDistanceAndDirection(
    {
        renderWindow,
        renderer,
        interactor,
        mapper,
        actor,
        resliceCursorWidget,
        resliceCursorWidgetState,
        cprManipulator,
        planeManipulator,
        splineWidget,
        cprObjects,
    },
    angleUpdatedCallback,
    branchControlData,
    currentCPRFrameRef,
    selectedBranchName,
) {

    log("updateDistanceAndDirection");
    // Directions and position in world space from the widget.
    const resliceCursorWidgetPlanes = resliceCursorWidgetState.getPlanes();

    /**
     * A unit vector normal to the CPR plane (out-of-plane). It is the plane normal / camera offset direction and is used
     * together with worldTangent and worldNormal to build the full orientation basis.
     */
    let worldBitangent = [...resliceCursorWidgetPlanes[stretchViewType].normal];

    /**
     * A unit vector in world space that represents the mapper's model Y axis (the in-plane "along-height /
     * along-distance" direction, often treated as the plane's "up"). It lies in the CPR plane and is used to move points
     *  toward/away along the CPR height.
     */
    let worldNormal = [...resliceCursorWidgetPlanes[stretchViewType].viewUp];

    // Gram-Schmidt: https://en.wikipedia.org/wiki/Gram-Schmidt_process
    // Make sure that the Bitangent and Normal are orthonormal to each other.
    vec3.normalize(worldNormal, worldNormal);
    const proj = vec3.dot(worldBitangent, worldNormal);
    vec3.scaleAndAdd(worldBitangent, worldBitangent, worldNormal, -proj);
    vec3.normalize(worldBitangent, worldBitangent);

    /**
     * A unit vector in world space that represents the mapper's model X axis (the in-plane "across-width" direction).
     * It lies in the CPR plane and is used to move points left-right across the CPR strip.
     */
    const worldTangent = vec3.cross([], worldBitangent, worldNormal);
    vec3.normalize(worldTangent, worldTangent);

    const worldWidgetCenter = resliceCursorWidgetState.getCenter();
    const distance = cprManipulator.getCurrentDistance();

    // CPR mapper tangent and bitangent directions update
    const { orientation } = mapper.getCenterlinePositionAndOrientation(distance);
    const modelDirections = mat3.fromQuat([], orientation);
    const inverseModelDirections = mat3.invert([], modelDirections);
    const worldDirections = mat3.fromValues(
        ...worldTangent,
        ...worldBitangent,
        ...worldNormal
    );
    const baseDirections = mat3.mul([], inverseModelDirections, worldDirections);
    log('updateDistanceAndDirection - baseDirections:', baseDirections);

    // The direction matrix is the matrix composed of tangent, bitangent and normal directions.
    // It is used to orient the camera or the actor.
    mapper.setDirectionMatrix(baseDirections);

    planeManipulator.setUserOrigin(worldWidgetCenter);
    planeManipulator.setUserNormal(worldNormal);

    // Ensure spline widget manipulator uses the same plane so handles move on the CPR plane.
    splineWidget.setManipulator(planeManipulator);

    updateState(
        resliceCursorWidgetState,
        resliceCursorWidget.getScaleInPixels(),
        resliceCursorWidget.getRotationHandlePosition()
    );

    const width = mapper.getWidth();
    const height = mapper.getHeight();

    const worldActorTranslation = vec3.scaleAndAdd(
        [],
        worldWidgetCenter,
        worldTangent,
        -0.5 * width
    );
    vec3.scaleAndAdd(
        worldActorTranslation,
        worldActorTranslation,
        worldNormal,
        distance - height
    );
    const worldActorTransform = mat4.fromValues(
        ...worldTangent, 0,
        ...worldNormal, 0,
        ...vec3.scale([], worldBitangent, -1), 0,
        ...worldActorTranslation, 1
    );
    actor.setUserMatrix(worldActorTransform);

    // CPR frame info for spline update
    currentCPRFrameRef.current = {
        worldTangent: [...worldTangent],
        worldNormal: [...worldNormal],
        worldBitangent: [...worldBitangent],
        worldWidgetCenter: [...worldWidgetCenter],
        distance,
        height,
        width,
        worldActorTranslation: [...worldActorTranslation],
    };

    const camera = renderer.getActiveCamera();

    let targetParallelScale;

    // Check if there's a saved state for this branch
    const savedState = (cprObjects?.branchCameraStates && selectedBranchName)
        ? cprObjects.branchCameraStates.get(selectedBranchName)
        : null;

    if (savedState) {
        targetParallelScale = savedState.parallelScale;
    } else {
        // No saved state - use default scale based on CPR height
        targetParallelScale = 0.5 * height;
    }

    // Ensure camera is in parallel projection mode
    if (cprObjects && !cprObjects.cameraScaleInitialized) {
        camera.setParallelProjection(true);
        cprObjects.cameraScaleInitialized = true;
    }

    const cameraDistance =
        targetParallelScale /
        Math.tan(radiansFromDegrees(0.5 * camera.getViewAngle()));

    const cameraFocalPoint = vec3.scaleAndAdd(
        [],
        worldWidgetCenter,
        worldNormal,
        distance - 0.5 * height
    );

    const cameraPosition = vec3.scaleAndAdd(
        [],
        cameraFocalPoint,
        worldBitangent,
        -cameraDistance
    );

    // Apply pan offset if saved state exists
    let finalPosition = [...cameraPosition];
    let finalFocalPoint = [...cameraFocalPoint];

    if (savedState && savedState.panOffset) {
        finalPosition[0] += savedState.panOffset[0];
        finalPosition[1] += savedState.panOffset[1];
        finalPosition[2] += savedState.panOffset[2];

        finalFocalPoint[0] += savedState.panOffset[0];
        finalFocalPoint[1] += savedState.panOffset[1];
        finalFocalPoint[2] += savedState.panOffset[2];
    }

    // Set flag to prevent saving during programmatic camera updates.
    if (cprObjects) {
        cprObjects.preventCameraSave = true;
    }

    // Apply the computed camera settings with pan offset.
    camera.setPosition(...finalPosition);
    camera.setFocalPoint(...finalFocalPoint);
    camera.setViewUp(...worldNormal);
    camera.setParallelScale(targetParallelScale);

    // Save the current state with the computed default position as reference.
    // This allows us to track future pan offsets correctly.
    if (cprObjects?.branchCameraStates && selectedBranchName) {
        saveCameraStateForBranch(
            camera,
            cprObjects.branchCameraStates,
            selectedBranchName,
            cameraPosition,  // default position without pan
            cameraFocalPoint  // default focal point without pan
        );
    }

    // Clear the flag to allow user interactions to save.
    if (cprObjects) {
        cprObjects.preventCameraSave = false;
    }

    renderer.resetCameraClippingRange();
    interactor.render();
    renderWindow.render();

    updateSplineProjection(
        branchControlData,
        currentCPRFrameRef,
        mapper,
        splineWidget,
        interactor,
        renderWindow,
        planeManipulator,
        actor,
    );
}

/** Set the centerline polydata for the CPR mapper and update the reslice cursor widget center.
 * @param {vtkImageCPRMapper} mapper
 * @param {vtkPolyData} liveCenterline
 * @param {vtkPolyData} polyData
 * @param {import('vtk.js/Sources/Widgets/Widgets3D/ResliceCursorWidget/ResliceCursorWidgetState').ResliceCursorWidgetState} resliceCursorWidgetState
 * @param {vtkCPRManipulator} cprManipulator
 */
function setCenterlinePolyData(
    mapper,
    liveCenterline,
    polyData,
    resliceCursorWidgetState,
    cprManipulator
) {
    if (!polyData) {
        console.warn("setCenterlinePolyData: No polydata provided for centerline.");
        return;
    }

    // Positions.
    liveCenterline.getPoints().setData(polyData.getPoints().getData(), 3);

    // Polyline (single line from 0..N-1).
    const nPoints = polyData.getPoints().getNumberOfPoints();
    const lines = new Uint32Array(1 + nPoints);
    lines[0] = nPoints;

    for (let i = 0; i < nPoints; ++i) {
        lines[i + 1] = i;
    }

    liveCenterline.getLines().setData(lines);

    // Orientation (mat4 per point).
    const tensors = polyData.getPointData().getTensors();
    liveCenterline.getPointData().setTensors(
        vtkDataArray.newInstance({
            name: 'Orientation',
            numberOfComponents: 16,
            values: tensors.getData().slice(), // Copy
        })
    );

    liveCenterline.modified();

    const midPointDistance = mapper.getHeight() / 2;
    const { worldCoords } = cprManipulator.distanceEvent(midPointDistance);
    resliceCursorWidgetState.setCenter(worldCoords);
}

/** Rotate the CPR view by the specified angle in degrees.
 * @param {number} angleInDegrees
 * @param {vtkImageCPRMapper} mapper
 * @param {vtkCPRManipulator} cprManipulator
 * @param {import('vtk.js/Sources/Widgets/Widgets3D/ResliceCursorWidget/ResliceCursorWidgetState').ResliceCursorWidgetState} resliceCursorWidgetState
 */
function rotateView(angleInDegrees, mapper, cprManipulator, resliceCursorWidgetState) {
    const radAngle = radiansFromDegrees(angleInDegrees);
    const origin = [0, 0, 0];
    const normalDir = [0, 0, 1];
    const bitangentDir = [0, 1, 0];
    vec3.rotateZ(bitangentDir, bitangentDir, origin, radAngle);

    const distance = cprManipulator.getCurrentDistance();
    const { orientation } = mapper.getCenterlinePositionAndOrientation(distance);
    const modelDirections = mat3.fromQuat([], orientation);

    const worldBitangent = vec3.transformMat3([], bitangentDir, modelDirections);
    const worldNormal = vec3.transformMat3([], normalDir, modelDirections);

    // TODO: It would be nice to replace the reslice cursor widget with our own computed plane.
    const widgetPlanes = resliceCursorWidgetState.getPlanes();
    widgetPlanes[stretchViewType].normal = worldBitangent;
    widgetPlanes[stretchViewType].viewUp = worldNormal;
    resliceCursorWidgetState.setPlanes(widgetPlanes);
}

/** Configure the spline widget and its representation with desired settings.
 * @param {vtkSplineWidget} splineWidget
 * @param {import('vtk.js/Sources/Widgets/Core/WidgetManager/WidgetInstance')} splineWidgetRepresentation
 */
function configureSplineWidget(splineWidget, splineWidgetRepresentation) {
    splineWidgetRepresentation.setOutputBorder(true);
    splineWidgetRepresentation.setFill(false);
    const widgetState = splineWidget.getWidgetState();

    // Spline boundary conditions setup
    widgetState.setSplineBoundaryCondition(BoundaryCondition.DEFAULT);

    // TODO: If the boundary condition is not DEFAULT, we need to set the boundary condition values:
    // widgetState.setSplineBoundaryConditionValues([0, 0, 0]);

    widgetState.setSplineClosed(false);
    widgetState.setSplineKind(splineKind.KOCHANEK_SPLINE);

    // TODO: If using Kochanek spline, we can set tension, bias, continuity here:
    // widgetState.setSplineTension(0);
    // widgetState.setSplineBias(0);
    // widgetState.setSplineContinuity(0);

    splineWidgetRepresentation.setResolution(20);
    splineWidgetRepresentation.setHandleSizeInPixels(20);

    // Freehand control
    splineWidgetRepresentation.setAllowFreehand(false);
    splineWidgetRepresentation.setFreehandMinDistance(0.2);
}



/**
 * Find an existing control-point index that is "near" the given centerline id.
 * Returns the existing index value (e.g. 180) or null if none is within maxDelta.
 * @param {number} pointIndex - Centerline point index to search for.
 * @param {number} maxDelta - Maximum delta to consider "near".
 * @param {number[]} worldControlPointIndices - Array of existing control point indices.
 * @returns {number|null} - The found nearby control point index, or null if none found.
 */
function findNearbyControlPointIndex(pointIndex, maxDelta = 3, worldControlPointIndices) {
    if (
        !worldControlPointIndices ||
        !worldControlPointIndices.length
    ) {
        return null;
    }

    let bestIdx = null;
    let bestDelta = Infinity;

    for (const idx of worldControlPointIndices) {
        const delta = Math.abs(idx - pointIndex);
        if (delta <= maxDelta && delta < bestDelta) {
            bestDelta = delta;
            bestIdx = idx;
        }
    }

    return bestIdx;
}

/**
 * Add a new original control point index to the spline world control point data,
 * maintaining sorted order. If the index already exists, optionally update its
 * initial radial and Y offsets.
 * @param {number} pointIndex - Centerline point index to add.
 * @param {number} initialRadialOffset - Initial radial offset R to set for this control point.
 * @param {BranchControlData} branchControlData - Branch control data containing spline world control points and offset arrays.
 * @returns {number} - The index at which the control point was inserted or found.
 */
function addOriginalControlPointIndex(
    pointIndex,
    initialRadialOffset,
    branchControlData,
) {
    const r0 = Number.isFinite(initialRadialOffset) ? initialRadialOffset : 0.0;
    // BUG? ViTAA PoC code never passes in a value for initialYOffset, so y0 is always 0.0.
    // const y0 = Number.isFinite(initialYOffset) ? initialYOffset : 0.0;
    const y0 = 0.0;

    const oldIndices = branchControlData.splineWorldControlPointData.worldControlPointIndices;

    // Ensure offset arrays exist and are aligned
    ensureOffsetArraysExist(
        branchControlData,
        oldIndices.length,
    );

    const oldRadial = branchControlData.controlRadialOffsetR;
    const oldY = branchControlData.controlOffsetYCPR;

    // If it already exists, optionally update its offsets and return its index
    for (let i = 0; i < oldIndices.length; i++) {
        if (oldIndices[i] === pointIndex) {
            if (Number.isFinite(initialRadialOffset)) {
                oldRadial[i] = r0;
                branchControlData.controlRadialOffsetR = oldRadial;
            }
            // BUG? ViTAA PoC code never passes in a value for initialYOffset, so this would never fire.
            // if (Number.isFinite(initialYOffset)) {
            //     oldY[i] = y0;
            // }
            branchControlData.controlRadialOffsetR = oldRadial;
            branchControlData.controlOffsetYCPR = oldY;
            return i;
        }
    }

    // Insert the new index in sorted order, carrying offsets with it
    const newIndices = [];
    const newRadial = [];
    const newY = [];
    let insertedIndex = -1;

    for (let i = 0; i < oldIndices.length; i++) {
        const idx = oldIndices[i];

        if (insertedIndex === -1 && pointIndex < idx) {
            insertedIndex = newIndices.length;
            newIndices.push(pointIndex);
            newRadial.push(r0);
            newY.push(y0);
        }

        newIndices.push(idx);
        newRadial.push(oldRadial[i]);
        newY.push(oldY[i]);
    }

    if (insertedIndex === -1) {
        // New index goes at the end
        insertedIndex = newIndices.length;
        newIndices.push(pointIndex);
        newRadial.push(r0);
        newY.push(y0);
    }

    branchControlData.splineWorldControlPointData.worldControlPointIndices = newIndices;
    branchControlData.controlRadialOffsetR = newRadial;
    branchControlData.controlOffsetYCPR = newY;

    return insertedIndex;
}

/**
 * Compute CPR-strip coordinates [x, y, 0] for a centerline point i,
 * using the same logic as updateSplineProjection.
 *
 * Assumes:
 *   - orientedCenterline is mapper.getOrientedCenterline()
 *   - distancesToFirstPoint = orientedCenterline.getDistancesToFirstPoint()
 *   - orientations = orientedCenterline.getOrientations() or null
 *   - width/height come from currentCPRFrame
 *
 * @param {number} i - Centerline point index.
 * @param {vtkPolyLine} orientedCenterline - Oriented centerline polydata.
 * @param {Object} params - Parameters object.
 * @param {number} params.width - CPR width.
 * @param {number} params.height - CPR height.
 * @param {number[]} params.tangentDirection - World-space tangent direction vector.
 * @param {number[]} params.centerPoint - World-space center point [x, y, z].
 * @param {number[]} params.distancesToFirstPoint - Array of distances along centerline to first point.
 * @param {Array.<quat>|null} params.orientations - Array of orientation quaternions per centerline point, or null.
 * @returns {number[]|null} - CPR coordinates [x, y, 0] or null if invalid index.
 */
function computeCPRCoordsForCenterlineIndex(
    i,
    orientedCenterline,
    {
        width,
        height,
        tangentDirection,
        centerPoint,
        distancesToFirstPoint,
        orientations,
    }
) {
    const ptsArray = orientedCenterline.getPoints().getData();
    const nPts = orientedCenterline.getPoints().getNumberOfPoints();

    if (
        i < 0 ||
        i >= nPts ||
        !ptsArray ||
        !distancesToFirstPoint ||
        distancesToFirstPoint[i] === undefined
    ) {
        return null;
    }

    // ---- Y: distance along centerline -> CPR height ----
    const distance = distancesToFirstPoint[i];
    let newY = height - distance;
    newY = clamp(newY, 0, height);

    // ---- World position of this centerline point ----
    const point = [
        ptsArray[3 * i],
        ptsArray[3 * i + 1],
        ptsArray[3 * i + 2],
    ];

    // ---- X: based on centerPoint + local tangent/orientation ----
    let newX = width / 2;

    if (centerPoint) {
        const localXDirection = [0, 0, 0];
        const tmp = [0, 0, 0];

        // Orientation quaternion for this point (if available)
        const q = orientations && orientations[i] ? orientations[i] : null;

        if (q) {
            vec3.transformQuat(localXDirection, tangentDirection, q);
        } else {
            vec3.copy(localXDirection, tangentDirection);
        }

        // Offset along local X so that centerPoint is centered
        vec3.subtract(tmp, centerPoint, point);
        newX += vec3.dot(tmp, localXDirection);
    }

    // Clamp to strip
    newX = clamp(newX, 0, width);

    // Mirror across the midline (same as updateSplineProjection)
    newX = width - newX;

    return [newX, newY, 0];
}

// Simple smoothing on a segment of the centerline.
// Uses a B-spline-like 1D filter on X,Y,Z along the curve.
function smoothCenterlineSegment(outPts, startIdx, endIdx, iterations = 10) {
    const nPts = outPts.length / 3;
    if (!nPts) {
        return;
    }

    // Don't touch the very first/last points to keep endpoints stable.
    let s = Math.max(1, startIdx | 0);
    let e = Math.min(nPts - 2, endIdx | 0);
    if (e <= s) {
        return;
    }

    const tmp = new Float32Array(outPts.length);

    for (let it = 0; it < iterations; ++it) {
        tmp.set(outPts);
        for (let i = s; i <= e; ++i) {
            const im1 = 3 * (i - 1);
            const i0 = 3 * i;
            const ip1 = 3 * (i + 1);

            // Kernel [1, 6, 1] / 8
            outPts[i0] = (tmp[im1] + 6 * tmp[i0] + tmp[ip1]) / 8.0;
            outPts[i0 + 1] = (tmp[im1 + 1] + 6 * tmp[i0 + 1] + tmp[ip1 + 1]) / 8.0;
            outPts[i0 + 2] = (tmp[im1 + 2] + 6 * tmp[i0 + 2] + tmp[ip1 + 2]) / 8.0;
        }
    }
}

/**
 * Get the radial CPR-space offset r for an arbitrary centerline point index i
 * by interpolating between the nearest control points along the centerline.
 *
 * This turns controlRadialOffsetR (which is defined only at
 * worldControlPointIndices[]) into a continuous function r(i) over the
 * whole oriented centerline.
 *
 * @param {number} i - Centerline point index.
 * @param {number[]} worldControlPointIndices - Array of original control point indices.
 * @param {number[]} controlRadialOffsetR - Array of radial offsets at the control points.
 * @returns {number} - Interpolated radial offset r(i).
 */
function getRadialOffsetForCenterlineIndex(i, worldControlPointIndices, controlRadialOffsetR) {
    if (
        !worldControlPointIndices ||
        !worldControlPointIndices.length ||
        !controlRadialOffsetR ||
        !controlRadialOffsetR.length
    ) {
        return 0.0;
    }

    const ctrlIdx = worldControlPointIndices;
    const rArr = controlRadialOffsetR;
    const nCtrl = Math.min(ctrlIdx.length, rArr.length);

    if (nCtrl === 0) {
        return 0.0;
    }

    // Helper to read a finite r(k) or 0 if undefined/NaN
    const getR = (k) => {
        const v = rArr[k];
        return Number.isFinite(v) ? v : 0.0;
    };

    // Before first control point -> clamp to its offset
    if (i <= ctrlIdx[0]) {
        return getR(0);
    }

    // After last control point -> clamp to its offset
    if (i >= ctrlIdx[nCtrl - 1]) {
        return getR(nCtrl - 1);
    }

    // Find segment [i0, i1] such that i0 <= i <= i1
    for (let k = 0; k < nCtrl - 1; ++k) {
        const i0 = ctrlIdx[k];
        const i1 = ctrlIdx[k + 1];

        if (i < i0 || i > i1) {
            continue;
        }

        const r0 = getR(k);
        const r1 = getR(k + 1);

        if (i1 === i0) {
            // Degenerate, just pick the left one
            return r0;
        }

        const t = (i - i0) / (i1 - i0); // 0..1 along the segment
        return r0 + t * (r1 - r0);
    }

    // Fallback (shouldn't really hit here)
    return 0.0;
}

/**
 * Edited CPR coordinates for a centerline point i.
 *
 * - Start from the base CPR coords (computeCPRCoordsForCenterlineIndex)
 * - Apply a continuous radial offset r(i) in X via getRadialOffsetForCenterlineIndex.
 *
 * This is effectively an edited CPR-space "copy" of the oriented centerline that
 * tracks the user-edited spline, instead of only the original centerline.
 *
 * @param {number} i - Centerline point index.
 * @param {vtkPolyLine} orientedCenterline - Oriented centerline.
 * @param {Object} params - Parameters object.
 * @param {number} params.width - CPR width.
 * @param {number} params.height - CPR height.
 * @param {number[]} params.tangentDirection - World-space tangent direction vector.
 * @param {number[]} params.centerPoint - World-space center point [x, y, z].
 * @param {number[]} params.distancesToFirstPoint - Array of distances along centerline to first point.
 * @param {Array.<quat>|null} params.orientations - Array of orientation quaternions per centerline point, or null.
 * @param {number[]} worldControlPointIndices - Array of original control point indices.
 * @param {number[]} controlRadialOffsetR - Array of radial offsets at the control points.
 * @returns {number[]|null} - Edited CPR coordinates [x, y, 0] or null if invalid index.
 */
function computeEditedCPRCoordsForCenterlineIndex(
    i,
    orientedCenterline,
    params,
    worldControlPointIndices,
    controlRadialOffsetR
) {
    const base = computeCPRCoordsForCenterlineIndex(
        i,
        orientedCenterline,
        params
    );
    if (!base) {
        return null;
    }
    log('computeEditedCPRCoordsForCenterlineIndex - base:', base);

    const { width } = params || {};
    const r = getRadialOffsetForCenterlineIndex(i, worldControlPointIndices, controlRadialOffsetR);

    let x = base[0] + r;
    const y = base[1];
    const z = base[2] ?? 0.0;

    if (width != null) {
        x = clamp(x, 0, width);
    }

    return [x, y, z];
}

function setCPRCursor(style, cprCanvas) {
    if (cprCanvas && cprCanvas.style) {
        cprCanvas.style.cursor = style;
    }
}

/**
 * Convert DOM mouse event -> VTK display coords [x, y, 0]
 * @param {MouseEvent} ev - The mouse event.
 * @param {HTMLCanvasElement} cprCanvas - The CPR canvas element.
 * @returns {number[]|null} - Display coordinates [x, y, 0] or null if invalid.
 */
function displayCoordsFromMouseEvent(ev, cprCanvas) {
    if (!cprCanvas) return null;
    const rect = cprCanvas.getBoundingClientRect();
    const xCss = ev.clientX - rect.left;
    // VTK display origin is bottom-left; DOM origin is top-left
    const yCss = rect.height - (ev.clientY - rect.top);
    const sx = cprCanvas.width / rect.width;
    const sy = cprCanvas.height / rect.height;
    return [xCss * sx, yCss * sy, 0.0];
}

/**
 * World -> CPR local coordinates (actor space)
 * @param {number[]} worldPos - World position [x, y, z].
 * @param {vtkImageSlice} actor - The CPR image slice actor.
 * @returns {number[]|null} - CPR local coordinates [x, y, z] or null if failed.
 */
function worldToCPRCoordinates(worldPos, actor) {
    if (!actor || !actor.getUserMatrix) {
        return null;
    }

    const actorMatrix = actor.getUserMatrix();
    if (!actorMatrix) {
        return null;
    }

    const invMat = mat4.create();
    if (!mat4.invert(invMat, actorMatrix)) {
        return null;
    }

    const p = vec3.fromValues(worldPos[0], worldPos[1], worldPos[2]);
    const cpr = vec3.create();
    vec3.transformMat4(cpr, p, invMat);
    return cpr;
}

/**
 * Remove an original control point index from the spline world control point data.
 * Also removes corresponding entries in the radial and Y offset arrays.
 * @param {number} pointIndex - Centerline point index to remove.
 * @param {BranchControlData} branchControlData - Branch control data containing spline world control points and offset arrays.
  */
function removeOriginalControlPointIndex(
    pointIndex,
    branchControlData,
) {
    if (
        !branchControlData.splineWorldControlPointData ||
        !branchControlData.splineWorldControlPointData.worldControlPointIndices.length
    ) {
        return;
    }

    const oldIndices = branchControlData.splineWorldControlPointData.worldControlPointIndices;

    const oldRadial =
        branchControlData.controlRadialOffsetR &&
            branchControlData.controlRadialOffsetR.length === oldIndices.length
            ? branchControlData.controlRadialOffsetR
            : new Array(oldIndices.length).fill(0.0);

    const oldY =
        branchControlData.controlOffsetYCPR &&
            branchControlData.controlOffsetYCPR.length === oldIndices.length
            ? branchControlData.controlOffsetYCPR
            : new Array(oldIndices.length).fill(0.0);

    const newIndices = [];
    const newRadial = [];
    const newY = [];

    for (let i = 0; i < oldIndices.length; i++) {
        const idx = oldIndices[i];
        if (idx === pointIndex) {
            continue; // skip removed
        }
        newIndices.push(idx);
        newRadial.push(oldRadial[i]);
        newY.push(oldY[i]);
    }

    branchControlData.splineWorldControlPointData.worldControlPointIndices = newIndices;
    branchControlData.controlRadialOffsetR = newRadial;
    branchControlData.controlOffsetYCPR = newY;
}


/**
 * Set up the add/remove control point mouse handler for the CPR canvas.
 * @param {HTMLCanvasElement} cprCanvas - The CPR canvas element.
 * @param {React.RefObject.<Object>} currentCPRFrameRef - Ref to current CPR frame data.
 * @param {vtkPointPicker} pointPicker - The VTK point picker.
 * @param {vtkRenderer} renderer - The VTK renderer.
 * @param {vtkImageCPRMapper} mapper - The CPR image mapper.
 * @param {React.RefObject.<Map<string, BranchControlData>>} branchControlDataRef - Ref to map of branch control data.
 * @param {React.RefObject.<Union>} selectedBranchRef - Ref to the currently selected branch DU.
 * @param {vtkSplineWidget} splineWidget - The spline widget.
 * @param {vtkRenderWindowInteractor} interactor - The render window interactor.
 * @param {vtkRenderWindow} renderWindow - The render window.
 * @param {vtkPlaneManipulator} planeManipulator - The plane manipulator for the spline handles.
 * @param {vtkImageSlice} actor - The CPR image slice actor.
 * @param {React.RefObject.<function>} onCenterlinePointEditRef - Ref to callback function for centerline point edits.
 */
function setUpAddOrRemoveControlPointHandler(
    cprCanvas,
    currentCPRFrameRef,
    pointPicker,
    renderer,
    mapper,
    branchControlDataRef,
    selectedBranchRef,
    splineWidget,
    interactor,
    renderWindow,
    planeManipulator,
    actor,
    onCenterlinePointEditRef
) {
    function handleAddOrRemoveControlPoint(ev) {
        const branchControlData = branchControlDataRef.current.get(getDUName(selectedBranchRef.current))

        // Only react to left mouse button.
        if (ev.button !== 0) {
            return;
        }

        if (!currentCPRFrameRef.current) {
            return;
        }

        const displayPoint = displayCoordsFromMouseEvent(ev, cprCanvas);
        if (!displayPoint) {
            console.warn('Centerline add-point: could not compute display coords.');
            return;
        }

        // 1) Use vtkPointPicker to get world position on the CPR view
        pointPicker.pick(displayPoint, renderer);
        const worldPos = pointPicker.getPickPosition();
        if (!worldPos || worldPos.length !== 3) {
            log('Centerline add-point: no valid pick on CPR view.');
            return;
        }
        log('Centerline add-point: world pick position =', worldPos);

        // 2) Convert pick to CPR local coordinates
        log("picked point world :", worldPos)
        const pickCPR = worldToCPRCoordinates(worldPos, actor);
        log("picked point CPR: ", pickCPR)
        if (!pickCPR) {
            console.warn('Centerline add-point: could not transform pick to CPR space.');
            return;
        }

        // 3) Compare against oriented centerline points in CPR local space
        const orientedCL = mapper.getOrientedCenterline();
        if (!orientedCL) {
            console.warn('Centerline add-point: mapper.getOrientedCenterline() returned null.');
            return;
        }

        const pts = orientedCL.getPoints();
        if (!pts) {
            console.warn('Centerline add-point: oriented centerline has no points.');
            return;
        }

        const data = pts.getData();
        const nPts = pts.getNumberOfPoints();

        const params = buildCPRParams(mapper, orientedCL, currentCPRFrameRef.current);

        let bestId = -1;
        let bestDist2 = Infinity;
        let bestWorldCL = null;
        let bestCPRCL = null;

        for (let i = 0; i < nPts; i++) {
            const cprCL = computeEditedCPRCoordsForCenterlineIndex(
                i,
                orientedCL,
                params,
                branchControlData.splineWorldControlPointData.worldControlPointIndices,
                branchControlData.controlRadialOffsetR
            );
            if (!cprCL) continue;

            const dx = pickCPR[0] - cprCL[0];
            const dy = pickCPR[1] - cprCL[1];
            const d2 = dx * dx + dy * dy;

            if (d2 < bestDist2) {
                bestDist2 = d2;
                bestId = i;
                bestCPRCL = cprCL;
                bestWorldCL = [
                    data[3 * i],
                    data[3 * i + 1],
                    data[3 * i + 2],
                ];
            }
        }

        if (bestId < 0) {
            log('Centerline add-point: no nearest CPR-space point found.');
            return;
        }

        const bestDist = Math.sqrt(bestDist2);
        const maxDistanceMm = 2.0;

        log('picked point world:', worldPos);
        log('picked point CPR:', pickCPR);
        log('picked centerline point world:', bestWorldCL);
        log('picked centerline point CPR:', bestCPRCL);
        log(
            'Centerline add-point (CPR-space): nearest point is',
            { id: bestId, world_coord: bestWorldCL, cpr_distance_mm: bestDist }
        );

        if (bestDist > maxDistanceMm) {
            log(
                `Centerline add-point: nearest CPR-space point is ${bestDist.toFixed(
                    2
                )} mm away (> ${maxDistanceMm} mm). Click closer to the green line.`
            );
            return;
        }
        // SUCCESS: point within tolerance on CPR plane
        log('Accepted CPR-plane centerline pick within 1mm:', {
            id: bestId,
            cpr_distance_mm: bestDist,
        });

        // ADD / REMOVE logic based on proximity in *index* space
        const nearbyIdx = findNearbyControlPointIndex(bestId, 3, branchControlData.splineWorldControlPointData.worldControlPointIndices);

        if (nearbyIdx !== null) {
            // Click near an existing control point -> remove it
            removeOriginalControlPointIndex(
                nearbyIdx,
                branchControlData,
            );
            log(
                'Removed existing control point near picked id:',
                { pickedId: bestId, removedId: nearbyIdx }
            );
        } else {
            // Otherwise, add a brand new control point at bestId,
            // with an initial radial offset so it appears at bestCPRCL.
            const baseCPR = computeCPRCoordsForCenterlineIndex(
                bestId,
                orientedCL,
                params
            );
            log('handleAddRemoveControlPoint - baseCPR:', baseCPR);

            let initialR = 0.0;
            if (baseCPR && bestCPRCL) {
                initialR = bestCPRCL[0] - baseCPR[0];
            }

            addOriginalControlPointIndex(
                bestId,
                initialR,
                branchControlData,
            );
            log('Added control point at centerline id', bestId);
        }

        // Rebuild spline handles on CPR view
        updateSplineProjection(
            branchControlData,
            currentCPRFrameRef,
            mapper,
            splineWidget,
            interactor,
            renderWindow,
            planeManipulator,
            actor,
        );

        onCenterlinePointEditRef.current();

        ev.preventDefault();
    }

    return handleAddOrRemoveControlPoint;
}

/**
 * @typedef {object} EventHandler
 * @property {string} eventType - The type of the event (e.g., 'mousedown', 'mousemove').
 * @property {function} handler - The event handler function.
 * @property {HTMLElement|Window} container - The DOM container or window to which the event handler is attached.
 */

/**
 * Set up CPR spline handle picking and dragging via DOM mouse events.
 * @param {object} params
 * @param {HTMLElement} params.container - The DOM container of the CPR view.
 * @param {vtkRenderer} params.renderer - The vtkRenderer for the CPR view.
 * @param {vtkRenderWindow} params.renderWindow - The vtkRenderWindow for the CPR view.
 * @param {vtkSplineWidget} params.splineWidget - The spline widget to manipulate.
 * @param {import('vtk.js/Sources/Widgets/Core/WidgetManager/WidgetInstance')} params.widgetManager - The widget manager.
 * @param {vtkPointPicker} params.pointPicker - The VTK point picker.
 * @param {React.RefObject.<Object>} params.currentCPRFrameRef - Ref to current CPR frame data.
 * @param {React.RefObject.<Map<string, BranchControlData>>} params.branchControlDataRef - Ref to map of branch control data.
 * @param {React.RefObject.<Union>} params.selectedBranchRef - Ref to selected branch.
 * @param {vtkRenderWindowInteractor} params.interactor - The render window interactor.
 * @param {vtkPlaneManipulator} params.planeManipulator - The plane manipulator for the spline handles.
 * @param {vtkImageCPRMapper} params.mapper - The CPR image mapper.
 * @param {vtkImageSlice} params.actor - The CPR image slice actor.
 * @param {React.RefObject.<function>} params.onCenterlinePointEditRef - Ref to callback function for centerline point edits.
 * @return {Array<EventHandler>} Array of event handlers with their types and containers for cleanup.
 */
function setupSplineCPRPicking({
    container,
    renderer,
    renderWindow,
    splineWidget,
    widgetManager,
    pointPicker,
    currentCPRFrameRef,
    branchControlDataRef,
    selectedBranchRef,
    interactor,
    planeManipulator,
    mapper,
    actor,
    onCenterlinePointEditRef
}) {
    if (!container || !renderer || !renderWindow || !splineWidget) {
        console.warn('CPR picking (DOM) disabled: missing dependencies');
        return [];
    }

    const canvas = container.querySelector('canvas');
    if (!canvas) {
        console.warn('CPR picking (DOM) disabled: no canvas in container');
        return [];
    }

    log('CPR picking (DOM): installing handlers');

    let isDragging = false;
    let pickedHandle = null;

    function getHandleList() {
        const widgetState = splineWidget.getWidgetState();
        if (!widgetState || typeof widgetState.getHandleList !== 'function') {
            console.warn('CPR picking (DOM): spline widget state has no getHandleList()');
            return [];
        }
        return widgetState.getHandleList() || [];
    }

    function worldFromMouseEvent(ev) {
        const displayPoint = displayCoordsFromMouseEvent(ev, canvas);
        pointPicker.pick(displayPoint, renderer);
        const worldPos = pointPicker.getPickPosition();

        if (!worldPos || worldPos.length !== 3) {
            return null;
        }

        return worldPos;
    }


    function findClosestHandleTo(worldPos) {
        const handles = getHandleList();
        if (!handles.length || !worldPos) {
            return null;
        }

        let bestHandle = null;
        let bestDist2 = Infinity;

        for (let i = 0; i < handles.length; i++) {
            const h = handles[i];
            const o = h.getOrigin();
            const dx = o[0] - worldPos[0];
            const dy = o[1] - worldPos[1];
            const dz = o[2] - worldPos[2];
            const d2 = dx * dx + dy * dy + dz * dz;
            if (d2 < bestDist2) {
                bestDist2 = d2;
                bestHandle = h;
            }
        }

        const tolerance2 = 25.0; // ~5 mm²
        if (bestDist2 > tolerance2) {
            return null;
        }

        return bestHandle;
    }

    function onMouseDown(ev) {
        if (ev.button !== 0) {
            return;
        }

        const worldPos = worldFromMouseEvent(ev);
        if (!worldPos) {
            return;
        }
        log('onMouseDown: mouse down at world pos', worldPos);

        const handle = findClosestHandleTo(worldPos);
        if (!handle) {
            pickedHandle = null;
            isDragging = false;
            return;
        }

        pickedHandle = handle;
        isDragging = true;

        // Disable window/level interaction while dragging handle.
        interactor.getInteractorStyle().setEnabled(false);

        log(
            'CPR (DOM): start drag handle at',
            typeof pickedHandle.getOrigin === 'function'
                ? pickedHandle.getOrigin()
                : '(no origin)'
        );

        ev.preventDefault();
    }

    function onMouseMove(ev) {
        if (!isDragging || !pickedHandle) {
            return;
        }

        const worldPos = worldFromMouseEvent(ev);
        if (!worldPos) {
            return;
        }

        // --- Project mouse hit to the CPR plane via world -> CPR -> world ---

        const cpr = worldToCPRCoordinates(worldPos, actor);
        if (!cpr) {
            return;
        }

        let x = cpr[0];
        let y = cpr[1];

        if (currentCPRFrameRef.current) {
            const { width, height } = currentCPRFrameRef.current;
            if (width != null) {
                x = clamp(x, 0, width);
            }
            if (height != null) {
                y = clamp(y, 0, height);
            }
        }

        const cprPoint = [x, y, 0.0];

        const actorMatrix =
            actor && typeof actor.getUserMatrix === 'function'
                ? actor.getUserMatrix()
                : null;
        if (!actorMatrix) {
            return;
        }

        const worldOnPlane = [0, 0, 0];
        vec3.transformMat4(worldOnPlane, cprPoint, actorMatrix);

        // Live feedback: move the handle to the CPR-plane-projected point
        if (typeof pickedHandle.setOrigin === 'function') {
            log('CPR (DOM): moving dragged handle to', worldOnPlane);
            pickedHandle.setOrigin(worldOnPlane[0], worldOnPlane[1], worldOnPlane[2]);
        }

        // The following ViTAA code doesn't actually do anything. Keeping it commented out for reference until we get the updates to the PoC.
        /*
                // --- NEW: store CPR-space delta X for this control point ---
                if (
                    handleIndex !== -1 &&
                    Array.isArray(worldControlPointIndices) &&
                    handleIndex < worldControlPointIndices.length &&
                    currentCPRFrameRef.current
                ) {
                    const orientedCenterline =
                        typeof mapper.getOrientedCenterline === 'function'
                            ? mapper.getOrientedCenterline()
                            : null;

                    if (orientedCenterline) {
                        const { width, height } = currentCPRFrameRef.current;
                        const distancesToFirstPoint =
                            orientedCenterline.getDistancesToFirstPoint();
                        const orientations =
                            typeof orientedCenterline.getOrientations === 'function'
                                ? orientedCenterline.getOrientations() || null
                                : null;
                        const tangentDirection =
                            typeof mapper.getTangentDirection === 'function'
                                ? mapper.getTangentDirection()
                                : null;
                        const centerPoint =
                            typeof mapper.getCenterPoint === 'function'
                                ? mapper.getCenterPoint()
                                : null;

                        const centerlineIndex = worldControlPointIndices[handleIndex];

                        if (typeof centerlineIndex === 'number') {
                            const baseCPR = computeCPRCoordsForCenterlineIndex(
                                centerlineIndex,
                                orientedCenterline,
                                {
                                    width,
                                    height,
                                    tangentDirection,
                                    centerPoint,
                                    distancesToFirstPoint,
                                    orientations,
                                }
                            );

                            if (baseCPR) {
                                const baseX = baseCPR[0];

                                // r is now "how far from the centerline's CPR X" at this index
                                const r = x - baseX;
                            }
                        }
                    }
                }
        */

        if (widgetManager && typeof widgetManager.render === 'function') {
            widgetManager.render();
        }
        renderWindow.render();
        ev.preventDefault();
    }


    function onMouseUp(ev) {
        if (!isDragging) {
            return;
        }

        log('CPR (DOM): end drag');
        isDragging = false;
        pickedHandle = null;

        // Re-enable window/level interaction after dragging handle.
        interactor.getInteractorStyle().setEnabled(true);

        // Reset the interactor's button state by simulating a button release.
        // This ensures the interactor doesn't think the mouse button is still pressed.
        const rect = canvas.getBoundingClientRect();
        const x = ev.clientX - rect.left;
        const y = rect.height - (ev.clientY - rect.top);
        interactor.leftButtonReleaseEvent({
            position: { x, y, z: 0 },
            controlKey: ev.ctrlKey,
            altKey: ev.altKey,
            shiftKey: ev.shiftKey,
        });

        const branchControlData = branchControlDataRef.current.get(getDUName(selectedBranchRef.current));

        // 1) Update radial offsets based on where the handles ended up
        updateOffsetsFromHandles(
            branchControlData,
            currentCPRFrameRef,
            mapper,
            actor,
            splineWidget,
        );

        log("controlRadialOffsetR after drag: ", branchControlData.controlRadialOffsetR);
        log("controlOffsetYCPR after drag: ", branchControlData.controlOffsetYCPR);

        // 2) Rebuild the spline from the canonical arrays
        updateSplineProjection(
            branchControlData,
            currentCPRFrameRef,
            mapper,
            splineWidget,
            interactor,
            renderWindow,
            planeManipulator,
            actor,
        );

        onCenterlinePointEditRef.current();

        ev.preventDefault();
    }

    canvas.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    // Return event handlers and their respective container (i.e. canvas or window) to allow later removal if needed.
    return [
        { eventType: 'mousedown', handler: onMouseDown, container: canvas },
        { eventType: 'mousemove', handler: onMouseMove, container: window },
        { eventType: 'mouseup', handler: onMouseUp, container: window }
    ];
}

/**
 * Copy trunk control data from the source to the target control data.
 * @param {BranchControlData} sourceControlData - Source branch control data to copy from.
 * @param {BranchControlData} targetControlData - Target branch control data to copy to.
 */
function copyTrunkControlData(sourceControlData, targetControlData) {
    const {
        worldControlPoints: sourceWorldControlPoints,
        worldControlPointIndices: sourceWorldControlPointIndices,
        lastTrunkMasterIndex
    } = sourceControlData.splineWorldControlPointData;

    // Find the index of the last trunk master control point in worldControlPointIndices.
    let lastTrunkControlPointIndex = -1;
    for (let i = sourceWorldControlPointIndices.length - 1; i >= 0; i--) {
        const idx = sourceWorldControlPointIndices[i];
        if (idx <= lastTrunkMasterIndex) {
            lastTrunkControlPointIndex = i;
            break;
        }
    }

    // Copy the world control points and indices up to lastTrunkControlPointIndex.
    const trunkWorldControlPoints = sourceWorldControlPoints.slice(0, lastTrunkControlPointIndex + 1);
    const trunkWorldControlPointIndices = sourceWorldControlPointIndices.slice(0, lastTrunkControlPointIndex + 1);

    // Copy the controlRadialOffsetR and controlOffsetYCPR up to lastTrunkControlPointIndex.
    const trunkControlRadialOffsetR = sourceControlData.controlRadialOffsetR.slice(0, lastTrunkControlPointIndex + 1);
    const trunkControlOffsetYCPR = sourceControlData.controlOffsetYCPR.slice(0, lastTrunkControlPointIndex + 1);

    const {
        splineWorldControlPointData,
        controlRadialOffsetR,
        controlOffsetYCPR,
    } = targetControlData;

    // Count number of trunk control points in the new spline data.
    let newTrunkControlPointCount = 0;
    for (let i = 0; i < splineWorldControlPointData.worldControlPointIndices.length; i++) {
        const idx = splineWorldControlPointData.worldControlPointIndices[i];
        if (idx <= splineWorldControlPointData.lastTrunkMasterIndex) {
            newTrunkControlPointCount++;
        } else {
            break;
        }
    }

    // Overwrite the beginning of the new spline data with the trunk data.
    splineWorldControlPointData.worldControlPoints.splice(
        0,
        newTrunkControlPointCount,
        ...trunkWorldControlPoints
    );
    splineWorldControlPointData.worldControlPointIndices.splice(
        0,
        newTrunkControlPointCount,
        ...trunkWorldControlPointIndices
    );
    controlRadialOffsetR.splice(
        0,
        newTrunkControlPointCount,
        ...trunkControlRadialOffsetR
    );
    controlOffsetYCPR.splice(
        0,
        newTrunkControlPointCount,
        ...trunkControlOffsetYCPR
    );
}

function buildEditedCenterlinePolyData(
    mapper,
    worldControlPointIndices,
    controlRadialOffsetR,
) {
    // Get the oriented centerline from the CPR mapper
    const orientedCenterline = mapper.getOrientedCenterline();
    const pts = orientedCenterline.getPoints();
    const nPts = pts.getNumberOfPoints();
    const ptsArray = pts.getData();

    // Per-point orientations (quaternions) from mapper
    const orientations = orientedCenterline.getOrientations() || null;

    // Global "radial" axis in the CPR local frame, provided by mapper
    const tangentDirection = mapper.getTangentDirection();

    if (!tangentDirection) {
        console.warn('buildEditedCenterlinePolyData: no tangentDirection on mapper');
    }

    const outPts = new Float32Array(3 * nPts);

    const dir = [0, 0, 0];
    const delta = [0, 0, 0];
    const basePoint = [0, 0, 0];

    // 1) Compute world-space displaced points
    for (let i = 0; i < nPts; ++i) {
        basePoint[0] = ptsArray[3 * i];
        basePoint[1] = ptsArray[3 * i + 1];
        basePoint[2] = ptsArray[3 * i + 2];

        let px = basePoint[0];
        let py = basePoint[1];
        let pz = basePoint[2];

        const r = getRadialOffsetForCenterlineIndex(i, worldControlPointIndices, controlRadialOffsetR);

        if (tangentDirection && r !== 0.0) {
            const q = orientations && orientations[i] ? orientations[i] : null;
            if (q) {
                // Rotate CPR "X axis" (tangentDirection) into world at this point
                vec3.transformQuat(dir, tangentDirection, q);
            } else {
                vec3.copy(dir, tangentDirection);
            }

            if (vec3.length(dir) > 0) {
                vec3.normalize(dir, dir);
                // NOTE: no sign flip here - use same convention as CPR mapper.
                vec3.scale(delta, dir, r);
                px += delta[0];
                py += delta[1];
                pz += delta[2];
            }
        }

        outPts[3 * i] = px;
        outPts[3 * i + 1] = py;
        outPts[3 * i + 2] = pz;
    }

    // 2) Local smoothing around the active control point (±2 controls)
    // BUG: _activeControlPointIndex doesn't exist, so smoothing never happens.
    if (
        Array.isArray(worldControlPointIndices) &&
        typeof _activeControlPointIndex === 'number'
    ) {
        const nCtrl = worldControlPointIndices.length;
        if (nCtrl > 0) {
            const h0 = Math.max(0, Math.min(nCtrl - 1, _activeControlPointIndex));

            const hStart = Math.max(0, h0 - 2);
            const hEnd = Math.min(nCtrl - 1, h0 + 2);

            let clStart = worldControlPointIndices[hStart];
            let clEnd = worldControlPointIndices[hEnd];

            if (clStart > clEnd) {
                const tmp = clStart;
                clStart = clEnd;
                clEnd = tmp;
            }

            clStart = Math.max(0, Math.min(nPts - 1, clStart));
            clEnd = Math.max(0, Math.min(nPts - 1, clEnd));

            smoothCenterlineSegment(outPts, clStart, clEnd);
        }
    }

    // 3) Build new polydata with a single polyline
    const poly = vtkPolyData.newInstance();
    const vtkPts = vtkPoints.newInstance();
    vtkPts.setData(outPts, 3);
    poly.setPoints(vtkPts);

    const lines = new Uint32Array(nPts + 1);
    lines[0] = nPts;
    for (let i = 0; i < nPts; ++i) {
        lines[i + 1] = i;
    }
    poly.getLines().setData(lines);

    return poly;
}

/**
 * @param {Object} props
 * @param {Union} props.cprViewMode
 * @param {Union} props.selectedBranch - UnifiedDistanceBranch
 * @param {File[]} props.imageFiles
 * @param {function} props.onTouchStart
 * @param {function} props.onTouchEnd
 * @param {Object} props.interactionModeProps
 * @param {Object} props.screenshotProps
 * @param {boolean} props.isSliceScroll
 * @param {number} angleInDegrees
 * @param {function} props.onAngleChanged - Callback to invoke when the angle changes.
 * @param {boolean} [props.isVisible]
 * @param {vtkPolyData|null} [props.wallCenterline]
 * @param {Union} props.cprControlPointMode
 * @param {string} viewportId
 * @param {Union} props.maybeActiveMeasurement
 * @param {function} props.onVisualizationReady - Callback to invoke when the visualization is ready.
 */
const CprVisualization = (
    {
        viewportId,
        cprViewMode,
        selectedBranch,
        imageFiles: imageFiles,
        onTouchStart,
        onTouchEnd,
        interactionModeProps,
        screenshotProps,
        isSliceScroll,
        angleInDegrees,
        onAngleChanged,
        isVisible = true,
        wallCenterline = null,
        onCenterlinePointEdit,
        eventNames,
        cprControlPointMode,
        imageLoader: imageLoader,
        onCenterlineVtpExported,
        maybeActiveMeasurement,
        onStretchedCprControlPointsExported,
        onReady,
        onVisualizationReady
    }) => {

    const containerRef = useRef(null);
    const context = useRef(null);
    const onAngleChangedRef = useRef(onAngleChanged);
    const addOrRemoveControlPointHandlerRef = useRef(null);
    const onCenterlineVtpExportedRef = useRef(onCenterlineVtpExported);
    const maybeActiveMeasurementRef = useRef(maybeActiveMeasurement);
    const onStretchedCprControlPointsExportedRef = useRef(onStretchedCprControlPointsExported);
    const onReadyRef = useRef(onReady);

    /** @type {React.MutableRefObject<Map<string, BranchControlData | null>>} */
    const branchControlDataRef = useRef(
        new Map([
            [CenterlineDistanceBranchSelection.LEFT, null],
            [CenterlineDistanceBranchSelection.RIGHT, null],
            [CenterlineDistanceBranchSelection.CUSP, null],
        ])
    );

    const angleInDegreesRef = useRef(angleInDegrees);
    const selectedBranchRef = useRef(selectedBranch);
    const previousSelectedBranchRef = useRef(null);

    const currentCPRFrameRef = useRef({
        worldTangent: null,
        worldNormal: null,
        worldBitangent: null,
        worldWidgetCenter: null,
        distance: null, // current distance along centerline where CPR plane is built
        height: null, // CPR mapper height
        width: null, // CPR mapper width
        worldActorTranslation: null, // translation component of actor user matrix
    });
    const onCenterlinePointEditRef = useRef(onCenterlinePointEdit);

    /** @type {React.MutableRefObject<Map<string, CommittedChanges | null>>} */
    const committedChangesRef = useRef(
        new Map([
            [CenterlineDistanceBranchSelection.LEFT, null],
            [CenterlineDistanceBranchSelection.RIGHT, null],
            [CenterlineDistanceBranchSelection.CUSP, null],
        ])
    );

    const isStraightened = getDUName(cprViewMode) === CprViewMode.STRAIGHTENED;
    const extraZoom = isStraightened ? 1.0 : 1.1;

    /**
     * Composites the Konva layer onto the given canvas context for both straightened and stretched CPR views.
     * @param {CanvasRenderingContext2D} ctx - The canvas context to draw on.
     * @param {number} width - The canvas width.
     * @param {number} height - The canvas height.
     */
    function compositeKonvaLayer(ctx, width, height) {
        // Determine which Konva elements to use (straightened or stretched view).
        const konvaElements = isStraightened
            ? context.current.konvaElements
            : context.current.stretchedKonvaElements;

        if (konvaElements?.stage) {
            const konvaStage = konvaElements.stage;
            const konvaCanvas = konvaStage.toCanvas();
            ctx.drawImage(konvaCanvas, 0, 0, width, height);
        }
    }

    function _captureCustomScreenshot() {
        return new Promise((resolve, reject) => {
            context.current.renderWindow.render();
            const viewportCanvas = context.current.genericRenderWindow
                .getApiSpecificRenderWindow()
                .getCanvas();
            if (!viewportCanvas) {
                reject(`Unable to find canvas for viewport ${viewportId}`);
                return;
            }

            const offscreenCanvas = document.createElement("canvas");
            // SVGs do not respect the scaling passed into CanvasContext.drawImage, so instead of doing a bunch of
            // futzing to force the SVG to scale up with the DPR, we will just scale the canvas down.
            // TODO: this approach results in a lower quality image, we should fix it later when we have more time.
            // TODO: Uncomment if we start using SVG annotations with CPR viewports.
            // const dpr = window.devicePixelRatio;
            // offscreenCanvas.width = viewportCanvas.width / dpr;
            // offscreenCanvas.height = viewportCanvas.height / dpr;

            // TODO: Remove if we start using SVG annotations with CPR viewports (see above).
            offscreenCanvas.width = viewportCanvas.width;
            offscreenCanvas.height = viewportCanvas.height;

            if (viewportCanvas.width === 0 || viewportCanvas.height === 0) {
                reject(`Trying to take a screenshot of a viewport ${viewportId}, not being shown`);
                return;
            }

            const offscreenCtx = offscreenCanvas.getContext("2d");
            offscreenCtx.drawImage(viewportCanvas, 0, 0, offscreenCanvas.width, offscreenCanvas.height);

            const finalizeScreenshot = () => {
                compositeKonvaLayer(offscreenCtx, offscreenCanvas.width, offscreenCanvas.height);
                const canvasInfo = {
                    viewportIndex: viewportId,
                    canvasData: offscreenCanvas.toDataURL()
                };
                resolve(canvasInfo);
            }

            finalizeScreenshot();

            // TODO: Uncomment if we start using SVG annotations with CPR viewports.
            // const svg = viewportCanvas.nextElementSibling;
            // if (!svg) {
            //     console.warn(`Unable to find annotations for viewport ${viewportId}, rendering without annotations`);
            //     finalizeScreenshot();
            //     return;
            // }
            //
            // const outerHTML = svg.outerHTML;
            // // VTK is evil and doesn't actually make the tag a valid SVG file.
            // const validSvg = outerHTML.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"');
            // const blob = new Blob([validSvg], {type: 'image/svg+xml'});
            // const url = URL.createObjectURL(blob);
            //
            // const svgImg = new Image();
            //
            // svgImg.onload = function() {
            //     offscreenCtx.drawImage(
            //         svgImg,
            //         0,
            //         0,
            //         offscreenCanvas.width,
            //         offscreenCanvas.height
            //     );
            //     URL.revokeObjectURL(url);
            //     finalizeScreenshot();
            // }
            //
            // const rejectHandler = function() {
            //     URL.revokeObjectURL(url);
            //     // Would it be better to fail here?
            //     console.warn(`Unable to render annotations for viewport ${viewportId}, rendering without annotations`);
            //     finalizeScreenshot();
            // }
            // svgImg.oncancel = rejectHandler;
            // svgImg.onabort = rejectHandler;
            // svgImg.onerror = rejectHandler;
            //
            // svgImg.width = offscreenCanvas.width;
            // svgImg.height = offscreenCanvas.height;
            // svgImg.src = url;
        });
    }

    function captureCustomScreenshot(ev) {
        if (ev.detail.viewportId !== viewportId) {
            return;
        }

        if (typeof ev.detail.callback !== 'function') {
            console.error("Invalid event detail for screenshot capture");
            return;
        }

        // Wait for a short delay to ensure all rendering is complete.
        setTimeout(async () => {
            try {
                const ret = await _captureCustomScreenshot();
                ev.detail.callback(ret);
            } catch (e) {
                console.error(e);
            }
        }, 1000);
    }

    function setUpConfirmCancelChangesHandler(
        centerlinesPolyData,
    ) {
        function cancelChanges() {
            const branchName = getDUName(selectedBranchRef.current);
            log('Cancelling changes and reverting to original control points for branch', branchName);

            const branchControlData = branchControlDataRef.current.get(branchName);
            const branchCommittedChanges = committedChangesRef.current.get(branchName);

            if (branchCommittedChanges) {
                // Revert to last committed changes (deep copy to avoid shared references).
                const { splineWorldControlPointData, controlRadialOffsetR, controlOffsetYCPR } = branchCommittedChanges.branchControlData;
                branchControlData.splineWorldControlPointData = structuredClone(splineWorldControlPointData);
                branchControlData.controlRadialOffsetR = structuredClone(controlRadialOffsetR);
                branchControlData.controlOffsetYCPR = structuredClone(controlOffsetYCPR);
            } else {
                // Revert to original control points from centerline.
                const originalBranchControlData = buildOriginalControlPointsFromPolyData(centerlinesPolyData[branchName]);
                branchControlDataRef.current.set(branchName, originalBranchControlData);
            }
            const { renderWindow } = context.current;
            const { mapper, cprManipulator, resliceCursorWidgetState } = context.current.cprObjects;

            rotateView(angleInDegreesRef.current, mapper, cprManipulator, resliceCursorWidgetState);

            const cprContext = createCPRUpdateContext(context.current);

            updateDistanceAndDirection(
                cprContext,
                onAngleChangedRef.current,
                branchControlDataRef.current.get(branchName),
                currentCPRFrameRef,
                branchName
            );

            renderWindow.render();
        }

        window.addEventListener(eventNames.CancelChanges, cancelChanges);

        function confirmChanges(ev) {
            const branch = ev.detail;
            const branchName = getDUName(branch);
            log("Confirming changes for branch", branchName);

            const selectedBranchControlData = branchControlDataRef.current.get(branchName);

            committedChangesRef.current.forEach((committedChanges, otherBranch) => {
                if (otherBranch === branchName) {
                    // Commit the selected branch's control data.
                    committedChangesRef.current.set(
                        otherBranch,
                        {
                            branchControlData:
                            {
                                // Deep copy the data to avoid sharing references.
                                splineWorldControlPointData: structuredClone(selectedBranchControlData.splineWorldControlPointData),
                                controlRadialOffsetR: [...selectedBranchControlData.controlRadialOffsetR],
                                controlOffsetYCPR: [...selectedBranchControlData.controlOffsetYCPR]
                            }
                        }
                    );
                } else {
                    // For other branches, first update their committed changes with their own
                    // live branch control data, then sync trunk data from the selected branch.
                    const otherBranchControlData = branchControlDataRef.current.get(otherBranch);

                    if (otherBranchControlData) {
                        const updatedBranchControlData = {
                            splineWorldControlPointData: structuredClone(otherBranchControlData.splineWorldControlPointData),
                            controlRadialOffsetR: [...otherBranchControlData.controlRadialOffsetR],
                            controlOffsetYCPR: [...otherBranchControlData.controlOffsetYCPR]
                        };

                        // Copy trunk data from the selected branch to keep branches in sync.
                        copyTrunkControlData(selectedBranchControlData, updatedBranchControlData);

                        committedChangesRef.current.set(otherBranch, {
                            branchControlData: updatedBranchControlData
                        });
                    }
                }
            });
        }

        window.addEventListener(eventNames.ConfirmChanges, confirmChanges);

        function exportVtp() {
            log("Exporting centerline vtp");

            const { branchOriginalIndices, wallCenterline, mapper, liveCenterline, centerlinesPolyData, resliceCursorWidgetState, cprManipulator } = context.current.cprObjects;
            const src = wallCenterline;
            const selectedBranchName = getDUName(selectedBranchRef.current);

            const originalPoints = src.getPoints();
            const originalArray =
                originalPoints && originalPoints.getData ? originalPoints.getData() : null;

            if (!originalArray || !originalArray.length) {
                console.warn('saveEditedCenterlineAsVTP: empty original point data');
                return;
            }

            // Copy original coordinates into a fresh buffer we can modify.
            const outCoords = new Float32Array(originalArray); // clone

            // For each branch, generate centerlinePolyData fresh from branchControlData at export time.
            // This ensures the VTP output accurately reflects the current state of all control points,
            // especially for the shared trunk which may have been modified from any branch.
            committedChangesRef.current.forEach((committedChanges, branch) => {
                if (!committedChanges || !committedChanges.branchControlData) {
                    return;
                }

                const indices = branchOriginalIndices[branch];
                if (!indices || !indices.length) {
                    return;
                }

                // Switch the mapper to this branch's centerline to generate accurate polydata.
                setCenterlinePolyData(
                    mapper,
                    liveCenterline,
                    centerlinesPolyData[branch],
                    resliceCursorWidgetState,
                    cprManipulator
                );

                // Build centerlinePolyData fresh from the committed branchControlData.
                const { branchControlData } = committedChanges;
                const edited = buildEditedCenterlinePolyData(
                    mapper,
                    branchControlData.splineWorldControlPointData.worldControlPointIndices,
                    branchControlData.controlRadialOffsetR
                );

                if (!edited) {
                    return;
                }

                const editedPts = edited.getPoints();
                const editedArray = editedPts.getData();

                if (editedArray.length !== indices.length * 3) {
                    console.warn(
                        `Branch ${branch}: edited points length (${editedArray.length}) ` +
                        `does not match indices length (${indices.length * 3})`
                    );
                    return;
                }

                for (let k = 0; k < indices.length; ++k) {
                    const srcIdx = 3 * k;
                    const dstPointIndex = indices[k];
                    const dstIdx = 3 * dstPointIndex;

                    outCoords[dstIdx] = editedArray[srcIdx];
                    outCoords[dstIdx + 1] = editedArray[srcIdx + 1];
                    outCoords[dstIdx + 2] = editedArray[srcIdx + 2];
                }
            });

            // Restore the mapper to the selected branch's centerline.
            setCenterlinePolyData(
                mapper,
                liveCenterline,
                centerlinesPolyData[selectedBranchName],
                resliceCursorWidgetState,
                cprManipulator
            );

            // Build an output polydata that is a copy of the original, but with updated point coordinates.
            const combined = vtkPolyData.newInstance();
            combined.shallowCopy(src);

            const outPoints = vtkPoints.newInstance();
            outPoints.setData(outCoords, 3);
            combined.setPoints(outPoints);

            // Extra safety: if combined is somehow invalid, don't hit the writer.
            if (!combined.getPoints()) {
                console.warn(
                    'exportVtp: combined polydata is invalid, aborting write'
                );
                return;
            }

            // Strip field data arrays to avoid writer.getRange() issues.
            const fieldData = combined.getFieldData();
            for (let i = fieldData.getNumberOfArrays() - 1; i >= 0; --i) {
                const arr = fieldData.getArrayByIndex(i);
                fieldData.removeArray(arr.getName());
            }

            // Write combined polydata.
            const writer = vtkXMLPolyDataWriter.newInstance();
            writer.setFormat(vtkXMLWriter.FormatTypes.BINARY);
            const fileContents = writer.write(combined);

            // const fileName = 'edited_full_centerline.vtp';
            // const blob = new Blob([fileContents], {
            //     type: 'application/vnd.vtk.vtp',
            // });

            onCenterlineVtpExportedRef.current(fileContents);
        }

        window.addEventListener(eventNames.ExportVtp, exportVtp);

        /**
         * Export the committed stretched CPR spline control points as JSON.
         * This exports the branchControlData for all branches that have committed changes.
         */
        function exportStretchedCprControlPoints() {
            log("Exporting stretched CPR control points");

            const exportData = {
                version: "v1" // unused for now, future-proofing
            };

            committedChangesRef.current.forEach((committedChanges, branch) => {
                if (committedChanges && committedChanges.branchControlData) {
                    const { splineWorldControlPointData, controlRadialOffsetR, controlOffsetYCPR } =
                        committedChanges.branchControlData;

                    exportData[branch] = {
                        splineWorldControlPointData: {
                            worldControlPoints: splineWorldControlPointData.worldControlPoints,
                            worldControlPointIndices: splineWorldControlPointData.worldControlPointIndices,
                            lastTrunkMasterIndex: splineWorldControlPointData.lastTrunkMasterIndex
                        },
                        controlRadialOffsetR: controlRadialOffsetR,
                        controlOffsetYCPR: controlOffsetYCPR
                    };
                }
            });

            const jsonString = JSON.stringify(exportData);
            onStretchedCprControlPointsExportedRef.current(jsonString);
        }

        window.addEventListener(eventNames.ExportStretchedCprControlPoints, exportStretchedCprControlPoints);

        /**
         * Import stretched CPR spline control points from JSON.
         * This restores the branchControlData for all branches from the exported state.
         * @param {CustomEvent} ev - The event containing the JSON string in ev.detail.
         */
        function importStretchedCprControlPoints(ev) {
            try {
                const jsonString = ev.detail;
                const { renderWindow } = context.current;
                const { mapper, cprManipulator, resliceCursorWidgetState, liveCenterline, centerlinesPolyData } = context.current.cprObjects;

                /**
                 * Helper function to process a branch and update its control data.
                 * @param {string} branchName - The name of the branch to process.
                 * @param {BranchControlData} branchControlData - The control data for the branch.
                 */
                const processBranch = (branchName, branchControlData) => {
                    // Update the live branch control data.
                    branchControlDataRef.current.set(branchName, structuredClone(branchControlData));

                    // Update the committed changes.
                    committedChangesRef.current.set(branchName, {
                        branchControlData: structuredClone(branchControlData)
                    });
                };

                // If jsonString is null or empty (baseline revision case), reinitialize control point data for all branches.
                if (!jsonString || jsonString === '') {
                    Object.keys(CenterlineDistanceBranchSelection).forEach(branch => {
                        const branchName = CenterlineDistanceBranchSelection[branch];
                        const originalBranchControlData =
                            buildOriginalControlPointsFromPolyData(
                                centerlinesPolyData[branchName]
                            );

                        processBranch(branchName, originalBranchControlData);
                    });
                } else {
                    const importData = JSON.parse(jsonString);

                    Object.keys(importData).forEach((branch) => {
                        const branchData = importData[branch];

                        if (branchData && branchData.splineWorldControlPointData) {
                            const restoredBranchControlData = {
                                splineWorldControlPointData: {
                                    worldControlPoints: branchData.splineWorldControlPointData.worldControlPoints,
                                    worldControlPointIndices: branchData.splineWorldControlPointData.worldControlPointIndices,
                                    lastTrunkMasterIndex: branchData.splineWorldControlPointData.lastTrunkMasterIndex
                                },
                                controlRadialOffsetR: branchData.controlRadialOffsetR,
                                controlOffsetYCPR: branchData.controlOffsetYCPR
                            };

                            processBranch(branch, restoredBranchControlData);
                        }
                    });
                }

                // Restore the selected branch's centerline on the mapper.
                const selectedBranchName = getDUName(selectedBranchRef.current);
                setCenterlinePolyData(
                    mapper,
                    liveCenterline,
                    centerlinesPolyData[selectedBranchName],
                    resliceCursorWidgetState,
                    cprManipulator
                );

                rotateView(angleInDegreesRef.current, mapper, cprManipulator, resliceCursorWidgetState);

                const cprContext = createCPRUpdateContext(context.current);

                updateDistanceAndDirection(
                    cprContext,
                    onAngleChangedRef.current,
                    branchControlDataRef.current.get(getDUName(selectedBranchRef.current)),
                    currentCPRFrameRef,
                    selectedBranchName
                );

                renderWindow.render();
            } catch (error) {
                console.error("Failed to import stretched CPR control points:", error);
            }
        }

        window.addEventListener(eventNames.ImportStretchedCprControlPoints, importStretchedCprControlPoints);

        return [
            { eventType: eventNames.CancelChanges, handler: cancelChanges, container: window },
            { eventType: eventNames.ConfirmChanges, handler: confirmChanges, container: window },
            { eventType: eventNames.ExportVtp, handler: exportVtp, container: window },
            { eventType: eventNames.ExportStretchedCprControlPoints, handler: exportStretchedCprControlPoints, container: window },
            { eventType: eventNames.ImportStretchedCprControlPoints, handler: importStretchedCprControlPoints, container: window },
        ];
    }

    const render = () => {
        if (context.current?.genericRenderWindow) {
            log("Rendering via resize");
            // OPTIMIZE:
            // This is a quick fix for the resolution issue when switching between single/quad view.
            // A better fix would be to depend on a visibility prop.
            context.current.genericRenderWindow.resize();
        }
    };

    const handleResize = () => {
        if (containerRef.current && context.current?.genericRenderWindow) {
            context.current.genericRenderWindow.resize();

            // Update horizontal measurement lines for straightened view.
            if (context.current.cprObjects?.horizontalIndexLines?.length &&
                context.current.cprObjects?.resliceFilter) {
                updateStraightenedIndexLines(
                    context.current.cprObjects.resliceFilter,
                    context.current.cprObjects.horizontalIndexLines,
                    context.current.renderer,
                    context.current.renderWindow,
                );
            }

            // Update the yellow arrow for straightened view.
            if (context.current.cprObjects?.lengthMeasurementArrow &&
                context.current.cprObjects?.resliceFilter) {
                updateStraightenedMeasurementArrow(
                    context.current.cprObjects.resliceFilter,
                    context.current.cprObjects.lengthMeasurementArrow,
                    context.current.renderer,
                    context.current.renderWindow,
                );
            }

            // Resize Konva stage to match container.
            if (context.current.konvaElements?.stage) {
                const container = containerRef.current;
                context.current.konvaElements.stage.width(container.offsetWidth);
                context.current.konvaElements.stage.height(container.offsetHeight);

                // Update the measurement label position after resize.
                updateStraightenedMeasurementLabel(context.current, maybeActiveMeasurementRef.current);
            }

            // Resize stretched measurement Konva stage.
            if (context.current.stretchedKonvaElements?.stage) {
                const container = containerRef.current;
                resizeStretchedMeasurementKonva(
                    context.current.stretchedKonvaElements,
                    container.offsetWidth,
                    container.offsetHeight
                );

                // Update stretched measurement Konva elements after resize.
                updateStretchedMeasurementKonva(
                    context.current,
                    maybeActiveMeasurementRef.current,
                    selectedBranchRef.current,
                    currentCPRFrameRef.current
                );
            }

            context.current.renderWindow.render();
        }
    };

    /**
     * Updates the reslice filter's axes based on the provided transform and values.
     * @param {import('vtk.js/Sources/Imaging/Core/ImageReslice').default} resliceFilter - The vtkImageReslice instance to update.
     * @param {import('vtk.js/Sources/Common/Core/MatrixBuilder').default} transform - The vtkMatrixBuilder instance used for transformations.
     * @param {number[]} transformValues - Array of numbers representing translation values.
     * @param {number} obliqueAngleInDegrees - The oblique angle in degrees to rotate the transform.
     */
    const updateResliceFilter = (resliceFilter, transform, transformValues, obliqueAngleInDegrees) => {
        log("Translating transform by", ...transformValues);
        transform.translate(...transformValues);
        transform.rotateX(90); // Rotate to coronal.
        transform.rotateY(obliqueAngleInDegrees); // Rotate to the desired oblique angle.
        transform.rotateZ(-90); // Rotate so it's not sideways.
        resliceFilter.setResliceAxes(transform.getMatrix());
        resliceFilter.update();
    }

    // Effect that only runs when the component mounts and unmounts.
    useEffect(() => {
        log("Mounting");
        onVisualizationReady?.([viewportId, false]);
        const container = containerRef.current;

        if (container) {
            const resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(container);

            context.current = {
                ...context.current,
                resizeObserver
            };
        }

        return () => {
            const ctx = context.current;

            if (ctx) {
                log("Unmounting");
                const container = containerRef.current;

                const {
                    genericRenderWindow,
                    istyle,
                    interactor,
                    combinedTouchStart,
                    combinedTouchEnd,
                    resizeObserver,
                    cprObjects,
                    cameraModifiedSubscription } = ctx;

                if (container) {
                    container.removeEventListener('touchstart', combinedTouchStart);
                    container.removeEventListener('touchend', combinedTouchEnd);
                    container.removeEventListener('touchmove', istyle.handleTouchMove);
                }

                if (eventNames?.CaptureCustomScreenshot) {
                    window.removeEventListener(eventNames.CaptureCustomScreenshot, captureCustomScreenshot);
                }

                if (ctx.eventHandlers) {
                    // Iterate through the list and remove each event listener.
                    ctx.eventHandlers.forEach(({ eventType, handler, container }) => {
                        if (container && handler) {
                            log('Removing event listener for', eventType, 'from', container);
                            container.removeEventListener(eventType, handler);
                        }
                    });
                }

                // Unsubscribe from camera modifications.
                if (cameraModifiedSubscription) {
                    cameraModifiedSubscription.unsubscribe();
                }

                interactor?.cancelAnimation();
                cprObjects?.widgetManager?.releaseFocus();

                resizeObserver?.disconnect();
                cprObjects?.actor?.delete();
                cprObjects?.mapper?.delete();
                cprObjects?.centerlineActor?.delete();
                cprObjects?.centerlineMapper?.delete();
                if (cprObjects?.horizontalIndexLines?.length) {
                    disposeStraightenedIndexLines(ctx.renderer, cprObjects.horizontalIndexLines);
                }
                if (cprObjects?.lengthMeasurementArrow) {
                    disposeStraightenedMeasurementArrow(ctx.renderer, cprObjects.lengthMeasurementArrow);
                    cprObjects.lengthMeasurementArrow = null;
                }
                // Clean up stretched measurement indicators.
                if (cprObjects?.stretchedMeasurementLines?.length) {
                    disposeStretchedMeasurementLines(ctx.renderer, cprObjects.stretchedMeasurementLines);
                    cprObjects.stretchedMeasurementLines = [];
                }
                if (cprObjects?.stretchedMeasurementArrow) {
                    disposeStretchedMeasurementArrow(ctx.renderer, cprObjects.stretchedMeasurementArrow);
                    cprObjects.stretchedMeasurementArrow = null;
                }
                cprObjects?.widgetManager?.delete();

                // Clean up Konva stage.
                if (ctx.konvaElements?.stage) {
                    ctx.konvaElements.stage.destroy();
                }
                if (ctx.konvaElements?.konvaContainer && container) {
                    container.removeChild(ctx.konvaElements.konvaContainer);
                }
                ctx.konvaElements = null;

                // Clean up stretched Konva elements.
                if (ctx.stretchedKonvaElements) {
                    disposeStretchedMeasurementKonva(ctx.stretchedKonvaElements);
                    ctx.stretchedKonvaElements = null;
                }

                genericRenderWindow?.delete();
                context.current = null;
            }
        };
    }, []);

    // Effect to update onAngleChanged ref.
    useEffect(() => {
        onAngleChangedRef.current = onAngleChanged;
    }, [onAngleChanged]);

    // Effect to update angleInDegrees ref.
    useEffect(() => {
        angleInDegreesRef.current = angleInDegrees;
    }, [angleInDegrees]);

    // Effect to update onCenterlinePointEdit ref.
    useEffect(() => {
        onCenterlinePointEditRef.current = onCenterlinePointEdit;
    }, [onCenterlinePointEdit]);

    // Effect to update onCenterlineVtpExported ref.
    useEffect(() => {
        onCenterlineVtpExportedRef.current = onCenterlineVtpExported;
    }, [onCenterlineVtpExported]);

    // Effect to update maybeActiveMeasurement ref.
    useEffect(() => {
        maybeActiveMeasurementRef.current = maybeActiveMeasurement;
    }, [maybeActiveMeasurement]);

    // Effect to update onStretchedCprControlPointsExported ref.
    useEffect(() => {
        onStretchedCprControlPointsExportedRef.current = onStretchedCprControlPointsExported;
    }, [onStretchedCprControlPointsExported]);

    // Effect to update onReady ref.
    useEffect(() => {
        onReadyRef.current = onReady;
    }, [onReady]);

    // Effect to set up the DICOM visualization.
    useEffect(() => {
        const container = containerRef.current;
        if (!context.current?.cprObjects?.actor && imageFiles?.length > 0 && container) {
            function importVtp(ev) {
                // This will not be implemented.
                // if (!isStraightened) {
                //     const fileContents = ev.detail;
                //     const reader = vtkXMLPolyDataReader.newInstance();
                //     reader.parseAsArrayBuffer(fileContents);
                //     const polyData = reader.getOutputData();
                //
                //     console.log("POKE", polyData);
                // } else {
                //     console.log("POKE3");
                // }
            }

            window.addEventListener(eventNames.ImportVtp, importVtp);

            context.current.viewportId = viewportId;

            if (!isStraightened && !wallCenterline) {
                // Cannot proceed without centerline for stretched CPR view.
                return;
            }

            // We use the wrapper here to abstract out manual RenderWindow/Renderer/OpenGLRenderWindow setup
            const genericRenderWindow = vtkGenericRenderWindow.newInstance({
                background: [0.0, 0.0, 0.0]
            });

            //var genericRenderWindow = vtkOpenGLRenderWindow.newInstance();
            genericRenderWindow.setContainer(containerRef.current);
            genericRenderWindow.resize();

            const renderer = genericRenderWindow.getRenderer();
            const renderWindow = genericRenderWindow.getRenderWindow();
            debug(() => global.debug_renderer = renderer);
            debug(() => global.debug_renderWindow = renderWindow);
            renderer.getActiveCamera().setParallelProjection(true);

            // Set up interactor style
            const istyle = customImageInteractor.newInstance();

            // Interaction styles don't provide the ability to distinguish between a touch and left mouse press,
            // so we have to add that functionality ourselves
            const combinedTouchStart = (e) => { onTouchStart(e); istyle.handleTouchStart(e) };
            const combinedTouchEnd = (e) => { onTouchEnd(e); istyle.handleTouchEnd(e) };

            containerRef.current.addEventListener('touchstart', combinedTouchStart);
            containerRef.current.addEventListener('touchend', combinedTouchEnd);
            containerRef.current.addEventListener('touchmove', istyle.handleTouchMove);

            if (eventNames?.CaptureCustomScreenshot) {
                window.addEventListener(eventNames.CaptureCustomScreenshot, captureCustomScreenshot);
            }

            const interactor = renderWindow.getInteractor();
            istyle.setContainer(containerRef.current);
            istyle.setRenderWindow(renderWindow);
            istyle.setMouseWheelIsSliceScroll(isSliceScroll);
            istyle.setInteractionMode('IMAGE_SLICING');
            interactor.setInteractorStyle(istyle);

            let cprObjects;

            if (isStraightened) {
                // Straightened CPR view setup
                // --- Set up the slicing actor ---
                const actor = vtkImageSlice.newInstance();
                const mapper = vtkImageMapper.newInstance();
                const slicePlane = vtkPlane.newInstance();
                const resliceFilter = vtkImageReslice.newInstance();
                const transform = vtkMatrixBuilder.buildFromDegree();

                actor.setMapper(mapper);
                actor.getProperty().setInterpolationType(InterpolationType.NEAREST);

                cprObjects = {
                    actor,
                    mapper,
                    slicePlane,
                    resliceFilter,
                    transform,
                    centerlineActor: null,
                    centerlineMapper: null,
                    centerlinePolyData: null,
                    horizontalIndexLines: [],
                    lengthMeasurementArrow: null,
                };
            } else {
                // Stretched CPR view setup
                const stretchViewType = ViewTypes.XZ_PLANE;
                const planeName = viewTypeToPlaneName[stretchViewType];
                log(`Setting up ResliceCursorWidget for stretched CPR view - plane`, planeName); // Should be 'Y'

                const resliceCursorWidget = vtkResliceCursorWidget.newInstance({
                    planes: [planeName],
                    behavior: widgetBehavior,
                });

                const widgetManager = vtkWidgetManager.newInstance();
                widgetManager.setRenderer(renderer);

                const resliceCursorWidgetState = resliceCursorWidget.getWidgetState();

                const actor = vtkImageSlice.newInstance();
                const mapper = vtkImageCPRMapper.newInstance();
                mapper.setBackgroundColor(0, 0, 0, 0);
                actor.setMapper(mapper);

                // Holds the active centerline polydata for the selected branch.
                const liveCenterline = vtkPolyData.newInstance();

                mapper.setInputData(liveCenterline, 1);
                mapper.setOrientationArrayName('Orientation'); // mat4 per point
                mapper.setWidth(400);
                mapper.setProjectionSlabThickness(0.1);
                mapper.setProjectionSlabNumberOfSamples(1);
                mapper.setProjectionMode(ProjectionMode.MAX);

                const cprManipulator = vtkCPRManipulator.newInstance({
                    cprActor: actor,
                });
                const planeManipulator = vtkPlaneManipulator.newInstance();

                const {
                    centerlinesPolyData,
                    branchOriginalIndices,
                } = buildCenterlinesPolyData(wallCenterline);

                debug(() => global.debug_centerlinesPolyData = centerlinesPolyData);

                // Centerline spline
                const splineWidget = vtkSplineWidget.newInstance();
                const splineWidgetRepresentation = widgetManager.addWidget(splineWidget);
                configureSplineWidget(splineWidget, splineWidgetRepresentation);

                const pointPicker = vtkPointPicker.newInstance();

                cprObjects = {
                    actor,
                    mapper,
                    resliceCursorWidget,
                    widgetManager,
                    resliceCursorWidgetState,
                    cprManipulator,
                    planeManipulator,
                    centerlinesPolyData,
                    branchOriginalIndices,
                    liveCenterline,
                    splineWidget,
                    splineWidgetRepresentation,
                    pointPicker,
                    // For each control point i, store radial offset in the cross-section:
                    //   offsetR[i] = scalar r such that edited point at angle θ is:
                    //   P + r * R(θ)   where R(θ) = cosθ * N + sinθ * B
                    controlRadialOffsetR: [],
                    wallCenterline,
                    // Stretched measurement indicators.
                    stretchedMeasurementLines: [],
                    stretchedMeasurementArrow: null,
                    // Track whether we've initialized the camera parallel scale.
                    cameraScaleInitialized: false,
                    // Per-branch camera state to preserve pan and zoom.
                    branchCameraStates: new Map(),
                    // Flag to prevent saving camera state during programmatic updates.
                    preventCameraSave: false,
                };
            }

            // Initialize Konva stage for measurement labels in straightened view.
            const konvaElements = isStraightened ? initializeStraightenedMeasurementLabel(container, viewportId) : null;

            // Initialize Konva stage for stretched CPR measurement display.
            const stretchedKonvaElements = !isStraightened ? initializeStretchedMeasurementKonva(container, viewportId) : null;

            context.current = {
                ...context.current,
                genericRenderWindow,
                renderWindow,
                renderer,
                cprObjects,
                istyle,
                interactor,
                combinedTouchStart,
                combinedTouchEnd,
                konvaElements,
                stretchedKonvaElements
            };

            debug(() => global.debug_cprObjects = cprObjects);

            // Set up default window/level.
            // TODO: Can we dynamically set this based on the image data?
            cprObjects.actor.getProperty().setColorWindow(1200);
            cprObjects.actor.getProperty().setColorLevel(80);

            let imageCenter;

            // TODO: VRM-4359 Can we share the vtkImageData across DICOM viewports? This could be useful for
            //  [Axial / Sagittal / Coronal] and [Centerline Plane A / Plane B].
            imageLoader.tryGetImage(imageFiles)
                .then(imageData => {
                    if (isNullOrUndefined(imageData)) return;

                    if (cprObjects.mapper && !cprObjects.mapper.isDeleted()) {
                        debug(() => global.debug_imageData = imageData);

                        imageCenter = imageData.getCenter();

                        context.current = {
                            ...context.current,
                            imageCenter
                        };

                        if (isStraightened) {
                            const { resliceFilter, mapper, actor, transform } = cprObjects;

                            resliceFilter.setOutputDimensionality(2);

                            // Hook up the reslice filter to the mapper.
                            resliceFilter.setInputData(imageData);
                            mapper.setInputConnection(resliceFilter.getOutputPort());

                            updateResliceFilter(
                                resliceFilter,
                                transform,
                                imageCenter,
                                angleInDegrees
                            );

                            // Add image slice actor to scene.
                            renderer.addActor(actor);

                            // Create and add centerline visualization.
                            const centerlineObjects = createStraightenedCenterline(resliceFilter);
                            if (centerlineObjects) {
                                cprObjects.centerlineActor = centerlineObjects.actor;
                                cprObjects.centerlineMapper = centerlineObjects.mapper;
                                cprObjects.centerlinePolyData = centerlineObjects.polyData;
                                renderer.addActor(centerlineObjects.actor);
                            }

                            refreshStraightenedMeasurementLines(context.current, maybeActiveMeasurement);
                            refreshStraightenedLengthMeasurementArrow(context.current, maybeActiveMeasurement);

                        } else {
                            // Stretched CPR
                            const {
                                resliceCursorWidget,
                                resliceCursorWidgetState,
                                mapper,
                                actor,
                                cprManipulator,
                                planeManipulator,
                                liveCenterline,
                                centerlinesPolyData,
                                splineWidget,
                                widgetManager,
                                pointPicker
                            } = cprObjects;

                            mapper.setImageData(imageData);

                            resliceCursorWidget.setImage(imageData);
                            const imageDimensions = imageData.getDimensions();
                            mapper.setWidth(imageDimensions[0]);

                            actor.setUserMatrix(resliceCursorWidget.getResliceAxes(stretchViewType));
                            resliceCursorWidget.updateCameraPoints(renderer, stretchViewType, true, true);

                            // Add image slice actor to scene.
                            renderer.addActor(cprObjects.actor);

                            const branchName = getDUName(selectedBranch);

                            setCenterlinePolyData(
                                mapper,
                                liveCenterline,
                                centerlinesPolyData[branchName],
                                resliceCursorWidgetState,
                                cprManipulator
                            );

                            mapper.useStretchedMode();

                            // Initialize control point data for each branch.
                            Object.keys(CenterlineDistanceBranchSelection).forEach(branch => {
                                const originalBranchControlData =
                                    buildOriginalControlPointsFromPolyData(
                                        centerlinesPolyData[CenterlineDistanceBranchSelection[branch]]
                                    );

                                branchControlDataRef.current.set(
                                    CenterlineDistanceBranchSelection[branch],
                                    originalBranchControlData
                                );

                                committedChangesRef.current.set(
                                    CenterlineDistanceBranchSelection[branch],
                                    {
                                        branchControlData: structuredClone(originalBranchControlData)
                                    }
                                );
                            });

                            const originalBranchControlData = buildOriginalControlPointsFromPolyData(
                                centerlinesPolyData[branchName],
                            );
                            branchControlDataRef.current.set(branchName, originalBranchControlData);

                            // Default seems to be 180 degrees, so we need to rotate to match our angleInDegrees prop.
                            rotateView(angleInDegrees, mapper, cprManipulator, resliceCursorWidgetState)

                            const cprContext = createCPRUpdateContext(context.current);

                            updateDistanceAndDirection(
                                cprContext,
                                onAngleChanged,
                                branchControlDataRef.current.get(getDUName(selectedBranchRef.current)),
                                currentCPRFrameRef,
                                branchName
                            );

                            // Initialize stretched measurement indicators.
                            refreshStretchedMeasurementLines(
                                context.current,
                                maybeActiveMeasurementRef.current,
                                selectedBranchRef.current,
                                currentCPRFrameRef.current
                            );
                            refreshStretchedLengthMeasurementArrow(
                                context.current,
                                maybeActiveMeasurementRef.current,
                                selectedBranchRef.current,
                                currentCPRFrameRef.current
                            );
                            updateStretchedMeasurementKonva(
                                context.current,
                                maybeActiveMeasurementRef.current,
                                selectedBranchRef.current,
                                currentCPRFrameRef.current
                            );

                            // Enable CPR spline handle picking/dragging
                            const splineCprPickingHandlers = setupSplineCPRPicking({
                                container: genericRenderWindow.getContainer(),
                                renderer,
                                renderWindow,
                                splineWidget,
                                widgetManager,
                                pointPicker,
                                currentCPRFrameRef,
                                branchControlDataRef,
                                selectedBranchRef,
                                interactor,
                                planeManipulator,
                                mapper,
                                actor,
                                onCenterlinePointEditRef
                            });

                            const confirmCancelChangeHandlers = setUpConfirmCancelChangesHandler(
                                centerlinesPolyData,
                            )

                            const eventHandlers = splineCprPickingHandlers.concat(confirmCancelChangeHandlers);

                            context.current = {
                                ...context.current,
                                eventHandlers
                            }

                            // Notify that the CPR visualization is ready to receive events.
                            if (onReadyRef.current) {
                                onReadyRef.current();
                            }
                        }

                        // DEBUG - Add cone for reference
                        // const coneSource = vtkConeSource.newInstance({ height: 100.0, center: [ 5.801562499999989, -0.3984375, -124.25 ], radius: 100, direction: [0,0,1] });
                        // const coneMapper = vtkMapper.newInstance();
                        // coneMapper.setInputConnection(coneSource.getOutputPort());
                        // const coneActor = vtkActor.newInstance();
                        // coneActor.setMapper(coneMapper);
                        // renderer.addActor(coneActor);
                        // global.coneSource = coneSource;
                        // global.coneActor = coneActor;

                        // Reset camera and render the scene
                        let camera = renderer.getActiveCamera();
                        debug(() => global.debug_camera = camera);

                        if (isStraightened) {
                            camera.setViewUp(0, 0, -1);
                            renderer.getActiveCamera().setParallelProjection(true);
                            renderer.resetCamera();

                            // Listen for camera modifications to update konva label position during panning.
                            const cameraModifiedSubscription = camera.onModified(() => {
                                updateStraightenedMeasurementLabel(context.current, maybeActiveMeasurementRef.current);
                            });

                            // Store the subscription for cleanup.
                            context.current = {
                                ...context.current,
                                cameraModifiedSubscription
                            };
                        } else {
                            // Listen for camera modifications to update konva label position during panning
                            // and save camera state per branch.
                            const cameraModifiedSubscription = camera.onModified(() => {
                                updateStretchedMeasurementKonva(
                                    context.current,
                                    maybeActiveMeasurementRef.current,
                                    selectedBranchRef.current,
                                    currentCPRFrameRef.current
                                );

                                // Only save camera state if not in the middle of programmatic updates.
                                if (context.current?.cprObjects?.preventCameraSave) {
                                    return;
                                }

                                // Save the current camera state for the active branch.
                                if (context.current?.cprObjects?.branchCameraStates && selectedBranchRef.current) {
                                    const branchName = getDUName(selectedBranchRef.current);
                                    saveCameraStateForBranch(
                                        camera,
                                        context.current.cprObjects.branchCameraStates,
                                        branchName
                                    );
                                }
                            });

                            // Store the subscription for cleanup.
                            context.current = {
                                ...context.current,
                                cameraModifiedSubscription
                            };
                        }

                        // Reset the camera clipping range to fit the image, otherwise we won't see anything.
                        renderer.resetCameraClippingRange();

                        renderer.setActiveCamera(camera);

                        fitImageVerticallyToViewport(context.current, extraZoom);

                        renderWindow.render();

                        onVisualizationReady?.([viewportId, true]);
                    } else {
                        log("Aborted rendering DICOM, image mapper was disposed");
                    }
                });

        }

        // --- Expose globals so we can play with values in the dev console ---

        //global.dicomGenericRenderWindow = genericRenderWindow;
        //global.dicomRenderWindow = renderWindow;
        //global.dicomRenderer = renderer;
        //global.dicomActor = actor;
        //global.dicomMapper = mapper;
    }, [imageFiles, wallCenterline]);


    // Effect to change the branch centerline when the selected branch changes.
    useEffect(() => {
        selectedBranchRef.current = selectedBranch;

        if (!isNullOrUndefined(selectedBranch)) {

            if (isStraightened) {
                // The branch change for straightened CPR is currently handled in the parent component by swapping out the React component.
            }
            else if (context.current?.cprObjects?.centerlinesPolyData) {
                // Stretched CPR
                const {
                    centerlinesPolyData,
                    mapper,
                    liveCenterline,
                    resliceCursorWidgetState,
                    cprManipulator,
                } = context.current.cprObjects;

                const { renderWindow } = context.current;
                const branchName = getDUName(selectedBranch);
                const centerlinePolyData = centerlinesPolyData[branchName];

                // Implicitly confirm any pending changes when switching branches.
                if (previousSelectedBranchRef.current) {
                    /** @type {Array<EventHandler>} */
                    if (context.current.eventHandlers) {
                        // Find the ConfirmChanges handler and invoke it.
                        const confirmChangesHandler = context.current.eventHandlers.find(
                            handlerObj => handlerObj.eventType === eventNames.ConfirmChanges
                        );

                        if (confirmChangesHandler && confirmChangesHandler.handler) {
                            confirmChangesHandler.handler({ detail: previousSelectedBranchRef.current });
                        }
                    }
                }

                if (centerlinePolyData) {
                    log(`Updating centerline to branch ${branchName}`);
                    setCenterlinePolyData(
                        mapper,
                        liveCenterline,
                        centerlinePolyData,
                        resliceCursorWidgetState,
                        cprManipulator
                    );

                    const currentBranchControlData = branchControlDataRef.current.get(getDUName(selectedBranchRef.current));

                    if (currentBranchControlData) {
                        const previousBranchControlData = (() => {
                            if (previousSelectedBranchRef.current) {
                                log('Getting previous branch control data for', getDUName(previousSelectedBranchRef.current));
                                return branchControlDataRef.current.get(getDUName(previousSelectedBranchRef.current));
                            } else {
                                log('No previous branch selected, using current branch control data for', getDUName(selectedBranchRef.current));
                                return currentBranchControlData;
                            }
                        })();

                        copyTrunkControlData(previousBranchControlData, currentBranchControlData);
                        rotateView(angleInDegrees, mapper, cprManipulator, resliceCursorWidgetState);

                        const cprContext = createCPRUpdateContext(context.current);

                        updateDistanceAndDirection(
                            cprContext,
                            onAngleChanged,
                            currentBranchControlData,
                            currentCPRFrameRef,
                            branchName
                        );
                    }

                    // Refresh stretched measurement indicators after branch change.
                    refreshStretchedMeasurementLines(
                        context.current,
                        maybeActiveMeasurementRef.current,
                        selectedBranchRef.current,
                        currentCPRFrameRef.current
                    );
                    refreshStretchedLengthMeasurementArrow(
                        context.current,
                        maybeActiveMeasurementRef.current,
                        selectedBranchRef.current,
                        currentCPRFrameRef.current
                    );
                    updateStretchedMeasurementKonva(
                        context.current,
                        maybeActiveMeasurementRef.current,
                        selectedBranchRef.current,
                        currentCPRFrameRef.current
                    );

                    renderWindow.render();
                } else {
                    console.warn('No centerline polydata found for branch', branchName);
                }
            }

            previousSelectedBranchRef.current = selectedBranch;
        }
    }, [getDUName(selectedBranch)]);

    // Effect to update the resize observer.
    useEffect(() => {
        if (context.current) {
            if (isVisible) {
                if (context.current.resizeObserver) {
                    context.current.resizeObserver.disconnect();
                }

                const resizeObserver = new ResizeObserver(handleResize);
                resizeObserver.observe(containerRef.current);
                context.current.resizeObserver = resizeObserver;
            } else {
                if (context.current.resizeObserver) {
                    context.current.resizeObserver.disconnect();
                    context.current.resizeObserver = null;
                }
            }
        }
    }, [isVisible])

    // Screenshot side effect
    const [captureScreenshot, ...screenshotInteraction] =
        useScreenshot(
            containerRef.current,
            context.current,
            null,
            screenshotProps
        );

    // Interaction side effect
    useInteraction(
        null,
        interactionModeProps,
        null,
        null,
        screenshotInteraction,
        captureScreenshot,
        null,
        null,
        null,
        null);

    // Side effect to set the mouse wheel scroll mode.
    useEffect(() => {
        if (context.current?.istyle) {
            context.current.istyle.setMouseWheelIsSliceScroll(isSliceScroll);
        }
    }, [isSliceScroll]);

    // Side effect to force rendering when the component becomes visible.
    useEffect(() => {
        if (context.current?.renderWindow && context.current?.genericRenderWindow && isVisible) {
            render();
        }
    }, [isVisible]);

    // Side effect to fit Stretched / Straightened centerline DICOM images to the viewport when it becomes visible.
    useEffect(() => {
        // Returns true when the node is laid out (no ancestor has display:none).
        function isEffectivelyShown(node) {
            if (!node) {
                return false;
            }

            let element = node;

            // Walk up to the root node.
            while (element) {
                const styleDeclaration = getComputedStyle(element);

                if (styleDeclaration.display === "none") {
                    return false;
                }

                element = element.parentElement;
            }

            // If it exists in layout, its box will be measurable.
            const rect = node.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
        }

        // The parent container is the one that gets hidden/shown when switching between views.
        const parentContainer = containerRef.current?.parentElement;

        if (!parentContainer) {
            return;
        }

        const update = () => {
            if (!isEffectivelyShown(parentContainer)) {
                return;
            }

            if (context.current?.imageCenter) {
                requestAnimationFrame(() => fitImageVerticallyToViewport(context.current, extraZoom));
            }
        }

        const observer = new MutationObserver(mutations => {
            requestAnimationFrame(update);
        });

        observer.observe(parentContainer, { attributes: true, attributeFilter: ["style", "class"] });

        return () => {
            observer.disconnect();
        }
    }, []);

    // Side effect to update the oblique angle.
    useEffect(() => {
        if (isStraightened) {
            if (!isNullOrUndefined(angleInDegrees) &&
                context.current?.imageCenter &&
                context.current?.cprObjects?.resliceFilter &&
                context.current?.cprObjects?.transform) {
                log(`Setting oblique angle to ${angleInDegrees} degrees`);
                const transform = context.current.cprObjects.transform;

                // Reset back to the starting position.
                transform.identity();

                updateResliceFilter(
                    context.current.cprObjects.resliceFilter,
                    transform,
                    context.current.imageCenter,
                    angleInDegrees
                );

                // Update centerline position after reslice filter update.
                if (context.current.cprObjects.centerlinePolyData) {
                    updateStraightenedCenterline(
                        context.current.cprObjects.resliceFilter,
                        context.current.cprObjects.centerlinePolyData
                    );
                }

                if (context.current.cprObjects.horizontalIndexLines?.length) {
                    updateStraightenedIndexLines(
                        context.current.cprObjects.resliceFilter,
                        context.current.cprObjects.horizontalIndexLines,
                        context.current.renderer,
                        context.current.renderWindow,
                    );
                }

                if (context.current.cprObjects.lengthMeasurementArrow) {
                    updateStraightenedMeasurementArrow(
                        context.current.cprObjects.resliceFilter,
                        context.current.cprObjects.lengthMeasurementArrow,
                        context.current.renderer,
                        context.current.renderWindow,
                    );
                }

                updateStraightenedMeasurementLabel(context.current, maybeActiveMeasurement);

                context.current.renderWindow.render();
            }
        } else {
            // Stretched CPR
            if (!isNullOrUndefined(angleInDegrees) &&
                context.current?.interactor &&
                context.current?.renderer &&
                onAngleChangedRef.current) {
                log(`Setting stretched CPR angle to ${angleInDegrees} degrees`);
                log('mapper width and height:', context.current.cprObjects.mapper.getWidth(), context.current.cprObjects.mapper.getHeight());

                const {
                    cprManipulator,
                    mapper,
                    resliceCursorWidgetState
                } = context.current.cprObjects;

                rotateView(angleInDegrees, mapper, cprManipulator, resliceCursorWidgetState);

                const cprContext = createCPRUpdateContext(context.current);

                updateDistanceAndDirection(
                    cprContext,
                    onAngleChangedRef.current,
                    branchControlDataRef.current.get(getDUName(selectedBranchRef.current)),
                    currentCPRFrameRef,
                    getDUName(selectedBranchRef.current)
                );

                // Refresh stretched measurement indicators after CPR frame update.
                refreshStretchedMeasurementLines(
                    context.current,
                    maybeActiveMeasurementRef.current,
                    selectedBranchRef.current,
                    currentCPRFrameRef.current
                );
                refreshStretchedLengthMeasurementArrow(
                    context.current,
                    maybeActiveMeasurementRef.current,
                    selectedBranchRef.current,
                    currentCPRFrameRef.current
                );
                updateStretchedMeasurementKonva(
                    context.current,
                    maybeActiveMeasurementRef.current,
                    selectedBranchRef.current,
                    currentCPRFrameRef.current
                );
            }
        }
    }, [angleInDegrees]);

    // Effect to handle changes to the CPR control point mode.
    useEffect(() => {
        if (!isStraightened && containerRef.current && context.current?.cprObjects) {
            const cprCanvas = containerRef.current.querySelector('canvas');

            if (getDUName(cprControlPointMode) === CprControlPointMode.ADD_OR_DELETE) {
                const mapper = context.current.cprObjects.mapper;
                const orientedCL = mapper.getOrientedCenterline();
                const pts = orientedCL ? orientedCL.getPoints() : null;
                const nPts = pts ? pts.getNumberOfPoints() : 0;

                if (!nPts) {
                    return;
                }

                setCPRCursor('crosshair', cprCanvas);
                const handler = setUpAddOrRemoveControlPointHandler(
                    cprCanvas,
                    currentCPRFrameRef,
                    context.current.cprObjects.pointPicker,
                    context.current.renderer,
                    context.current.cprObjects.mapper,
                    branchControlDataRef,
                    selectedBranchRef,
                    context.current.cprObjects.splineWidget,
                    context.current.interactor,
                    context.current.renderWindow,
                    context.current.cprObjects.planeManipulator,
                    context.current.cprObjects.actor,
                    onCenterlinePointEditRef
                );
                log('Adding add/remove control point handler');
                cprCanvas.addEventListener('mousedown', handler);
                addOrRemoveControlPointHandlerRef.current = handler;
            } else {
                if (addOrRemoveControlPointHandlerRef.current) {
                    log('Removing add/remove control point handler');
                    cprCanvas.removeEventListener('mousedown', addOrRemoveControlPointHandlerRef.current);
                    addOrRemoveControlPointHandlerRef.current = null;
                }

                setCPRCursor('default', cprCanvas);
            }

        }
    }, [cprControlPointMode]);

    // Effect to handle viewport screenshot capture events.
    useEffect(() => {
        if (!eventNames || !eventNames.CaptureCprVisualizationScreenshot || !viewportId) {
            return;
        }

        const eventName = `${eventNames.CaptureCprVisualizationScreenshot}_${viewportId}`;

        const handleScreenshotCapture = (ev) => {
            if (!ev.detail || !context.current?.genericRenderWindow) {
                return;
            }

            const { callback } = ev.detail;

            if (typeof callback !== 'function') {
                console.error('Invalid callback for viewport screenshot capture');
                return;
            }

            try {
                const renderWindow = context.current.genericRenderWindow.getRenderWindow();

                const waitForCanvasReady = () => {
                    const vtkCanvas = context.current.genericRenderWindow
                        .getApiSpecificRenderWindow()
                        .getCanvas();

                    if (!vtkCanvas) {
                        console.error('VTK canvas not available for screenshot');
                        callback('');
                        return;
                    }

                    const canvasReady =
                        vtkCanvas.width > 0 && vtkCanvas.height > 0;

                    if (!canvasReady) {
                        renderWindow.render();
                        requestAnimationFrame(waitForCanvasReady);
                        return;
                    }

                    // Double RAF to ensure the browser has fully applied layout + paint before capturing canvas
                    requestAnimationFrame(() => {
                        renderWindow.render();

                        requestAnimationFrame(() => {
                            renderWindow.render();

                            const latestVtkCanvas = context.current.genericRenderWindow.getApiSpecificRenderWindow().getCanvas();
                            capture(latestVtkCanvas);
                        });
                    });
                }

                const capture = (vtkCanvas) => {
                    // Force a render to ensure the canvas buffer is populated.
                    // Determine which Konva elements to use (straightened or stretched view).
                    const konvaElements = context.current.konvaElements || context.current.stretchedKonvaElements;
                    const konvaStage = konvaElements?.stage;

                    if (konvaStage) {
                        const konvaLayer = konvaElements?.layer;
                        const konvaLabel = konvaElements?.label;

                        // Temporarily scale up the label for better screenshot quality.
                        const screenshotScale = 1.6;
                        let originalScale = null;
                        let originalPosition = null;

                        if (konvaLabel && konvaLayer) {
                            // Store original scale and position.
                            originalScale = konvaLabel.scale();
                            originalPosition = { x: konvaLabel.x(), y: konvaLabel.y() };

                            // Apply enlarged scale.
                            konvaLabel.scale({ x: screenshotScale, y: screenshotScale });

                            // Adjust position to keep label centered vertically and maintain
                            // horizontal distance from the arrow.
                            // When scaling, the label grows from its origin, so we need to offset
                            // by half the size increase to maintain the center point.
                            const labelHeight = konvaLabel.height();
                            const scaleIncrease = screenshotScale - originalScale.x;
                            const yOffset = (labelHeight * scaleIncrease) / 2;

                            konvaLabel.x(originalPosition.x);
                            konvaLabel.y(originalPosition.y - yOffset);

                            konvaLayer.draw();
                        }

                        // Create a temporary canvas to composite both layers.
                        const compositeCanvas = document.createElement('canvas');
                        const dpr = window.devicePixelRatio || 1;
                        compositeCanvas.width = vtkCanvas.width / dpr;
                        compositeCanvas.height = vtkCanvas.height / dpr;
                        const ctx = compositeCanvas.getContext('2d');

                        // Draw VTK canvas first (background).
                        ctx.drawImage(vtkCanvas, 0, 0, compositeCanvas.width, compositeCanvas.height);

                        // Draw Konva canvas on top, scaling it to match VTK canvas DPR dimensions.
                        const konvaCanvas = konvaStage.toCanvas();
                        ctx.drawImage(
                            konvaCanvas,
                            0,
                            0,
                            compositeCanvas.width,
                            compositeCanvas.height,
                        );

                        // Restore original scale and position.
                        if (konvaLabel && konvaLayer && originalScale && originalPosition) {
                            konvaLabel.scale(originalScale);
                            konvaLabel.x(originalPosition.x);
                            konvaLabel.y(originalPosition.y);
                            konvaLayer.draw();
                        }

                        const b64Data = compositeCanvas.toDataURL('image/png').split(',')[1];
                        callback(b64Data);
                    } else {
                        // No Konva canvas, just return VTK canvas.
                        const b64Data = vtkCanvas.toDataURL('image/png').split(',')[1];
                        callback(b64Data);
                    }
                }

                requestAnimationFrame(waitForCanvasReady);
            } catch (error) {
                console.error('Error capturing viewport screenshot:', error);
                callback('');
            }
        };

        window.addEventListener(eventName, handleScreenshotCapture);

        return () => {
            window.removeEventListener(eventName, handleScreenshotCapture);
        };
    }, [eventNames, viewportId]);

    // Effect to display the active measurement on straightened CPR view.
    useEffect(() => {
        if (!isStraightened) {
            return;
        }

        refreshStraightenedMeasurementLines(context.current, maybeActiveMeasurement);
        refreshStraightenedLengthMeasurementArrow(context.current, maybeActiveMeasurement);
        updateStraightenedMeasurementLabel(context.current, maybeActiveMeasurement);
    }, [isStraightened, maybeActiveMeasurement]);

    // Effect to display the active measurement on stretched CPR view.
    useEffect(() => {
        if (isStraightened) {
            return;
        }

        if (!context.current?.cprObjects?.mapper || !currentCPRFrameRef.current?.width) {
            return;
        }

        refreshStretchedMeasurementLines(
            context.current,
            maybeActiveMeasurement,
            selectedBranchRef.current,
            currentCPRFrameRef.current
        );
        refreshStretchedLengthMeasurementArrow(
            context.current,
            maybeActiveMeasurement,
            selectedBranchRef.current,
            currentCPRFrameRef.current
        );
        updateStretchedMeasurementKonva(
            context.current,
            maybeActiveMeasurement,
            selectedBranchRef.current,
            currentCPRFrameRef.current
        );
    }, [isStraightened, maybeActiveMeasurement, getDUName(selectedBranch)]);

    return <div className="layout-view-view-container" ref={containerRef} />;
};

export default CprVisualization;